// @ts-nocheck
/**
 * Compiler-rewrite Workstream A step 6 — authoring-to-typed lowering.
 *
 * This module is the *only* place that turns a live authored
 * `PreparedWorksheetExecutionCore` into a `TypedWorksheetSpec`. It is
 * deliberately narrow: for any worksheet shape that is not currently
 * inside the Rust compiler's supported surface, it returns
 * `{ typedSpec: null, reason }`. Production strictness then fails the
 * compile closed, and the unsupported reason is surfaced for diagnostics.
 *
 * This is NOT a fallback shim. It is the architectural seam the plan
 * calls for: every production compile path routes through this
 * lowering, the supported surface expands one explicit gate at a time,
 * and at PR6 cutover every worksheet the runtime exercises is covered
 * by this lowering. Anything not covered fails fast instead of
 * silently dropping to the legacy compiler.
 *
 * The initial supported surface is the simplest-possible aggregation:
 *   - `kind === 'crosstab'`, no calcs/LODs/tooltips/marks overrides
 *   - at most one measure shelf with a recognized aggregation
 *   - no filters, no totals, no table calcs, no parameters, no
 *     measureValues override
 *   - an `extractUri` that resolves to a parquet source
 *
 * Every other case routes through `ts-legacy` today. Widening the
 * supported surface happens by relaxing one check at a time, adding a
 * test fixture for the newly-covered shape, and re-running the WASM
 * parity + conformance suites.
 */

import type { PreparedWorksheetExecutionCore } from './worksheetExecutionCore'
import type {
  TypedAggregation,
  TypedDimension,
  TypedFilter,
  TypedFilterOperator,
  TypedMeasure,
  TypedOrderBy,
  TypedTotals,
  TypedWorksheetSpec,
  TypedDerivedMeasure,
  TypedDerivedExpr,
  TypedFixedLod,
  TypedMeasurePivot,
  TypedMeasurePivotEntry,
  TypedWindowMeasure,
} from './worksheetCompilerTyped'
import type { WorksheetSpecV1, CrosstabSpecV1, ShelfField, FilterSpec } from './spec'
import {
  resolveWorksheetTableCalcFields,
  type WorksheetTableCalcFieldRecord,
} from './worksheetRequest'
import { normalizeTableauFieldKey, normalizeTableauFieldKeyStrict } from './fieldIdentity'
import {
  aggregatedCalcExprBlockerHint,
  buildCalcFormulaIndexFromWorkbookIr,
  buildFieldCaptionIndexFromWorkbookIr,
  classifyCalcFormula,
  parseAggregatedCalcExpr,
  parseDimensionCalcFormula,
  parseRowLevelExpr,
  type DimensionCalcExprNode,
  recognizeCompositePattern,
  resolveBareAggregationViaCalcIndex,
  type AggregatedCalcExprNode,
  type ClassifiedCalcAggregation,
  type ClassifiedCalcKind,
  type RowLevelExprNode,
} from './tableauCalcFormula'
import type { TypedCalcExpr, TypedCalculatedField } from './worksheetCompilerTyped'
import { isHideDisplayFilter } from './hideFilterPolicy'

export type TypedSpecLoweringOutcome =
  | { typedSpec: TypedWorksheetSpec; reason: null }
  | { typedSpec: null; reason: string }

type AggResolution = { agg: TypedAggregation; distinct: boolean }
const AGG_MAP: Record<string, AggResolution | null> = {
  sum: { agg: 'SUM', distinct: false },
  count: { agg: 'COUNT', distinct: false },
  avg: { agg: 'AVG', distinct: false },
  min: { agg: 'MIN', distinct: false },
  max: { agg: 'MAX', distinct: false },
  median: { agg: 'MEDIAN', distinct: false },
  countd: { agg: 'COUNT', distinct: true },
  count_distinct: { agg: 'COUNT', distinct: true },
  ctd: { agg: 'COUNT', distinct: true },
  none: null,
}

function unsupported(reason: string): TypedSpecLoweringOutcome {
  return { typedSpec: null, reason }
}

const semanticProjectionLookupKeys = (raw: unknown): string[] => {
  const s = String(raw ?? '').trim()
  if (!s) return []
  return [
    s,
    s.toLowerCase(),
    normalizeTableauFieldKeyStrict(s),
    normalizeTableauFieldKey(s),
  ].filter((v, idx, arr): v is string => Boolean(v) && arr.indexOf(v) === idx)
}

function buildSemanticProjectionFieldIndex(semanticSpec: unknown): Map<string, string> {
  const out = new Map<string, string>()
  const projection = Array.isArray((semanticSpec as any)?.projection)
    ? ((semanticSpec as any).projection as any[])
    : []
  const set = (source: unknown, target: string): void => {
    for (const key of semanticProjectionLookupKeys(source)) {
      if (!out.has(key)) out.set(key, target)
    }
  }
  for (const p of projection) {
    if (!p || typeof p !== 'object') continue
    const alias = String((p as any)?.alias ?? '').trim()
    if (!alias) continue
    set(alias, alias)
    set((p as any)?.physical_column, alias)
    set((p as any)?.field_id, alias)
  }
  return out
}

function lookupSemanticProjectionField(index: Map<string, string>, raw: unknown): string | null {
  for (const key of semanticProjectionLookupKeys(raw)) {
    const hit = index.get(key)
    if (hit) return hit
  }
  return null
}

const fieldManifestIdentityLookupKeys = (raw: unknown): string[] => {
  const values: string[] = []
  const add = (value: unknown): void => {
    const s = String(value ?? '').trim()
    if (!s) return
    values.push(s)
    if (s.startsWith('[') && s.endsWith(']')) values.push(s.slice(1, -1).trim())
    for (const match of s.matchAll(/\[([^\]]+)\]/g)) {
      const inner = String(match?.[1] ?? '').trim()
      if (inner) values.push(inner)
    }
    const token = s.replace(/^\[/, '').replace(/\]$/, '').trim()
    if (token.includes(':')) {
      for (const part of token.split(':')) {
        const p = part.trim()
        if (!p) continue
        const key = p.toLowerCase()
        if (
          key === 'usr' ||
          key === 'none' ||
          key === 'sum' ||
          key === 'avg' ||
          key === 'average' ||
          key === 'min' ||
          key === 'max' ||
          key === 'count' ||
          key === 'countd' ||
          key === 'qk' ||
          key === 'nk' ||
          key === 'ok'
        ) continue
        values.push(p)
      }
    }
  }
  add(raw)
  const out: string[] = []
  for (const value of values) {
    for (const key of semanticProjectionLookupKeys(value)) {
      if (key && !out.includes(key)) out.push(key)
    }
  }
  return out
}

const fieldManifestCalcFormulaLookupKeys = (
  raw: unknown,
  options?: { structuredParts?: boolean },
): string[] => {
  const values: string[] = []
  const add = (value: unknown): void => {
    const s = String(value ?? '').trim()
    if (!s) return
    values.push(s)
    if (s.startsWith('[') && s.endsWith(']')) values.push(s.slice(1, -1).trim())
    if (options?.structuredParts === true) {
      const token = s.replace(/^\[/, '').replace(/\]$/, '').trim()
      if (token.includes(':')) {
        for (const part of token.split(':')) {
          const p = part.trim()
          if (!p) continue
          const key = p.toLowerCase()
          if (
            key === 'usr' ||
            key === 'none' ||
            key === 'sum' ||
            key === 'avg' ||
            key === 'average' ||
            key === 'min' ||
            key === 'max' ||
            key === 'count' ||
            key === 'countd' ||
            key === 'qk' ||
            key === 'nk' ||
            key === 'ok'
          ) continue
          values.push(p)
        }
      }
    }
  }
  add(raw)
  const out: string[] = []
  for (const value of values) {
    for (const key of semanticProjectionLookupKeys(value)) {
      if (key && !out.includes(key)) out.push(key)
    }
  }
  return out
}

function mergeFieldManifestCalcFormulaIndex(
  target: Map<string, string>,
  fieldManifest: unknown,
): void {
  const fields = Array.isArray((fieldManifest as any)?.fields)
    ? ((fieldManifest as any).fields as any[])
    : []
  for (const field of fields) {
    if (!field || typeof field !== 'object') continue
    const formula = String(
      (field as any)?.calc_formula ??
      (field as any)?.calcFormula ??
      (field as any)?.calc_formula_raw ??
      (field as any)?.calcFormulaRaw ??
      '',
    ).trim()
    if (!formula) continue
    const structuredIdentities = [
      (field as any)?.field_id,
      (field as any)?.token,
      (field as any)?.tableau_token,
      (field as any)?.tableau_token_raw,
      (field as any)?.base_field_id,
      (field as any)?.column_name,
    ]
    const displayIdentities = [
      (field as any)?.remote_name,
      (field as any)?.caption,
    ]
    for (const identity of structuredIdentities) {
      for (const key of fieldManifestCalcFormulaLookupKeys(identity, { structuredParts: true })) {
        if (key && !target.has(key)) target.set(key, formula)
      }
    }
    for (const identity of displayIdentities) {
      for (const key of fieldManifestCalcFormulaLookupKeys(identity)) {
        if (key && !target.has(key)) target.set(key, formula)
      }
    }
  }
}

function mergeFieldManifestCaptionIndex(
  target: Map<string, string>,
  fieldManifest: unknown,
): void {
  const fields = Array.isArray((fieldManifest as any)?.fields)
    ? ((fieldManifest as any).fields as any[])
    : []
  for (const field of fields) {
    if (!field || typeof field !== 'object') continue
    const caption = String((field as any)?.caption ?? '').trim()
    if (!caption) continue
    const identities = [
      (field as any)?.field_id,
      (field as any)?.token,
      (field as any)?.tableau_token,
      (field as any)?.tableau_token_raw,
      (field as any)?.base_field_id,
      (field as any)?.column_name,
      (field as any)?.remote_name,
      caption,
    ]
    for (const identity of identities) {
      for (const key of fieldManifestIdentityLookupKeys(identity)) {
        if (key && !target.has(key)) target.set(key, caption)
      }
    }
  }
}

const looksLikeTableauCalcFieldRef = (raw: unknown): boolean => {
  const s = String(raw ?? '').trim()
  if (!s) return false
  return /(?:^|[:\[\]])Calculation_[0-9A-Za-z_]+/i.test(s)
}

function isShelfFieldRef(field: ShelfField | any): field is ShelfField {
  return (
    !!field &&
    typeof field === 'object' &&
    typeof (field as any).field_name === 'string' &&
    (field as any).field_name.length > 0
  )
}

type WorksheetSpecNormalizationOutcome =
  | { ok: true; worksheetSpec: WorksheetSpecV1 }
  | { ok: false; reason: string }

function normalizeVirtualMnvFieldName(raw: string): string {
  const trimmed = String(raw ?? '').trim()
  const key = trimmed.replace(/^:+/, '').replace(/\s+/g, ' ').toLowerCase()
  if (key === 'measure names') return 'Measure Names'
  if (key === 'measure values' || key === 'multiple values') return 'Measure Values'
  return trimmed
}

function isVirtualMnvFieldName(raw: string | null | undefined): boolean {
  const key = String(raw ?? '').trim().replace(/^:+/, '').replace(/\s+/g, ' ').toLowerCase()
  return key === 'measure names' || key === 'measure values' || key === 'multiple values'
}

function isVirtualMeasureNamesFieldName(raw: string | null | undefined): boolean {
  const key = String(raw ?? '').trim().replace(/^:+/, '').replace(/\s+/g, ' ').toLowerCase()
  return key === 'measure names'
}

function isGeneratedLatLonFieldName(raw: string | null | undefined): boolean {
  const s = String(raw ?? '').trim()
  if (!/\(\s*generated\s*\)/i.test(s)) return false
  const key = normalizeTableauFieldKey(s) || s.toLowerCase()
  return key === 'latitude' || key === 'longitude'
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed.length > 0) return trimmed
  }
  return ''
}

function dequoteTableauFieldToken(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  const dequoted = s.replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()
  const bracketed = Array.from(dequoted.matchAll(/\[([^\]]+)\]/g))
    .map((match) => String(match[1] ?? '').trim())
    .filter(Boolean)
  return bracketed[bracketed.length - 1] ?? dequoted
}

function isTableauParameterFieldToken(raw: unknown): boolean {
  const s = String(raw ?? '').trim()
  if (!s) return false
  const bracketed = Array.from(s.matchAll(/\[([^\]]+)\]/g))
    .map((match) => String(match[1] ?? '').trim())
    .filter(Boolean)
  return bracketed.length >= 2 && bracketed[0]?.toLowerCase() === 'parameters'
}

function isTableauRankFieldToken(raw: unknown): boolean {
  const token = dequoteTableauFieldToken(raw)
  return /^rank:/i.test(token)
}

function parseTableauFieldTokenRole(raw: unknown): {
  fieldName: string
  role: 'dimension' | 'measure'
  type: 'discrete' | 'continuous'
  aggregation?: string
} | null {
  const rawString = String(raw ?? '').trim()
  if (!rawString) return null
  if (isTableauParameterFieldToken(rawString)) return null
  const virtualFieldName = normalizeVirtualMnvFieldName(rawString)
  if (isVirtualMnvFieldName(virtualFieldName)) {
    return {
      fieldName: virtualFieldName,
      role: virtualFieldName === 'Measure Values' ? 'measure' : 'dimension',
      type: virtualFieldName === 'Measure Values' ? 'continuous' : 'discrete',
    }
  }
  const token = dequoteTableauFieldToken(rawString)
  const parts = token.split(':').map((part) => part.trim()).filter(Boolean)
  const first = String(parts[0] ?? '').toLowerCase()
  const second = String(parts[1] ?? '').toLowerCase()
  const suffix = String(parts[parts.length - 1] ?? '').toLowerCase()
  const compactFieldName = (() => {
    if (parts.length < 2) return token || rawString
    const withoutSuffix = parts.length > 2 ? parts.slice(0, -1) : parts
    if (first === 'pcto' && withoutSuffix.length >= 3) return withoutSuffix.slice(2).join(':').trim()
    if (first === 'rank' && withoutSuffix.length >= 3) return withoutSuffix.join(':').trim()
    if (AGG_MAP[first] && withoutSuffix.length >= 2) return withoutSuffix.slice(1).join(':').trim()
    if (
      first === 'none' ||
      first === 'usr' ||
      first === 'yr' ||
      first === 'qtr' ||
      first === 'mn' ||
      first === 'tmn' ||
      first === 'twk' ||
      first === 'dy' ||
      first === 'qr'
    ) {
      return withoutSuffix.slice(1).join(':').trim()
    }
    return token || rawString
  })()
  const fieldName = normalizeVirtualMnvFieldName(compactFieldName || token || rawString)
  const aggKey = first === 'pcto' ? second : first
  const agg = AGG_MAP[aggKey]
  if (agg) {
    return {
      fieldName,
      role: 'measure',
      type: 'continuous',
      aggregation: agg.distinct ? 'countd' : agg.agg.toLowerCase(),
    }
  }
  if (first === 'rank') {
    return {
      fieldName,
      role: 'measure',
      type: 'continuous',
      aggregation: 'agg',
    }
  }
  if (first === 'usr' && suffix === 'qk') {
    return { fieldName, role: 'measure', type: 'continuous', aggregation: 'agg' }
  }
  if (
    first === 'none' ||
    first === 'usr' ||
    first === 'yr' ||
    first === 'qtr' ||
    first === 'mn' ||
    first === 'tmn' ||
    first === 'twk' ||
    first === 'dy' ||
    first === 'qr'
  ) {
    return { fieldName, role: 'dimension', type: 'discrete' }
  }
  return { fieldName, role: 'dimension', type: 'discrete' }
}

type FixedLodComparisonDimensionCalc = {
  partitionBy: string[]
  agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
  field: string
  op: 'gt' | 'ge' | 'lt' | 'le' | 'eq' | 'ne'
  value: number
}

function normalizeFormulaForFixedLodDimension(raw: string): string {
  return String(raw ?? '')
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/\/\/[^\n]*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseFixedLodComparisonDimensionCalc(raw: string): FixedLodComparisonDimensionCalc | null {
  const s = normalizeFormulaForFixedLodDimension(raw)
  if (!s) return null
  const match = s.match(
    /^\{\s*fixed\s+([\s\S]+?)\s*:\s*(sum|avg|min|max|countd|count)\s*\(\s*\[([^\]]+)\]\s*\)\s*\}\s*(>=|<=|<>|!=|=|>|<)\s*(-?\d+(?:\.\d+)?)$/i,
  )
  if (!match) return null
  const partitionSegment = String(match[1] ?? '')
  const partitionBy = Array.from(partitionSegment.matchAll(/\[([^\]]+)\]/g))
    .map((m) => String(m[1] ?? '').trim())
    .filter(Boolean)
  if (partitionBy.length <= 0) return null
  const agg = String(match[2] ?? '').trim().toUpperCase() as FixedLodComparisonDimensionCalc['agg']
  const field = String(match[3] ?? '').trim()
  const opRaw = String(match[4] ?? '').trim()
  const value = Number(match[5])
  if (!field || !Number.isFinite(value)) return null
  const op =
    opRaw === '>' ? 'gt' :
    opRaw === '>=' ? 'ge' :
    opRaw === '<' ? 'lt' :
    opRaw === '<=' ? 'le' :
    opRaw === '=' ? 'eq' :
    'ne'
  return { partitionBy, agg, field, op, value }
}

function normalizeVizAggregation(raw: unknown): string | undefined {
  const value = String(raw ?? '').trim().toLowerCase()
  if (!value || value === 'null' || value === 'undefined') return undefined
  if (value === 'average' || value === 'mean') return 'avg'
  if (value === 'count distinct' || value === 'count_distinct') return 'countd'
  return value
}

function normalizeVizPillToShelfField(pill: unknown): ShelfField | null {
  if (isTableauParameterFieldToken(pill)) return null
  if (isTableauRankFieldToken(pill)) return null
  if (typeof pill === 'string' && pill.trim()) {
    const parsed = parseTableauFieldTokenRole(pill)
    if (!parsed) return null
    const out: ShelfField = {
      field_name: parsed.fieldName,
      role: parsed.role,
      type: parsed.type,
    }
    if (parsed.role === 'measure' && parsed.aggregation) out.aggregation = parsed.aggregation as any
    return out
  }
  if (!pill || typeof pill !== 'object') return null
  const p = pill as any
  const rawFieldName = firstNonEmptyString(p.field_name, p.field, p.name, p.column_name, p.column)
  if (!rawFieldName) return null
  if (isTableauParameterFieldToken(rawFieldName)) return null
  if (isTableauRankFieldToken(rawFieldName)) return null
  const parsedToken = parseTableauFieldTokenRole(rawFieldName)
  const fieldName = parsedToken?.fieldName ?? normalizeVirtualMnvFieldName(rawFieldName)
  const rawDisplay = firstNonEmptyString(p.display_name, p.alias, p.label, p.caption)
  const roleRaw = String(p.role ?? '').trim().toLowerCase()
  const aggregation = normalizeVizAggregation(p.aggregation) ?? parsedToken?.aggregation
  const hasDateTimeGrain = typeof p.time_grain === 'string' && p.time_grain.trim().length > 0
  const isMeasure =
    fieldName === 'Measure Values' ||
    (
      !hasDateTimeGrain &&
      (
        parsedToken?.role === 'measure' ||
        roleRaw === 'measure' ||
        p.is_measure === true ||
        (
          roleRaw !== 'dimension' &&
          !!aggregation &&
          !isVirtualMeasureNamesFieldName(fieldName)
        )
      )
    )
  const role = isMeasure ? 'measure' : 'dimension'
  const typeRaw = String(p.type ?? '').trim().toLowerCase()
  const type =
    typeRaw === 'discrete' || typeRaw === 'continuous'
      ? typeRaw
      : p.is_discrete === true
      ? 'discrete'
      : p.is_discrete === false
      ? 'continuous'
      : parsedToken?.type
      ? parsedToken.type
      : role === 'measure'
      ? 'continuous'
      : 'discrete'
  const out: ShelfField = {
    field_name: fieldName,
    role,
    type,
  }
  if (rawDisplay && rawDisplay !== fieldName) out.display_name = rawDisplay
  if (p.aliases && typeof p.aliases === 'object') out.aliases = p.aliases
  if (typeof p.format === 'string' && p.format.length > 0) out.format = p.format
  if (role === 'measure' && aggregation) out.aggregation = aggregation as any
  if (p.sort && typeof p.sort === 'object') out.sort = { ...(p.sort as object) } as any
  if (p.sort_config && typeof p.sort_config === 'object') {
    out.sort_config = { ...(p.sort_config as object) } as any
  }
  if (p.sortConfig && typeof p.sortConfig === 'object') {
    out.sortConfig = { ...(p.sortConfig as object) } as any
  }
  if (typeof p.time_grain === 'string' && p.time_grain.trim()) {
    ;(out as any).time_grain = p.time_grain.trim()
  }
  return out
}

function normalizeVizPillArray(raw: unknown): ShelfField[] {
  const values = Array.isArray(raw)
    ? raw
    : (typeof raw === 'string' && raw.trim()) || (raw && typeof raw === 'object')
    ? [raw]
    : []
  const out: ShelfField[] = []
  for (const value of values) {
    const field = normalizeVizPillToShelfField(value)
    if (field) out.push(field)
  }
  return out
}

function vizSqlProjectionFieldKey(field: ShelfField): string {
  const role = String(field.role ?? '').trim().toLowerCase()
  const rawAgg = String(field.aggregation ?? '').trim().toLowerCase()
  const effectiveAgg = role === 'measure' ? rawAgg || 'sum' : rawAgg
  return [
    role,
    normalizeTableauFieldKey(field.field_name),
    effectiveAgg,
    String((field as any).time_grain ?? '').trim().toLowerCase(),
  ].join('|')
}

function addUniqueVizProjectionField(target: ShelfField[], seen: Set<string>, field: ShelfField): void {
  const key = vizSqlProjectionFieldKey(field)
  if (seen.has(key)) return
  seen.add(key)
  target.push(field)
}

function firstNormalizedVizPill(raw: unknown): ShelfField | undefined {
  return normalizeVizPillArray(raw)[0]
}

function normalizeVizMarkType(raw: unknown): CrosstabSpecV1['marks']['type'] {
  const value = String(raw ?? '').trim().toLowerCase()
  if (value === 'bar' || value === 'line' || value === 'square' || value === 'circle' || value === 'text') {
    return value
  }
  if (value === 'area') return 'line'
  return 'text'
}

function normalizeVizMarks(raw: unknown): CrosstabSpecV1['marks'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const marks = raw as any
  const out: CrosstabSpecV1['marks'] = {
    type: normalizeVizMarkType(marks.type ?? marks.mark),
  }
  const color = firstNormalizedVizPill(marks.color)
  const size = firstNormalizedVizPill(marks.size)
  const label = firstNormalizedVizPill(marks.label ?? marks.text)
  const tooltip = normalizeVizPillArray(marks.tooltip)
  if (color) out.color = color
  if (size) out.size = size
  if (label) out.label = label
  if (tooltip.length > 0) out.tooltip = tooltip
  return out
}

function extractMeasureNamesFilterValues(filters: unknown): Array<{ field_name: string }> {
  if (!Array.isArray(filters)) return []
  const out: Array<{ field_name: string }> = []
  const seen = new Set<string>()
  for (const filter of filters) {
    if (!filter || typeof filter !== 'object') continue
    const f = filter as any
    if (!isVirtualMeasureNamesFieldName(f.field_name)) continue
    const rawValue = f.value
    const values = Array.isArray(rawValue) ? rawValue : [rawValue]
    for (const value of values) {
      if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') continue
      const label = String(value).trim()
      if (!label || seen.has(label)) continue
      seen.add(label)
      out.push({ field_name: label })
    }
  }
  return out
}

function normalizeVizComputedSorts(rows: ShelfField[], columns: ShelfField[]): void {
  const projectedMeasures = [...rows, ...columns].filter(
    (field) => field.role === 'measure' && !isVirtualMnvFieldName(field.field_name),
  )
  const soleMeasureField = projectedMeasures.length === 1 ? projectedMeasures[0]?.field_name : null
  if (!soleMeasureField) return
  const normalizeKey = (value: string): string =>
    value.trim().replace(/^\[|\]$/g, '').replace(/\s+/g, ' ').toLowerCase()
  const projectedKeys = new Set<string>()
  for (const measure of projectedMeasures) {
    projectedKeys.add(normalizeKey(measure.field_name))
    if (measure.display_name) projectedKeys.add(normalizeKey(measure.display_name))
  }
  const ambiguousComputedSortTargets = new Set(['', 'calculation', 'measure', 'measure value', 'measure values'])
  for (const shelf of [...rows, ...columns]) {
    const sort = shelf.sort ?? shelf.sort_config ?? shelf.sortConfig
    if (!sort || typeof sort !== 'object') continue
    const sortType = String((sort as any).type ?? '').trim().toLowerCase()
    if (sortType !== 'computed') continue
    const byFieldRaw = String((sort as any).by_field ?? '').trim()
    const byKey = normalizeKey(byFieldRaw)
    if (projectedKeys.has(byKey) && !ambiguousComputedSortTargets.has(byKey)) continue
    const patched = { ...(sort as object), type: 'field', by_field: soleMeasureField } as any
    if (shelf.sort === sort) shelf.sort = patched
    if (shelf.sort_config === sort) shelf.sort_config = patched
    if (shelf.sortConfig === sort) shelf.sortConfig = patched
  }
}

function normalizeWorksheetSpecForTypedLowering(
  worksheetSpec: WorksheetSpecV1,
  context: { workbookIr?: unknown; worksheetName?: string | null | undefined } = {},
): WorksheetSpecNormalizationOutcome {
  const workbookWorksheetMarkEncodings = (): Record<string, unknown[]> | null => {
    const worksheetKey = firstNonEmptyString(
      context.worksheetName,
      (worksheetSpec as any)?.name,
      (worksheetSpec as any)?.source?.tableau?.worksheet_name,
      (worksheetSpec as any)?.source?.tableau?.worksheetName,
    )?.trim().toLowerCase()
    if (!worksheetKey) return null
    const worksheets = Array.isArray((context.workbookIr as any)?.worksheets)
      ? ((context.workbookIr as any).worksheets as any[])
      : []
    const rawWorksheet = worksheets.find((entry) => {
      const candidates = [
        (entry as any)?.worksheet_name,
        (entry as any)?.worksheetName,
        (entry as any)?.internal_name,
        (entry as any)?.internalName,
        (entry as any)?.name,
      ]
      return candidates.some((candidate) => String(candidate ?? '').trim().toLowerCase() === worksheetKey)
    })
    if (!rawWorksheet || typeof rawWorksheet !== 'object') return null
    const marks: Record<string, unknown[]> = {}
    const panes = Array.isArray((rawWorksheet as any).panes) ? ((rawWorksheet as any).panes as any[]) : []
    for (const pane of panes) {
      const encodings = Array.isArray((pane as any)?.encodings) ? ((pane as any).encodings as any[]) : []
      for (const encoding of encodings) {
        const kind = String((encoding as any)?.kind ?? '').trim().toLowerCase()
        const field = firstNonEmptyString((encoding as any)?.field, (encoding as any)?.column, (encoding as any)?.name)
        if (!kind || !field) continue
        const slot =
          kind === 'text' ? 'label' :
          kind === 'label' || kind === 'color' || kind === 'size' || kind === 'detail' || kind === 'shape' || kind === 'path' || kind === 'angle' || kind === 'tooltip'
            ? kind
            : ''
        if (!slot) continue
        const target = marks[slot] ?? (marks[slot] = [])
        target.push(field)
      }
    }
    return Object.keys(marks).length > 0 ? marks : null
  }

  const addMarkProjectionFieldsToShelves = (
    rows: ShelfField[],
    columns: ShelfField[],
    rawMarks: unknown,
  ): void => {
    if (!rawMarks || typeof rawMarks !== 'object') return
    const markProjectionSeen = new Set<string>()
    for (const field of [...rows, ...columns]) {
      if (isShelfFieldRef(field)) markProjectionSeen.add(vizSqlProjectionFieldKey(field))
    }
    const addMarkProjection = (raw: unknown, options: { includeStringTokens?: boolean } = {}): void => {
      const includeStringTokens = options.includeStringTokens !== false
      const projectionRaw =
        includeStringTokens
          ? raw
          : Array.isArray(raw)
          ? raw.filter((entry) => entry && typeof entry === 'object')
          : raw && typeof raw === 'object'
          ? raw
          : undefined
      for (const field of normalizeVizPillArray(projectionRaw)) {
        if (isVirtualMnvFieldName(field.field_name)) continue
        const target = field.role === 'measure' ? columns : rows
        addUniqueVizProjectionField(target, markProjectionSeen, field)
      }
    }
    const marks = rawMarks as any
    addMarkProjection(marks.detail)
    addMarkProjection(marks.color)
    addMarkProjection(marks.size)
    addMarkProjection(marks.label ?? marks.text)
    addMarkProjection(marks.tooltip, { includeStringTokens: false })
  }

  if (worksheetSpec.kind === 'crosstab') {
    const crosstab = worksheetSpec.spec as CrosstabSpecV1 | undefined
    if (!crosstab || typeof crosstab !== 'object') return { ok: true, worksheetSpec }
    const rows = Array.isArray(crosstab.rows) ? [...(crosstab.rows as ShelfField[])] : []
    const columns = Array.isArray(crosstab.columns) ? [...(crosstab.columns as ShelfField[])] : []
    addMarkProjectionFieldsToShelves(rows, columns, crosstab.marks)
    addMarkProjectionFieldsToShelves(rows, columns, workbookWorksheetMarkEncodings())
    return {
      ok: true,
      worksheetSpec: {
        ...worksheetSpec,
        spec: {
          ...(crosstab as object),
          rows,
          columns,
        } as CrosstabSpecV1,
      },
    }
  }
  if (worksheetSpec.kind !== 'viz') {
    return { ok: false, reason: `worksheet_kind_unsupported:${worksheetSpec.kind}` }
  }
  const viz = worksheetSpec.spec
  if (!viz || typeof viz !== 'object') return { ok: false, reason: 'missing_crosstab_spec' }
  const shelves = (viz as any).shelves && typeof (viz as any).shelves === 'object'
    ? (viz as any).shelves
    : {}
  const rows = normalizeVizPillArray((shelves as any).rows)
  const columns = normalizeVizPillArray((shelves as any).columns)
  normalizeVizComputedSorts(rows, columns)
  const rawFilters = Array.isArray((viz as any).filters) ? (viz as any).filters : []
  const measureValuesFromFilters = extractMeasureNamesFilterValues(rawFilters)
  const hasMeasureValuesPill = [...rows, ...columns].some(
    (field) => normalizeVirtualMnvFieldName(field.field_name) === 'Measure Values',
  )
  const marks = normalizeVizMarks((viz as any).marks)
  const virtualMeasureNamesMarkCandidates: ShelfField[] = []
  const collectVirtualMeasureNamesMark = (candidateMarks: CrosstabSpecV1['marks'] | undefined): void => {
    if (!candidateMarks) return
    for (const field of [
      candidateMarks.color,
      candidateMarks.size,
      candidateMarks.label,
      ...(Array.isArray(candidateMarks.tooltip) ? candidateMarks.tooltip : []),
    ]) {
      if (isShelfFieldRef(field) && isVirtualMeasureNamesFieldName(field.field_name)) {
        virtualMeasureNamesMarkCandidates.push(field)
      }
    }
  }
  collectVirtualMeasureNamesMark(marks)
  const layersForMeasureNamesMarks = Array.isArray((viz as any).layers) ? (viz as any).layers : []
  for (const layer of layersForMeasureNamesMarks) {
    collectVirtualMeasureNamesMark(normalizeVizMarks((layer as any)?.marks))
  }
  const measureNamesMarkShelf =
    virtualMeasureNamesMarkCandidates.find((field) => {
      const sort = field.sort ?? field.sort_config ?? field.sortConfig
      return !!sort && typeof sort === 'object' && Array.isArray((sort as any).custom_order)
    }) ??
    virtualMeasureNamesMarkCandidates[0] ??
    null
  const hasMeasureNamesMark = !!measureNamesMarkShelf
  if (hasMeasureValuesPill && hasMeasureNamesMark) {
    const hasMeasureNamesShelf = [...rows, ...columns].some((field) =>
      isVirtualMeasureNamesFieldName(field.field_name),
    )
    if (!hasMeasureNamesShelf) {
      columns.unshift({
        ...(measureNamesMarkShelf ?? {}),
        field_name: 'Measure Names',
        role: 'dimension',
        type: 'discrete',
      } as ShelfField)
    }
  }
  if (hasMeasureValuesPill && measureValuesFromFilters.length > 0) {
    const measureValueKeys = new Set(
      measureValuesFromFilters.map((entry) => entry.field_name.trim().toLowerCase()),
    )
    const projectedMeasureKeys = new Set(
      [...rows, ...columns]
        .filter((field) => field.role === 'measure')
        .flatMap((field) => [field.field_name, field.display_name ?? ''])
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean),
    )
    const layerMeasures: ShelfField[] = []
    const layers = Array.isArray((viz as any).layers) ? (viz as any).layers : []
    for (const layer of layers) {
      if (!layer || typeof layer !== 'object') continue
      const layerShelves =
        (layer as any).shelves && typeof (layer as any).shelves === 'object'
          ? (layer as any).shelves
          : {}
      for (const field of [
        ...normalizeVizPillArray((layerShelves as any).rows),
        ...normalizeVizPillArray((layerShelves as any).columns),
      ]) {
        if (field.role === 'measure' && !isVirtualMnvFieldName(field.field_name)) {
          layerMeasures.push(field)
        }
      }
    }
    const addSortHelperMeasureValue = (targetRaw: string): void => {
      const target = targetRaw.trim()
      if (!target) return
      const targetKey = target.toLowerCase()
      if (measureValueKeys.has(targetKey) || projectedMeasureKeys.has(targetKey)) return
      const match = layerMeasures.find((field) =>
        [field.field_name, field.display_name ?? ''].some(
          (value) => value.trim().toLowerCase() === targetKey,
        ),
      )
      if (!match) return
      measureValuesFromFilters.push({
        field_name: match.field_name,
        ...(match.aggregation ? { aggregation: match.aggregation } : {}),
      } as any)
      measureValueKeys.add(match.field_name.trim().toLowerCase())
      if (match.display_name) measureValueKeys.add(match.display_name.trim().toLowerCase())
    }
    for (const shelf of [...rows, ...columns]) {
      const sort = shelf.sort ?? shelf.sort_config ?? shelf.sortConfig
      if (!sort || typeof sort !== 'object') continue
      const sortType = String((sort as any).type ?? '').trim().toLowerCase()
      if (sortType !== 'computed') continue
      const byField = String((sort as any).by_field ?? '').trim()
      addSortHelperMeasureValue(byField)
    }
  }
  const filters = rawFilters.filter((filter) => {
    if (!filter || typeof filter !== 'object') return true
    return !isVirtualMeasureNamesFieldName((filter as any).field_name)
  })
  // Viz worksheets often carry their query-driving fields on Marks
  // instead of Rows/Columns. In Tableau those pills still define the
  // worksheet projection/granularity: detail contributes dimensions,
  // and color/text/tooltip/size can contribute measures. Lower them
  // into the typed projection once here so strict typed-required mode
  // sees the same query shape the legacy viz builder already used.
  addMarkProjectionFieldsToShelves(rows, columns, (viz as any).marks)
  addMarkProjectionFieldsToShelves(rows, columns, workbookWorksheetMarkEncodings())
  const crosstab: CrosstabSpecV1 = {
    ...(viz as object),
    rows,
    columns,
    filters,
    ...(marks ? { marks } : {}),
  } as CrosstabSpecV1
  const rawMeasureValues =
    (viz as any).measureValues ?? (viz as any).measure_values ?? (viz as any).measurevalues
  if (Array.isArray(rawMeasureValues)) {
    ;(crosstab as any).measureValues = rawMeasureValues
  } else if (hasMeasureValuesPill && measureValuesFromFilters.length > 0) {
    crosstab.measureValues = measureValuesFromFilters
  }
  return {
    ok: true,
    worksheetSpec: {
      ...worksheetSpec,
      kind: 'crosstab',
      spec: crosstab,
    },
  }
}

type ShelfSort = NonNullable<ShelfField['sort']>

/**
 * Returns the effective sort config on a shelf, preferring `sort_config`
 * then `sortConfig` then `sort` (matching the precedence the legacy
 * compiler uses). Null when the shelf has no sort at all.
 */
function resolveShelfSort(field: ShelfField): ShelfSort | null {
  const candidate = field.sort_config ?? field.sortConfig ?? field.sort ?? null
  if (!candidate || typeof candidate !== 'object') return null
  return candidate
}

type SortLoweringOutcome =
  | { ok: true; descriptor: SortDescriptor }
  | { ok: false; reason: string }

type SortDescriptor =
  | { kind: 'none' }
  | { kind: 'self'; descending: boolean }
  | { kind: 'by_field'; byField: string; descending: boolean }
  | { kind: 'custom_order'; values: string[]; descending: boolean }

/**
 * Shelf-sort analysis. Returns a descriptor the main lowering resolves
 * once the full measures list is known:
 *   - `self`: `ORDER BY "<shelf_field>" [DESC]` (dimension natural sort
 *     or measure-shelf-default-alias sort).
 *   - `by_field`: `ORDER BY "<measure_alias_of_by_field>" [DESC]`.
 *     If the referenced field is not otherwise visible but can be
 *     resolved from the semantic projection, the main lowering adds a
 *     sort-helper aggregate for it.
 *   - `none`: shelf has no sort.
 *
 * Everything outside this surface (custom_order, computed aggregate
 * sort) falls back to `ts-legacy` with a stable reason.
 */
function tryLowerShelfSort(field: ShelfField): SortLoweringOutcome {
  const sort = resolveShelfSort(field)
  if (!sort) return { ok: true, descriptor: { kind: 'none' } }
  const sortType = String(sort.type ?? '').trim().toLowerCase()
  if (sortType === 'computed') {
    const byField = String(sort.by_field ?? '').trim()
    if (!byField) {
      return { ok: false, reason: 'shelf_sort_computed_not_yet_typed' }
    }
  }
  // `shelf-sort-v2` is a newer authoring-metadata tag. The legacy
  // query builder has no special handling for it (grep confirms zero
  // references in `worksheetQueryBuilder.ts`), so it behaves identically
  // to a natural / alphabetic sort with the same direction / by_field.
  // Accept it as a pure type-tag alias; any custom_order or computed
  // nuance is still gated by the earlier checks in this function.
  const directionRaw = String(sort.direction ?? '').trim().toLowerCase()
  let descending = false
  if (directionRaw === '' || directionRaw === 'asc' || directionRaw === 'ascending') {
    descending = false
  } else if (directionRaw === 'desc' || directionRaw === 'descending') {
    descending = true
  } else {
    return { ok: false, reason: `shelf_sort_direction_not_yet_typed:${directionRaw}` }
  }
  if (typeof sort.by_field === 'string' && sort.by_field.length > 0) {
    return { ok: true, descriptor: { kind: 'by_field', byField: sort.by_field, descending } }
  }
  // PR7 custom-order lowering. Tableau authors a `custom_order: [...]`
  // array on a shelf dimension when the user fixes an explicit row/
  // column sequence (drag-to-reorder, or "Sort → Manual"). We lower
  // this to an ORDER BY CASE emitted by both the Rust compiler and TS
  // oracle; `tryLowerShelfSort` used to reject with
  // `shelf_sort_custom_order_not_yet_typed` and this branch replaces
  // that rejection.
  if (Array.isArray(sort.custom_order) && sort.custom_order.length > 0) {
    const values: string[] = []
    for (const v of sort.custom_order as unknown[]) {
      if (typeof v === 'string') {
        values.push(v)
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        values.push(String(v))
      } else {
        return { ok: false, reason: 'shelf_sort_custom_order_value_non_scalar' }
      }
    }
    return { ok: true, descriptor: { kind: 'custom_order', values, descending } }
  }
  return { ok: true, descriptor: { kind: 'self', descending } }
}

/**
 * Strict gate. Returns a `TypedWorksheetSpec` only when every field on
 * the authored worksheet spec is structurally trivial enough that the
 * Rust compiler's Phase 4 `compile_typed` path can own it end-to-end.
 */
/**
 * Lean inputs the typed-spec lowering actually reads. Keeps the
 * production authoring path (`tryBuildTypedSpecFromAuthoring`) and the
 * vizql/worker paths (`tryBuildTypedSpecFromVizqlPlan`) on the same
 * core — any gate widening lands once here, not twice.
 */
export type TypedSpecLoweringInputs = {
  worksheetSpec: WorksheetSpecV1 | null | undefined
  runtimeFilters: readonly unknown[]
  measureValuesOverride: unknown
  worksheetName: string | null | undefined
  workbookIr: unknown
  fieldManifest?: unknown
  semanticSpec?: unknown
  extractUri: string | null | undefined
  baseFromSql?: string | null | undefined
  limit: number | null | undefined
  /**
   * PR4 Workstream D step 1: paging-driven projection requirement
   * (see `presentation-gap-ownership.md` item 5). Each named field
   * MUST resolve to a column already projected by the worksheet
   * (dimension, measure alias, or measure field_name). Unknown
   * fields reject so TS cannot silently re-introduce post-compile
   * column stripping or invent extra columns.
   */
  pagingProjection?: { fields: readonly string[] } | null
}

export function tryBuildTypedSpecFromAuthoring(
  prepared: PreparedWorksheetExecutionCore,
): TypedSpecLoweringOutcome {
  return tryBuildTypedSpecFromInputs({
    worksheetSpec: prepared.request.normalizedRequest.worksheetSpec as
      | WorksheetSpecV1
      | undefined,
    runtimeFilters: Array.isArray(prepared.request.normalizedRequest.filters)
      ? prepared.request.normalizedRequest.filters
      : [],
    measureValuesOverride: prepared.request.normalizedRequest.measureValuesOverride,
    worksheetName: prepared.request.normalizedRequest.worksheetName,
    workbookIr: (prepared.request as any).authoringState?.workbookIr,
    fieldManifest:
      (prepared.queryOptions as any)?.fieldManifest ??
      (prepared.queryOptions as any)?.field_manifest ??
      null,
    semanticSpec:
      (prepared.queryOptions as any)?.semanticSpec ??
      (prepared.queryOptions as any)?.semantic_spec ??
      null,
    extractUri: (prepared.queryOptions as any)?.extractUri,
    baseFromSql:
      (prepared.queryOptions as any)?.baseFromSql ??
      (prepared.queryOptions as any)?.base_from_sql ??
      null,
    limit: (prepared.queryOptions as any)?.limit,
    pagingProjection: (prepared.queryOptions as any)?.pagingProjection ?? null,
  })
}

export function tryBuildTypedSpecFromInputs(
  inputs: TypedSpecLoweringInputs,
): TypedSpecLoweringOutcome {
  const rawWorksheetSpec = inputs.worksheetSpec
  if (!rawWorksheetSpec) return unsupported('missing_worksheet_spec')
  const normalizedWorksheetSpec = normalizeWorksheetSpecForTypedLowering(rawWorksheetSpec, {
    workbookIr: inputs.workbookIr,
    worksheetName: inputs.worksheetName,
  })
  if (!normalizedWorksheetSpec.ok) return unsupported(normalizedWorksheetSpec.reason)
  const worksheetSpec = normalizedWorksheetSpec.worksheetSpec
  const worksheetName = firstNonEmptyString(
    inputs.worksheetName,
    (worksheetSpec as any)?.name,
    (worksheetSpec as any)?.source?.tableau?.worksheet_name,
  )

  const crosstab = worksheetSpec.spec as CrosstabSpecV1 | undefined
  if (!crosstab) return unsupported('missing_crosstab_spec')

  // `Measure Names` / `Measure Values` are virtual Tableau pills, not
  // real columns. When the classic MNV authoring pattern is used —
  // `Measure Names` as a dimension on a shelf, plus a populated
  // `crosstab.measureValues` list — the SQL projection is:
  //   - the non-virtual dimensions on rows/columns,
  //   - one column per entry in `measureValues` (each a TypedMeasure).
  // We keep the `Measure Names` pill out of the typed spec entirely
  // (it does not map to any real SQL column) and rely on the
  // `measureValues` unfolding below to materialize the measures. This
  // mirrors Tableau Desktop's rendering: N side-by-side measure
  // columns, labeled with each measure's caption.
  const isVirtualMnvField = (fieldName: string): boolean => {
    const k = fieldName.trim().toLowerCase().replace(/^:+/, '').replace(/\s+/g, ' ')
    return k === 'measure names' || k === 'measure values' || k === 'multiple values'
  }
  // Normalize a `crosstab.measureValues` entry into a `{ field, aggregation }`
  // shape. Accepts bare strings, full `ShelfField`s, and legacy
  // `{ field_name, aggregation }` / `{ field, aggregation }` objects.
  type MeasureValueEntry = { field: string; aggregation: string | null; caption?: string | null }
  const extractMeasureValueEntry = (entry: unknown): MeasureValueEntry | 'invalid' => {
    if (typeof entry === 'string' && entry.length > 0) return { field: entry, aggregation: null }
    if (!entry || typeof entry !== 'object') return 'invalid'
    const e = entry as any
    const field =
      typeof e.field_name === 'string' && e.field_name.length > 0
        ? e.field_name
        : typeof e.field === 'string' && e.field.length > 0
        ? e.field
        : ''
    if (!field) return 'invalid'
    const aggRaw = typeof e.aggregation === 'string' ? e.aggregation : null
    return { field, aggregation: aggRaw }
  }
  // Collect measures declared directly on shelves (the "one measure
  // pill per shelf" authoring mode). In the classic MNV pattern this
  // is typically empty — measures live exclusively in `measureValues`.
  // We use this below to decide whether `measureValues` is driving the
  // projection (empty shelf-measure set) or is a redundant mirror of
  // shelf measures (per-entry match required).
  const shelfMeasureOrder: string[] = []
  const collectShelfMeasureOrder = (entries: unknown): void => {
    if (!Array.isArray(entries)) return
    for (const e of entries) {
      if (!isShelfFieldRef(e)) continue
      const s = e as ShelfField
      if (s.role !== 'measure') continue
      if (isVirtualMnvField(s.field_name)) continue
      shelfMeasureOrder.push(s.field_name)
    }
  }
  collectShelfMeasureOrder(crosstab.rows)
  collectShelfMeasureOrder(crosstab.columns)

  const hasVirtualMnvAuthoringContext = (() => {
    const scanEntries = (entries: unknown): boolean => {
      if (!Array.isArray(entries)) return false
      return entries.some((entry) =>
        isShelfFieldRef(entry) && isVirtualMnvField((entry as ShelfField).field_name),
      )
    }
    if (scanEntries(crosstab.rows) || scanEntries(crosstab.columns)) return true
    const marks = crosstab.marks
    const markFields = [
      marks?.color,
      marks?.size,
      marks?.label,
      ...(Array.isArray(marks?.tooltip) ? marks.tooltip : []),
    ]
    if (markFields.some((entry) =>
      isShelfFieldRef(entry) && isVirtualMnvField((entry as ShelfField).field_name),
    )) {
      return true
    }
    const filters = Array.isArray(crosstab.filters) ? crosstab.filters : []
    return filters.some((filter: any) => isVirtualMnvField(String(filter?.field_name ?? filter?.field ?? '')))
  })()

  // `measureValuesFromMnv` is the unfolded measure projection the
  // classic MNV pattern implies. It's populated only when the typed
  // spec will need to project one column per `crosstab.measureValues`
  // entry instead of relying on shelf-measure pills. Downstream code
  // appends these after the shelf-derived measures so column order
  // matches Tableau Desktop (shelves-first, measure-values list
  // order second).
  // Extract the `Measure Names` pill's `sort.custom_order` if authored.
  // When present, it declares the exact set (and order) of measure
  // labels that participate in the MNV pivot. Any entry in
  // `crosstab.measureValues` whose caption is NOT in this set is
  // projected as a regular side-by-side measure column, not melted
  // into the (`Measure Names`, `Measure Values`) pair. This matches
  // Tableau Desktop: authors can keep some measures out of the pivot
  // (e.g. `newcustomercount`, `repeatcustomercount`) while still
  // listing them on `measureValues` for tooltip / mark purposes; the
  // resulting crosstab header has both `Measure Values` and the
  // un-pivoted measure columns side by side.
  const measureNamesCustomOrder: string[] = (() => {
    const scan = (entries: unknown): string[] | null => {
      if (!Array.isArray(entries)) return null
      for (const e of entries) {
        if (!isShelfFieldRef(e)) continue
        if (!isVirtualMnvField((e as ShelfField).field_name)) continue
        const sort = (e as any).sort
        if (!sort || typeof sort !== 'object') continue
        const order = (sort as any).custom_order
        if (!Array.isArray(order)) continue
        const out: string[] = []
        for (const v of order) if (typeof v === 'string' && v.length > 0) out.push(v)
        if (out.length > 0) return out
      }
      return null
    }
    return scan(crosstab.rows) ?? scan(crosstab.columns) ?? []
  })()
  const measureValuesFromMnv: MeasureValueEntry[] = []
  const measureNamesLabelMeasureIndex: Map<string, MeasureValueEntry> = (() => {
    const out = new Map<string, MeasureValueEntry>()
    const workbookIrForMeasureNames = inputs.workbookIr
    if (!workbookIrForMeasureNames || typeof workbookIrForMeasureNames !== 'object') return out
    const fd = (workbookIrForMeasureNames as { field_dictionary?: unknown }).field_dictionary
    if (!fd || typeof fd !== 'object') return out
    const columns = Array.isArray((fd as any).columns) ? (fd as any).columns as any[] : []
    const columnInstances = Array.isArray((fd as any).column_instances) ? (fd as any).column_instances as any[] : []
    const labelKey = (raw: unknown): string => normalizeTableauFieldKey(String(raw ?? '').trim()) || String(raw ?? '').trim().toLowerCase()
    const cleanCiToken = (raw: unknown): string => {
      let s = String(raw ?? '').trim()
      if (!s) return ''
      s = s.replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()
      const qualified = s.match(/\]\.\[([^\]]+)\]\s*$/)
      if (qualified) s = `[${qualified[1]}]`
      return s
    }
    const ciKeys = (raw: unknown): string[] => {
      const cleaned = cleanCiToken(raw)
      const unbracketed = cleaned.replace(/^\[/, '').replace(/\]$/, '').trim()
      return [String(raw ?? '').trim(), cleaned, unbracketed]
        .map((v) => labelKey(v))
        .filter((v, i, arr): v is string => Boolean(v) && arr.indexOf(v) === i)
    }
    const ciByKey = new Map<string, any>()
    for (const ci of columnInstances) {
      if (!ci || typeof ci !== 'object') continue
      for (const key of ciKeys((ci as any).name)) {
        if (!ciByKey.has(key)) ciByKey.set(key, ci)
      }
    }
    const aggregationForCi = (ci: any): string | null => {
      const rawDerivation = String((ci as any)?.derivation ?? (ci as any)?.attrs?.derivation ?? '').trim().toLowerCase()
      const rawName = cleanCiToken((ci as any)?.name).replace(/^\[/, '').replace(/\]$/, '').trim()
      const tokenAgg = String(rawName.split(':')[0] ?? '').trim().toLowerCase()
      const raw = rawDerivation || tokenAgg
      if (raw === 'countd' || raw === 'count distinct' || raw === 'count_distinct' || raw === 'ctd') return 'countd'
      if (raw === 'count') return 'count'
      if (raw === 'avg' || raw === 'average') return 'avg'
      if (raw === 'min' || raw === 'max' || raw === 'sum') return raw
      return null
    }
    const measureEntryForCi = (ci: any, captionRaw: unknown): MeasureValueEntry | null => {
      const columnRaw = String((ci as any)?.column ?? '').trim()
      const field = columnRaw.replace(/^\[/, '').replace(/\]$/, '').trim()
      if (!field) return null
      return {
        field,
        aggregation: aggregationForCi(ci),
        caption: String(captionRaw ?? (ci as any)?.caption ?? '').trim() || null,
      }
    }
    const addEntry = (captionRaw: unknown, ci: any): void => {
      const key = labelKey(captionRaw)
      if (!key || out.has(key)) return
      const entry = measureEntryForCi(ci, captionRaw)
      if (entry) out.set(key, entry)
    }
    for (const ci of columnInstances) {
      if (!ci || typeof ci !== 'object') continue
      if (typeof (ci as any).caption === 'string' && (ci as any).caption.trim()) {
        addEntry((ci as any).caption, ci)
      }
    }
    for (const c of columns) {
      if (!c || typeof c !== 'object') continue
      if (!isVirtualMnvField(String((c as any).name ?? ''))) continue
      const aliases = Array.isArray((c as any).aliases) ? (c as any).aliases as any[] : []
      for (const alias of aliases) {
        if (!alias || typeof alias !== 'object') continue
        const caption = String((alias as any).value ?? '').trim()
        const rawKey = String((alias as any).key ?? '').trim()
        if (!caption || !rawKey) continue
        let ci: any | null = null
        for (const key of ciKeys(rawKey)) {
          ci = ciByKey.get(key) ?? null
          if (ci) break
        }
        if (ci) addEntry(caption, ci)
      }
    }
    return out
  })()
  const resolveMeasureNamesLabelMeasureEntry = (raw: unknown): MeasureValueEntry | null => {
    const key = normalizeTableauFieldKey(String(raw ?? '').trim()) || String(raw ?? '').trim().toLowerCase()
    if (!key) return null
    const entry = measureNamesLabelMeasureIndex.get(key)
    if (entry) return { ...entry }
    return inferCountMeasureEntryFromMeasureNameLabel(raw)
  }
  const inferCountMeasureEntryFromMeasureNameLabel = (raw: unknown): MeasureValueEntry | null => {
    const label = String(raw ?? '').trim()
    const m = label.match(/^count\s+of\s+(.+)$/i)
    if (!m) return null
    const targetRaw = String(m[1] ?? '').trim()
    if (!targetRaw) return null
    const targetForms = new Set<string>()
    const addTarget = (value: string) => {
      const k = normalizeTableauFieldKey(value)
      if (k) targetForms.add(k)
      const singular = k.replace(/s$/i, '').trim()
      if (singular) targetForms.add(singular)
    }
    addTarget(targetRaw)
    addTarget(targetRaw.replace(/\bcustomers\b/i, 'customer name'))
    addTarget(targetRaw.replace(/\bcustomer\b/i, 'customer name'))

    const workbookIrForCountLabel = inputs.workbookIr
    const fd = (workbookIrForCountLabel && typeof workbookIrForCountLabel === 'object')
      ? (workbookIrForCountLabel as { field_dictionary?: unknown }).field_dictionary
      : null
    const candidates: Array<{ field: string; caption?: string | null; score: number }> = []
    const addCandidate = (fieldRaw: unknown, captionRaw: unknown, scoreBase: number) => {
      const field = String(fieldRaw ?? '').trim().replace(/^\[/, '').replace(/\]$/, '').trim()
      if (!field || isVirtualMnvField(field)) return
      const caption = String(captionRaw ?? '').trim()
      const keys = [
        normalizeTableauFieldKey(field),
        normalizeTableauFieldKey(caption),
      ].filter(Boolean) as string[]
      let score = 0
      for (const key0 of keys) {
        const key = key0.trim()
        if (!key) continue
        const singular = key.replace(/s$/i, '').trim()
        if (targetForms.has(key) || (singular && targetForms.has(singular))) score = Math.max(score, scoreBase + 4)
        for (const target of targetForms) {
          if (target && (key.includes(target) || target.includes(key))) score = Math.max(score, scoreBase + 1)
        }
      }
      if (score > 0) candidates.push({ field, caption: caption || null, score })
    }
    if (fd && typeof fd === 'object') {
      const columns = Array.isArray((fd as any).columns) ? ((fd as any).columns as any[]) : []
      const columnInstances = Array.isArray((fd as any).column_instances) ? ((fd as any).column_instances as any[]) : []
      for (const c of columns) addCandidate((c as any)?.name, (c as any)?.caption, 1)
      for (const ci of columnInstances) {
        const columnRaw = String((ci as any)?.column ?? '').trim()
        const nameRaw = String((ci as any)?.name ?? '').trim()
        const field = columnRaw || nameRaw
        addCandidate(field, (ci as any)?.caption ?? nameRaw, 2)
      }
    }
    candidates.sort((a, b) => b.score - a.score || a.field.localeCompare(b.field))
    const best = candidates[0]
    if (!best) return null
    return { field: best.field, aggregation: 'countd', caption: label }
  }
  // Some Tableau exports omit an explicit `crosstab.measureValues`
  // array even though the worksheet is still authored as an MNV
  // crosstab: the `Measure Names` pill is on a shelf and its
  // categorical filter carries the active measure domain. Treat that
  // filter value list as the declared MNV projection when no richer
  // measureValues list is present.
  const measureNamesFilterValues: string[] = (() => {
    const out: string[] = []
    const seen = new Set<string>()
    const scanFilters = (filters: unknown): void => {
      if (!Array.isArray(filters)) return
      for (const filter of filters) {
        if (!filter || typeof filter !== 'object') continue
        const field = String((filter as any).field_name ?? (filter as any).field ?? (filter as any).display_name ?? '').trim()
        if (!isVirtualMnvField(field)) continue
        const value = (filter as any).value
        const op = String((filter as any).operator ?? '').trim().toLowerCase().replace(/\s+/g, '_')
        if (op && op !== 'in' && op !== 'include' && op !== 'equals' && op !== '=') continue
        const values = Array.isArray(value) ? value : [value]
        for (const raw of values) {
          if (raw === null || raw === undefined) continue
          if (typeof raw !== 'string' && typeof raw !== 'number' && typeof raw !== 'boolean') continue
          const s = String(raw).trim()
          if (!s || seen.has(s)) continue
          seen.add(s)
          out.push(s)
        }
      }
    }
    scanFilters(crosstab.filters)
    scanFilters(inputs.runtimeFilters)
    return out
  })()
  // Also surface the full parsed `crosstab.measureValues` list so the
  // `measureValuesOverride` gate below can treat the authored MNV
  // domain as part of the effective measure universe, even in the
  // "mirror" case where `measureValuesFromMnv` stays empty because
  // shelves already project the measures. This matches the plan's
  // ownership doc: overrides can select from / reorder the authored
  // MNV universe but cannot introduce unauthored measures.
  const parsedMeasureValuesAll: MeasureValueEntry[] = []
  if (Array.isArray(crosstab.measureValues) && crosstab.measureValues.length > 0) {
    const parsed: MeasureValueEntry[] = []
    for (const entry of crosstab.measureValues) {
      const e = extractMeasureValueEntry(entry)
      if (e === 'invalid') return unsupported('measure_values_entry_shape_invalid')
      if (isVirtualMnvField(e.field)) continue
      const resolved = resolveMeasureNamesLabelMeasureEntry(e.field)
      parsed.push(resolved ? {
        ...resolved,
        aggregation: resolved.aggregation ?? e.aggregation,
        caption: resolved.caption ?? e.field,
      } : e)
    }
    for (const p of parsed) parsedMeasureValuesAll.push(p)
    if (shelfMeasureOrder.length === 0) {
      // Classic MNV pattern: all measures live in `measureValues`.
      // Unfold each into a TypedMeasure below.
      for (const p of parsed) measureValuesFromMnv.push(p)
    } else {
      const shelfMeasureSet = new Set(shelfMeasureOrder)
      const parsedNonShelf = parsed.filter((p) => !shelfMeasureSet.has(p.field))
      if (hasVirtualMnvAuthoringContext && parsedNonShelf.length > 0) {
        // Tableau often combines a real helper measure on a shelf with a
        // virtual Measure Names / Measure Values context. In that shape the
        // explicit measureValues list is not a redundant mirror of shelf
        // measures; only entries absent from the shelf are the MNV universe
        // that must be unfolded for the pivot.
        for (const p of parsedNonShelf) measureValuesFromMnv.push(p)
      } else {
        // Measures also appear on shelves — only accept when measureValues
        // is exactly a mirror of shelfMeasureOrder. This preserves the
        // previous safe-path semantics for non-MNV worksheets that
        // duplicate the measure list in `measureValues` for presentation
        // reasons. Any other shape still rejects so we don't silently
        // change column ordering between compilers.
        const mvOrder = parsed.map((p) => p.field)
        if (mvOrder.length !== shelfMeasureOrder.length) {
          return unsupported('measure_values_list_not_yet_typed:order_mismatch')
        }
        for (let i = 0; i < mvOrder.length; i++) {
          if (mvOrder[i] !== shelfMeasureOrder[i]) {
            return unsupported(`measure_values_list_not_yet_typed:${mvOrder[i]}`)
          }
        }
      }
    }
  }
  if (
    parsedMeasureValuesAll.length === 0 &&
    measureValuesFromMnv.length === 0 &&
    shelfMeasureOrder.length === 0 &&
    hasVirtualMnvAuthoringContext &&
    measureNamesFilterValues.length > 0
  ) {
    for (const field of measureNamesFilterValues) {
      const entry: MeasureValueEntry = resolveMeasureNamesLabelMeasureEntry(field) ?? { field, aggregation: 'sum' }
      parsedMeasureValuesAll.push(entry)
      measureValuesFromMnv.push(entry)
    }
  }
  // `columnGroups` is multi-level header metadata. It adds no SQL
  // columns when every referenced field is already projected on the
  // shelves — the presentation layer nests the already-emitted
  // columns into labeled groups. Any group naming an unprojected
  // field would require the legacy Measure Names / Values unfolding
  // (generating extra columns per group entry), which still rejects
  // until that lowering lands.
  if (Array.isArray(crosstab.columnGroups) && crosstab.columnGroups.length > 0) {
    const projectedFieldNames = new Set<string>()
    const collectProjectedFieldNames = (entries: unknown): void => {
      if (!Array.isArray(entries)) return
      for (const e of entries) {
        if (isShelfFieldRef(e)) projectedFieldNames.add((e as ShelfField).field_name)
      }
    }
    collectProjectedFieldNames(crosstab.rows)
    collectProjectedFieldNames(crosstab.columns)
    for (const group of crosstab.columnGroups) {
      if (!group || typeof group !== 'object') {
        return unsupported('column_groups_shape_invalid')
      }
      const fields = (group as any).fields
      if (!Array.isArray(fields)) {
        return unsupported('column_groups_shape_invalid')
      }
      for (const f of fields) {
        // A column-group entry may be either a full shelf-field ref or
        // a bare field-name string (legacy compact shape); either is
        // presentation-only as long as the named field is already
        // projected. Unrecognized shapes still reject so we don't
        // silently accept new group metadata we can't prove is
        // presentation-only.
        let fieldName: string | null = null
        if (isShelfFieldRef(f)) {
          fieldName = (f as ShelfField).field_name
        } else if (typeof f === 'string' && f.length > 0) {
          fieldName = f
        }
        if (!fieldName) {
          return unsupported('column_groups_not_yet_typed')
        }
        if (!projectedFieldNames.has(fieldName)) {
          return unsupported(`column_groups_not_yet_typed:${fieldName}`)
        }
      }
    }
  }
  const authoredFilters: FilterSpec[] = Array.isArray(crosstab.filters) ? crosstab.filters : []
  const loweredFilters: TypedFilter[] = []
  for (const filter of authoredFilters) {
    const field = String((filter as any)?.field_name ?? (filter as any)?.field ?? '').trim()
    if (isVirtualMnvField(field)) continue
    const lowered = tryLowerAuthoredFilter(filter)
    if (!lowered.ok) return unsupported(lowered.reason)
    for (const f of lowered.filters) loweredFilters.push(f)
  }
  const totalsOutcome = tryLowerAuthoredTotals(crosstab.totals)
  if (!totalsOutcome.ok) return unsupported(totalsOutcome.reason)
  const loweredTotals = totalsOutcome.totals
  // PR4 Workstream D: tooltip-token surface. Populated while walking
  // `marks.tooltip` entries below; surfaces on
  // `TypedWorksheetSpec.presentation.tooltipTokens` so downstream
  // consumers (and the Rust compiler) see the exact field list the UI
  // intends to render in tooltips without re-scanning the authoring
  // spec. Every entry here is guaranteed by the lowering to already
  // appear in the spec's projection — the tooltip slot is
  // presentation-only today.
  //
  // Tokens are de-duplicated by field name while preserving first-seen
  // order. Real tooltip planning (see
  // `docs/compiler-rewrite/presentation-gap-ownership.md §3`) cares
  // about the distinct set, not the multiplicity: rendering a tooltip
  // twice for the same field is a UI anti-pattern the compiler
  // shouldn't propagate. Deduping here also makes the surface stable
  // against upstream callers that happen to repeat a token (e.g.
  // `[Region, Region]` merging from two authored tooltip sources).
  const tooltipTokenFields: string[] = []
  const seenTooltipTokens = new Set<string>()
  // Pending tokens extracted from `view.tooltip.template`. Validated
  // against the final projection below (after shelves + MNV unfolding)
  // so "token references already-projected field" stays a single,
  // consistent rule across marks.tooltip and view.tooltip templates.
  const pendingViewTooltipTemplateTokens: string[] = []
  const marks = crosstab.marks
  if (marks) {
    // Marks color/size/label each project additional SQL columns for
    // the legacy query builder so they influence query shape. Empty
    // arrays / empty objects are no-ops and safe to accept. Any other
    // truthy shape rejects until the lowering can project those extra
    // measures.
    const isEmptyMarkSlot = (value: unknown): boolean => {
      if (value == null) return true
      if (Array.isArray(value)) return value.length === 0
      if (typeof value === 'object') return Object.keys(value as object).length === 0
      return false
    }
    // A marks slot (color/size/label) that names a field already
    // declared as a dimension on the shelves is presentation-only: the
    // SQL projects that column regardless, so rendering the mark on top
    // doesn't require extra columns. This captures the common case of
    // "color bars by Region" where Region is already on a shelf.
    // Slots referencing an unprojected field still reject — real
    // lowering would need to add an extra dimension to the projection.
    const dimensionFieldNames = new Set<string>()
    const measureFieldNamesForMarks = new Set<string>()
    const addProjectedMarkFieldName = (target: Set<string>, raw: unknown): void => {
      const value = String(raw ?? '').trim()
      if (!value) return
      for (const key of [
        value,
        normalizeTableauFieldKey(value),
        normalizeTableauFieldKeyStrict(value),
      ]) {
        if (key) target.add(key)
      }
    }
    const collectFieldNames = (entries: unknown): void => {
      if (!Array.isArray(entries)) return
      for (const e of entries) {
        if (!isShelfFieldRef(e)) continue
        const s = e as ShelfField
        if (s.role === 'dimension') {
          addProjectedMarkFieldName(dimensionFieldNames, s.field_name)
          addProjectedMarkFieldName(dimensionFieldNames, s.display_name)
        }
        if (s.role === 'measure') {
          addProjectedMarkFieldName(measureFieldNamesForMarks, s.field_name)
          addProjectedMarkFieldName(measureFieldNamesForMarks, s.display_name)
        }
      }
    }
    collectFieldNames(crosstab.rows)
    collectFieldNames(crosstab.columns)
    const promoteMarkSlotProjection = (entry: unknown): boolean => {
      const field = normalizeVizPillToShelfField(entry)
      if (!field || isVirtualMnvField(field.field_name)) return Boolean(field)
      const target = field.role === 'measure' ? crosstab.columns : crosstab.rows
      if (!Array.isArray(target)) return false
      const projectedSet = field.role === 'measure' ? measureFieldNamesForMarks : dimensionFieldNames
      const isAlreadyProjected = [
        field.field_name,
        field.display_name,
        normalizeTableauFieldKey(field.field_name),
        normalizeTableauFieldKeyStrict(field.field_name),
      ].some((key) => Boolean(key && projectedSet.has(String(key))))
      if (!isAlreadyProjected) {
        target.push(field)
        addProjectedMarkFieldName(projectedSet, field.field_name)
        addProjectedMarkFieldName(projectedSet, field.display_name)
      }
      return true
    }
    const marksSlotIsProjectedField = (slot: unknown): boolean => {
      const entries = Array.isArray(slot) ? slot : [slot]
      if (entries.length === 0) return true
      const projected = (target: Set<string>, raw: unknown): boolean => {
        const value = String(raw ?? '').trim()
        if (!value) return false
        return [
          value,
          normalizeTableauFieldKey(value),
          normalizeTableauFieldKeyStrict(value),
        ].some((key) => Boolean(key && target.has(key)))
      }
      for (const entry of entries) {
        if (typeof entry === 'string' && entry.trim()) {
          if (isTableauParameterFieldToken(entry)) continue
          if (isVirtualMnvField(entry)) continue
          if (projected(dimensionFieldNames, entry)) continue
          if (projected(measureFieldNamesForMarks, entry)) continue
          if (promoteMarkSlotProjection(entry)) continue
          return false
        }
        if (entry && typeof entry === 'object') {
          const rawFieldName = firstNonEmptyString(
            (entry as any).field_name,
            (entry as any).field,
            (entry as any).name,
            (entry as any).column_name,
            (entry as any).column,
          )
          if (isTableauParameterFieldToken(rawFieldName)) continue
        }
        if (!isShelfFieldRef(entry)) {
          if (promoteMarkSlotProjection(entry)) continue
          return false
        }
        const s = entry as ShelfField
        if (isVirtualMnvField(s.field_name)) continue
        if (s.role === 'dimension' && projected(dimensionFieldNames, s.field_name)) continue
        if (s.role === 'measure' && projected(measureFieldNamesForMarks, s.field_name)) continue
        if (projected(dimensionFieldNames, s.display_name)) continue
        if (projected(measureFieldNamesForMarks, s.display_name)) continue
        if (promoteMarkSlotProjection(entry)) continue
        return false
      }
      return true
    }
    if (!isEmptyMarkSlot(marks.color) && !marksSlotIsProjectedField(marks.color)) {
      return unsupported('marks_color_not_yet_typed')
    }
    if (!isEmptyMarkSlot(marks.size) && !marksSlotIsProjectedField(marks.size)) {
      return unsupported('marks_size_not_yet_typed')
    }
    if (!isEmptyMarkSlot(marks.label) && !marksSlotIsProjectedField(marks.label)) {
      return unsupported('marks_label_not_yet_typed')
    }
    // `marks.tooltip` is an array of tooltip tokens. Each token that
    // references an already-projected field is presentation-only — the
    // tooltip surfaces a value SQL already emits. Any token naming an
    // unprojected field needs real tooltip-planning lowering and
    // rejects until then.
    if (Array.isArray(marks.tooltip) && marks.tooltip.length > 0) {
      for (const token of marks.tooltip) {
        const tokenFieldName =
          typeof token === 'string' && token.length > 0
            ? token
            : isShelfFieldRef(token)
            ? (token as ShelfField).field_name
            : null
        if (tokenFieldName && !seenTooltipTokens.has(tokenFieldName)) {
          seenTooltipTokens.add(tokenFieldName)
          tooltipTokenFields.push(tokenFieldName)
        }
        // Accept both full ShelfField refs and bare field-name strings
        // (legacy compact shape). Any token naming an already-projected
        // dim/measure is presentation-only — the tooltip surfaces data
        // the SQL already emits. Unprojected or unrecognized shapes
        // still reject so we never silently project extra columns.
        if (typeof token === 'string' && token.length > 0) {
          if (dimensionFieldNames.has(token)) continue
          if (measureFieldNamesForMarks.has(token)) continue
          return unsupported('marks_tooltip_not_yet_typed')
        }
        if (!isShelfFieldRef(token)) {
          return unsupported('marks_tooltip_not_yet_typed')
        }
        const t = token as ShelfField
        if (t.role === 'dimension' && dimensionFieldNames.has(t.field_name)) continue
        if (t.role === 'measure' && measureFieldNamesForMarks.has(t.field_name)) continue
        return unsupported('marks_tooltip_not_yet_typed')
      }
    }
  }

  // `view.tooltip` drives tooltip-token projection in the legacy builder
  // (adds extra SQL columns for each tooltip token). The typed compiler
  // doesn't emit those columns yet, so any tooltip config that could
  // generate tokens still rejects. The one safe shape is an explicit
  // `{ enabled: false }` — tooltips are off, so there are no tokens to
  // project, and the rest of the SQL is unaffected.
  const viewTooltip = worksheetSpec.view?.tooltip as
    | {
        enabled?: unknown
        mode?: unknown
        template?: unknown
        template_html?: unknown
        tokens?: unknown
      }
    | null
    | undefined
  if (viewTooltip) {
    // The legacy builder only adds extra SELECT columns for a tooltip
    // when the config carries a *non-empty* template string under mode
    // `template` or `rich` (see `extractTooltipTokensFromTemplate` in
    // `worksheetQueryBuilder.ts`). Every other recognized shape is a
    // pure presentation concern and contributes no SQL, so the typed
    // compiler can safely mirror the no-op path. Unknown/enabled-false
    // still short-circuits out.
    // `view.tooltip` carries a pile of presentation-only metadata
    // (font, responsiveness, color, command-buttons, selection flags,
    // etc.). None of these affect the emitted SQL — they only influence
    // UI chrome. Rather than maintain a fragile explicit allowlist,
    // carve out the shape-keys that *could* influence SQL and accept
    // every other key as presentation metadata. The remaining gate
    // below (`tokens`, `mode`, `template`, `template_html`) is what
    // actually determines whether extra SQL columns would be projected.
    const sqlRelevantKeys = new Set(['enabled', 'mode', 'template', 'template_html', 'tokens'])
    for (const key of Object.keys(viewTooltip)) {
      if (sqlRelevantKeys.has(key)) continue
      // Everything else is pure UI metadata — no SQL impact. Accept
      // without gating so production workbooks don't hit spurious
      // `view_tooltip_not_yet_typed` rejections on presentation-only
      // shape drift.
    }
    const enabled = viewTooltip.enabled
    if (enabled !== undefined && enabled !== true && enabled !== false) {
      return unsupported('view_tooltip_not_yet_typed')
    }
    const disabled = enabled === false
    // `tokens` (if present at all) is a legacy presentation bag whose
    // SQL impact comes only from the *template* path. We accept empty
    // arrays as pure presentation; any populated tokens array still
    // rejects until token projection lowering lands.
    if (viewTooltip.tokens !== undefined) {
      if (!Array.isArray(viewTooltip.tokens)) {
        return unsupported('view_tooltip_not_yet_typed')
      }
      if (!disabled && viewTooltip.tokens.length > 0) {
        return unsupported('view_tooltip_not_yet_typed')
      }
    }
    const modeRaw = viewTooltip.mode
    const mode =
      typeof modeRaw === 'string' ? modeRaw.trim().toLowerCase() : modeRaw === undefined ? '' : null
    if (mode === null) {
      return unsupported('view_tooltip_not_yet_typed')
    }
    const recognizedModes = new Set(['', 'auto', 'fields', 'template', 'rich'])
    if (!recognizedModes.has(mode)) {
      return unsupported('view_tooltip_not_yet_typed')
    }
    // Templates only produce SQL when they are non-empty AND tooltips
    // are enabled. Empty-string templates are legacy-UI defaults and
    // contribute nothing to the query — safe to accept.
    const template = viewTooltip.template
    const templateHtml = viewTooltip.template_html
    const templateString = typeof template === 'string' ? template : ''
    const templateHtmlString = typeof templateHtml === 'string' ? templateHtml : ''
    const templateSource = mode === 'rich' ? templateHtmlString : templateString
    if (template !== undefined && typeof template !== 'string') {
      return unsupported('view_tooltip_not_yet_typed')
    }
    if (templateHtml !== undefined && typeof templateHtml !== 'string') {
      return unsupported('view_tooltip_not_yet_typed')
    }
    // Non-empty tooltip templates reference fields via `{{token}}` /
    // legacy `<token>` placeholders. The legacy query builder projects
    // any referenced field as an extra SQL column so the token can
    // render — a behavior the Rust compiler does not yet mirror.
    //
    // Rather than re-implement full tooltip-token projection here
    // (which would require per-token aggregation parsing + alias
    // resolution), the typed lowering treats the tooltip template as
    // presentation metadata: extract token names into
    // `pendingViewTooltipTemplateTokens`, and validate them below
    // against the final projection. Tokens that already resolve to a
    // projected dim / measure / alias (or a virtual MNV pill) are
    // harmless — the tooltip uses already-emitted data. Any token
    // referencing an unprojected field still rejects the typed spec
    // so we never silently drop tooltip-driven columns.
    if (!disabled && (mode === 'template' || mode === 'rich') && templateSource.length > 0) {
      const extracted: string[] = []
      const htmlTagBlacklist = new Set<string>([
        'a', 'b', 'br', 'code', 'div', 'em', 'font', 'g', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'i', 'img', 'li', 'line', 'ol', 'p', 'path', 'polygon', 'polyline', 'pre', 'rect',
        'script', 'small', 'span', 'strong', 'style', 'sub', 'sup', 'svg', 'table', 'tbody',
        'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul', 'circle',
      ])
      for (const m of templateSource.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
        const tok = String(m?.[1] ?? '').trim()
        if (tok) extracted.push(tok)
      }
      for (const m of templateSource.matchAll(/<([^<>]+)>/g)) {
        const tok = String(m?.[1] ?? '').trim()
        if (!tok) continue
        const lower = tok.toLowerCase()
        if (lower.startsWith('/')) continue
        const tag = lower.split(/\s+/)[0]?.replace(/\/$/, '') || ''
        if (tag && htmlTagBlacklist.has(tag)) continue
        extracted.push(tok)
      }
      for (const raw of extracted) {
        // Token may be a bare field name (`Field Supervisor`), a virtual
        // MNV placeholder (`Measure Names`, `Multiple Values`), or a
        // token with aggregation / function wrapping (`SUM(x)`,
        // `ATTR([Field])`). We only surface bare, non-virtual tokens;
        // wrapped tokens already derive from SQL the compiler emits.
        const stripped = raw.replace(/^\s*\[?/, '').replace(/\]?\s*$/, '').trim()
        if (!stripped) continue
        if (isVirtualMnvField(stripped)) continue
        // Skip tokens that contain function calls or obviously
        // non-fieldlike punctuation — those are rendered from
        // already-projected measure columns or computed inline. The
        // projection check below still catches any bare token that
        // doesn't resolve.
        if (/[()]/.test(stripped)) continue
        pendingViewTooltipTemplateTokens.push(stripped)
      }
    }
  }
  // `view.totals` is a presentation-only mirror of the totals config.
  // The legacy query builder never reads `view.totals`; the real SQL
  // impact comes from `crosstab.totals`, which is lowered above. Any
  // shape under `view.totals` therefore contributes zero columns to
  // the projection and is safe to accept as presentation metadata.
  // (Parity guarded below by the fact that `crosstab.totals` is the
  // sole source of totals SQL; if that assumption ever breaks, the
  // adjacent classification in
  // `docs/compiler-rewrite/post-compiler-sql-rewrite-classification.md`
  // requires an explicit category change rather than a silent re-add.)
  // `action_graph` is the dashboard-action metadata attached to the
  // worksheet. An empty shell (`{}`, `{ actions: [] }`, `{ nodes: [] }`)
  // means no dashboard actions target this worksheet, so there is no
  // extra filter / parameter / highlight to push into SQL. Any non-empty
  // collection of actions/nodes means Rust would need to honor them to
  // produce matching rows, which the typed compiler does not yet model.
  const actionGraph = (worksheetSpec as any).action_graph as
    | { actions?: unknown; nodes?: unknown; edges?: unknown; version?: unknown }
    | null
    | undefined
  if (actionGraph && typeof actionGraph === 'object') {
    // `action_graph.actions[]` is declarative metadata describing
    // dashboard interactions (filter / select / highlight / url).
    // Nothing in this shape becomes SQL at *compile* time — the real
    // filter effect is delivered by `resolvedActionState` /
    // `runtimeFilters` when the dashboard action fires (PR3 wired the
    // runtime-filter lowering). The compile-time gate only needs to
    // ensure each declarative entry carries a recognized action type
    // we know is runtime-dispatched; otherwise we could silently
    // accept shapes with inline filter criteria the typed compiler
    // can't express.
    const actions = Array.isArray(actionGraph.actions) ? actionGraph.actions : []
    const nodes = Array.isArray(actionGraph.nodes) ? actionGraph.nodes : []
    const edges = Array.isArray(actionGraph.edges) ? actionGraph.edges : []
    // `nodes`/`edges` are graph topology metadata — no SQL effect. Any
    // unknown top-level key that isn't `actions`/`nodes`/`edges`/
    // `version` still rejects so schema drift stays visible.
    const knownKeys = new Set(['actions', 'nodes', 'edges', 'version'])
    for (const k of Object.keys(actionGraph)) {
      if (!knownKeys.has(k)) return unsupported(`action_graph_not_yet_typed:unknown_key:${k}`)
    }
    // Accepted action types are the runtime-dispatched ones whose SQL
    // effect arrives via `resolvedActionState` (not the action graph
    // itself). `filter` / `select` / `highlight` / `url` all match.
    // Any other type (e.g. custom `sql_override`) still rejects.
    const acceptedTypes = new Set(['filter', 'select', 'highlight', 'url', 'navigate'])
    for (const action of actions) {
      if (!action || typeof action !== 'object') {
        return unsupported('action_graph_not_yet_typed:shape_invalid')
      }
      const type = String((action as any).type ?? '').trim().toLowerCase()
      if (!type) return unsupported('action_graph_not_yet_typed:missing_type')
      if (!acceptedTypes.has(type)) {
        return unsupported(`action_graph_not_yet_typed:action_type:${type}`)
      }
    }
    // We don't inspect `nodes` / `edges` values — they're pure graph
    // topology with no SQL effect. Their mere presence is fine.
    void nodes
    void edges
  }

  // Parameters are consumed by Rust via `boundParameters` on the
  // compile input (PR3 merger in `worksheetCompilerRustBackend.ts`);
  // the typed spec only needs to declare them when a `param_ref`
  // appears inside a calc/filter/LOD. Every supported surface in this
  // gate rejects non-scalar filter values and non-shelf-field
  // projections, so authored parameters cannot be referenced by
  // anything we currently lower — passing them through is safe.
  // `measureValuesOverride` is the request-time Measure Values list
  // that the legacy builder unfolds into extra projected columns. It
  // is request-augmentation input (see
  // `docs/compiler-rewrite/presentation-gap-ownership.md` §2): it
  // selects from and reorders the worksheet's effective projected
  // measure universe but cannot introduce a brand-new measure that
  // the authoring spec did not already request.
  //
  // The effective projected-measure universe is the union of:
  //   - measure pills on `crosstab.rows` / `crosstab.columns`, and
  //   - the classic Measure Names / Measure Values list in
  //     `crosstab.measureValues` (already parsed into
  //     `measureValuesFromMnv` above).
  //
  // An empty / missing override has no SQL effect. A non-empty
  // override whose entries all resolve into that universe is
  // accepted; when the override drives the MNV projection we
  // additionally reorder / subset `measureValuesFromMnv` so the
  // downstream unfold loop emits `typedSpec.measures` in override
  // order. Any entry that does not resolve into the universe still
  // rejects with a stable per-field reason — introducing a new
  // measure requires a real Measure Values lowering widen on the
  // Rust side.
  const mvOverride = inputs.measureValuesOverride
  if (mvOverride != null) {
    if (!Array.isArray(mvOverride)) {
      return unsupported('measure_values_override_shape_invalid')
    }
    if (mvOverride.length > 0) {
      const shelfMeasureOrderLocal: string[] = []
      const collectShelfMeasureOrder = (entries: unknown): void => {
        if (!Array.isArray(entries)) return
        for (const e of entries) {
          if (isShelfFieldRef(e) && (e as ShelfField).role === 'measure') {
            shelfMeasureOrderLocal.push((e as ShelfField).field_name)
          }
        }
      }
      collectShelfMeasureOrder(crosstab.rows)
      collectShelfMeasureOrder(crosstab.columns)

      // Minimal caption <-> physical resolver derived from the
      // workbook field dictionary. Overrides and `measureValues` are
      // both authored either as physical column names or as the
      // Tableau Desktop caption; we have to match in either
      // direction so `Cuts / Day` (caption) resolves to the same
      // universe entry as its physical column name.
      const captionForField = new Map<string, string>()
      const fieldForCaption = new Map<string, string>()
      const extraLabelsForField = new Map<string, string[]>()
      const rememberFieldLabel = (physical: string, label: string): void => {
        const key = String(physical ?? '').trim()
        const value = String(label ?? '').trim()
        if (!key || !value) return
        const arr = extraLabelsForField.get(key) ?? []
        if (!arr.includes(value)) arr.push(value)
        extraLabelsForField.set(key, arr)
      }
      if (inputs.workbookIr && typeof inputs.workbookIr === 'object') {
        const fd = (inputs.workbookIr as { field_dictionary?: unknown }).field_dictionary
        if (fd && typeof fd === 'object') {
          const cols = (fd as { columns?: unknown }).columns
          if (Array.isArray(cols)) {
            for (const c of cols) {
              if (!c || typeof c !== 'object') continue
              const nameRaw = (c as { name?: unknown }).name
              const captionRaw = (c as { caption?: unknown }).caption
              if (typeof nameRaw !== 'string') continue
              const physical = nameRaw.trim().replace(/^\[/, '').replace(/\]$/, '').trim()
              if (!physical) continue
              if (typeof captionRaw === 'string' && captionRaw.trim()) {
                captionForField.set(physical, captionRaw)
                rememberFieldLabel(physical, captionRaw)
                if (!fieldForCaption.has(captionRaw)) fieldForCaption.set(captionRaw, physical)
                const trimmed = captionRaw.trim()
                if (trimmed && trimmed !== captionRaw && !fieldForCaption.has(trimmed)) {
                  fieldForCaption.set(trimmed, physical)
                }
              }
              const aliases = (c as { aliases?: unknown }).aliases
              if (Array.isArray(aliases)) {
                for (const alias of aliases) {
                  if (!alias || typeof alias !== 'object') continue
                  const keyRaw = (alias as { key?: unknown }).key
                  const valueRaw = (alias as { value?: unknown }).value
                  for (const aliasKey of fieldManifestIdentityLookupKeys(keyRaw)) {
                    if (!fieldForCaption.has(aliasKey)) fieldForCaption.set(aliasKey, physical)
                  }
                  if (typeof valueRaw === 'string' && valueRaw.trim()) {
                    rememberFieldLabel(physical, valueRaw)
                    if (!fieldForCaption.has(valueRaw)) fieldForCaption.set(valueRaw, physical)
                    const trimmed = valueRaw.trim()
                    if (trimmed && trimmed !== valueRaw && !fieldForCaption.has(trimmed)) {
                      fieldForCaption.set(trimmed, physical)
                    }
                  }
                }
              }
            }
          }
        }
      }
      const keysForField = (raw: string): string[] => {
        const out: string[] = []
        const seen = new Set<string>()
        const add = (value: unknown): void => {
          const v = String(value ?? '').trim()
          if (!v || seen.has(v)) return
          seen.add(v)
          out.push(v)
          const captionOfPhysical = captionForField.get(v)
          if (captionOfPhysical && !seen.has(captionOfPhysical)) {
            seen.add(captionOfPhysical)
            out.push(captionOfPhysical)
          }
          const extraLabels = extraLabelsForField.get(v) ?? []
          for (const label of extraLabels) {
            if (!seen.has(label)) {
              seen.add(label)
              out.push(label)
            }
          }
          const physicalOfCaption = fieldForCaption.get(v)
          if (physicalOfCaption && !seen.has(physicalOfCaption)) {
            seen.add(physicalOfCaption)
            out.push(physicalOfCaption)
          }
        }
        add(raw)
        for (const key of fieldManifestIdentityLookupKeys(raw)) add(key)
        return out
      }

      // Build the universe from every authoring surface that
      // declares a projectable measure for this worksheet:
      //   - measure pills on the row/column shelves,
      //   - the classic Measure Names / Measure Values list
      //     (`crosstab.measureValues`), even in the "mirror" case
      //     where `measureValuesFromMnv` stays empty, and
      //   - the `Measure Names` pill's `sort.custom_order`, which is
      //     Tableau's authoritative declaration of the discrete
      //     label domain the pill projects.
      // Each entry is indexed under its as-authored form AND its
      // caption/physical partner so overrides authored in either form
      // still validate.
      const universeKeys = new Set<string>()
      for (const m of shelfMeasureOrderLocal) {
        for (const k of keysForField(m)) universeKeys.add(k)
      }
      for (const mv of measureValuesFromMnv) {
        for (const k of keysForField(mv.field)) universeKeys.add(k)
      }
      for (const mv of parsedMeasureValuesAll) {
        for (const k of keysForField(mv.field)) universeKeys.add(k)
      }
      for (const caption of measureNamesCustomOrder) {
        for (const k of keysForField(caption)) universeKeys.add(k)
      }
      const overrideFields: string[] = []
      for (const entry of mvOverride) {
        const field =
          typeof entry === 'string'
            ? entry
            : typeof (entry as any)?.field_name === 'string'
            ? (entry as any).field_name
            : typeof (entry as any)?.field === 'string'
            ? (entry as any).field
            : ''
        if (!field) {
          return unsupported('measure_values_override_entry_shape_invalid')
        }
        overrideFields.push(field)
      }
      for (const field of overrideFields) {
        const keys = keysForField(field)
        const known = keys.some((k) => universeKeys.has(k))
        if (!known) {
          return unsupported(`measure_values_override_not_yet_typed:${field}`)
        }
      }

      // Order-driving: when the override references MNV entries,
      // reorder / subset `measureValuesFromMnv` so the downstream
      // unfold loop emits `typedSpec.measures` in override order
      // without any additional special-casing. We only mutate
      // `measureValuesFromMnv` when at least one override entry maps
      // onto it; shelf-only worksheets are left untouched, preserving
      // the presentation-only contract for those cases (the
      // override can reorder columns post-query without re-emitting
      // SQL, and the shelf pills remain the authoritative source of
      // projected measures).
      if (measureValuesFromMnv.length > 0) {
        const mvByKey = new Map<string, typeof measureValuesFromMnv[number]>()
        for (const mv of measureValuesFromMnv) {
          for (const k of keysForField(mv.field)) {
            if (!mvByKey.has(k)) mvByKey.set(k, mv)
          }
        }
        const reordered: typeof measureValuesFromMnv = []
        const seen = new Set<typeof measureValuesFromMnv[number]>()
        for (const field of overrideFields) {
          for (const k of keysForField(field)) {
            const hit = mvByKey.get(k)
            if (hit && !seen.has(hit)) {
              seen.add(hit)
              reordered.push(hit)
              break
            }
          }
        }
        if (reordered.length > 0) {
          measureValuesFromMnv.length = 0
          for (const mv of reordered) measureValuesFromMnv.push(mv)
        }
      }
    }
  }
  const runtimeFilters = Array.isArray(inputs.runtimeFilters) ? inputs.runtimeFilters : []
  const loweredRuntimeFilters: TypedFilter[] = []
  for (const rf of runtimeFilters) {
    const field = String((rf as any)?.display_name ?? (rf as any)?.field_name ?? (rf as any)?.field ?? '').trim()
    if (isVirtualMnvField(field)) continue
    const lowered = tryLowerRuntimeFilter(rf)
    if (!lowered.ok) return unsupported(lowered.reason)
    for (const f of lowered.filters) loweredRuntimeFilters.push(f)
  }

  // Safety: `detectWorksheetHasTableCalcs` only returns true when it
  // finds a calc field attached to the named worksheet *inside the
  // provided workbookIr*. With no IR we cannot prove absence, so
  // populating a typed spec would silently drop any calcs the Rust
  // compiler can't yet handle. Reject explicitly so callers without IR
  // stay on `ts-legacy`; the diagnostic makes the missing wiring
  // visible in switch-readiness telemetry.
  const workbookIr = inputs.workbookIr
  const workbookIrPresent =
    workbookIr != null &&
    typeof workbookIr === 'object' &&
    Array.isArray((workbookIr as any).worksheets)
  if (!workbookIrPresent) {
    return unsupported('missing_workbook_ir_for_calc_detection')
  }
  const activeWorkbookWorksheet = (() => {
    const sheets = Array.isArray((workbookIr as any).worksheets)
      ? ((workbookIr as any).worksheets as any[])
      : []
    const wanted = String(worksheetName ?? '').trim().toLowerCase()
    if (!wanted) return null
    return sheets.find((sheet) => {
      if (!sheet || typeof sheet !== 'object') return false
      return [
        (sheet as any).worksheet_name,
        (sheet as any).internal_name,
        (sheet as any).name,
      ].some((candidate) => String(candidate ?? '').trim().toLowerCase() === wanted)
    }) ?? null
  })()
  // PR7 table-calc lowering slice 1. Previously a blanket reject of
  // `table_calcs_not_yet_typed:<labels>`. Now every triggering calc
  // field is classified against the minimal grammar in
  // `src/tableau/tableauCalcFormula.ts`. Classified kinds become
  // `TypedWindowMeasure` entries so the Rust compiler can emit the
  // corresponding window SQL; anything outside the grammar surfaces a
  // narrow per-field rejection reason instead of the coarse label list.
  //
  // Scope of this slice:
  //   - `INDEX()` → full typed lowering to `TypedWindowKind::INDEX`.
  //   - All other classifiable kinds (`LOOKUP(...,<int|FIRST|LAST>)`)
  //     are parsed correctly by the classifier but still rejected
  //     here with `table_calc_lookup_semantics_pending:<label>` until
  //     Tableau offset / partition semantics are calibrated against
  //     ground-truth outputs (tracked as the next grind slice). This
  //     keeps the typed path from emitting subtly-wrong PY SQL while
  //     the classifier infrastructure stays exercised in production.
  const tableCalcRecords = resolveWorksheetTableCalcFields(
    workbookIr,
    worksheetName || null,
    { worksheetSpecRaw: worksheetSpec },
  )
  // Dev-only dump guarded by a runtime feature flag. Reference `process`
  // through a `typeof` check so the browser bundle (where `process` is
  // undefined) can evaluate this branch without a `ReferenceError` and
  // fall through to the normal typed-spec lowering path.
  if (
    typeof process !== 'undefined' &&
    process?.env?.FG_DUMP_TCR === '1'
  ) {
    // eslint-disable-next-line no-console
    console.log('TCR:', JSON.stringify(tableCalcRecords.map((r) => ({ l: r.label, f: r.fieldName, fm: r.formula.slice(0,60) }))))
  }
  type PendingWindowMeasure = {
    label: string
    alias: string
    classified: ClassifiedCalcKind
    record: WorksheetTableCalcFieldRecord
    allowSyntheticBaseMeasure: boolean
  }
  const pendingWindowMeasures: PendingWindowMeasure[] = []
  type PendingTableCalcDimensionRecord = {
    label: string
    labelSafe: string
    fieldName: string
    alias: string
    parsed: DimensionCalcExprNode
  }
  const pendingTableCalcDimensionRecords: PendingTableCalcDimensionRecord[] = []
  // PR7 slice 3 producer-side: collect recognized composite patterns for
  // materialization after `orderBy` / `measures` are finalized. Each
  // entry expands into one `TypedWindowMeasure` (LAG/LEAD/FIRST) plus
  // one `TypedDerivedMeasure` (post-window arithmetic). Emitting
  // here — after `classifyCalcFormula` returns rejection — replaces the
  // pre-landing `_pending_post_window_arithmetic` stub.
  type PendingCompositePattern = {
    label: string
    labelSafe: string
    alias: string
    pattern: import('./tableauCalcFormula').RecognizedCompositePattern
    allowSyntheticBaseMeasure: boolean
  }
  const pendingComposites: PendingCompositePattern[] = []
  // PR7 table-calc-lookup-semantics (step 1 of calc-ref resolution):
  // index every calc field's formula up front. The composite-pattern
  // handlers below consult this index when a base resolves to a bare
  // `([Calculation_xxx])` reference — widening a previously-hard
  // `_bare_agg_unsupported` rejection into acceptance when the
  // referenced calc is itself a pure `<AGG>([phys])` alias. Non-alias
  // calc formulas (ratio-of-aggregates, FIXED LODs, arbitrary
  // expressions) still reject with explicit new reasons so the
  // rejection inventory surfaces them for the next widening.
  const calcFormulaIndex = buildCalcFormulaIndexFromWorkbookIr(workbookIr)
  mergeFieldManifestCalcFormulaIndex(calcFormulaIndex, inputs.fieldManifest)
  const workbookParameterLiteralByName: Map<string, string> = (() => {
    const out = new Map<string, string>()
    const wbParams = (workbookIr as any)?.parameters
    if (!Array.isArray(wbParams)) return out
    const addKey = (raw: unknown, literal: string): void => {
      const value = String(raw ?? '').trim()
      if (!value) return
      for (const key of [
        value,
        value.replace(/^\[/, '').replace(/\]$/, '').trim(),
        normalizeTableauFieldKey(value),
      ]) {
        if (key && !out.has(key)) out.set(key, literal)
      }
    }
    for (const param of wbParams) {
      if (!param || typeof param !== 'object') continue
      const rawValue = (param as any).current_value ?? (param as any).default_value ?? (param as any).attrs?.value
      if (rawValue === null || rawValue === undefined) continue
      const dt = String((param as any).datatype ?? (param as any).data_type ?? (param as any).param_type ?? '').trim().toLowerCase()
      const raw = String(rawValue).trim()
      let literal = ''
      if (dt === 'integer' || dt === 'int') {
        const n = Number(raw.replace(/[^-\d]/g, ''))
        if (Number.isFinite(n)) literal = String(Math.trunc(n))
      } else if (dt === 'real' || dt === 'float' || dt === 'double' || dt === 'quantitative') {
        const n = Number(raw.replace(/[^-\d.]/g, ''))
        if (Number.isFinite(n)) literal = String(n)
      } else if (/^".*"$/.test(raw) || /^'.*'$/.test(raw)) {
        literal = raw
      } else if (raw) {
        literal = JSON.stringify(raw)
      }
      if (!literal) continue
      addKey((param as any).name, literal)
      addKey((param as any).caption, literal)
      addKey((param as any).attrs?.name, literal)
    }
    return out
  })()
  const workbookParameterNameKeys: Set<string> = (() => {
    const out = new Set<string>()
    const wbParams = (workbookIr as any)?.parameters
    if (!Array.isArray(wbParams)) return out
    const addKey = (raw: unknown): void => {
      const value = String(raw ?? '').trim()
      if (!value) return
      for (const key of [
        value,
        value.replace(/^\[/, '').replace(/\]$/, '').trim(),
        normalizeTableauFieldKey(value),
      ]) {
        if (key) out.add(key)
      }
    }
    for (const param of wbParams) {
      if (!param || typeof param !== 'object') continue
      addKey((param as any).name)
      addKey((param as any).caption)
      addKey((param as any).attrs?.name)
    }
    return out
  })()
  const isWorkbookParameterFieldName = (raw: unknown): boolean => {
    if (isTableauParameterFieldToken(raw)) return true
    const value = String(raw ?? '').trim()
    if (!value) return false
    return workbookParameterNameKeys.has(value) ||
      workbookParameterNameKeys.has(value.replace(/^\[/, '').replace(/\]$/, '').trim()) ||
      workbookParameterNameKeys.has(normalizeTableauFieldKey(value))
  }
  const substituteWorkbookParameterLiterals = (formula: string): string =>
    String(formula ?? '').replace(/\[Parameters\]\.\[([^\]]+)\]/g, (match, nameRaw) => {
      const key = String(nameRaw ?? '').trim()
      return (
        workbookParameterLiteralByName.get(key) ??
        workbookParameterLiteralByName.get(normalizeTableauFieldKey(key)) ??
        match
      )
    })
  // PR7 table-calc-lookup-semantics: caption → physical index for
  // resolving authored measure pills that reference a field by its
  // caption (`Hair Cut Count`) rather than its physical name
  // (`haircutcount`). MNV unfolding keeps whatever string the author
  // wrote; the composite-pattern handlers normalize through this index
  // so `measures.find(m => m.field === 'haircutcount')` still matches
  // a measure authored as `field === 'Hair Cut Count'`.
  const fieldCaptionIndex = buildFieldCaptionIndexFromWorkbookIr(workbookIr)
  mergeFieldManifestCaptionIndex(fieldCaptionIndex, inputs.fieldManifest)
  const getCalcFormulaForField = (
    fieldRaw: string | null | undefined,
    captionRaw?: string | null,
  ): string => {
    const field = String(fieldRaw ?? '').trim()
    const caption = String(captionRaw ?? '').trim()
    const resolvedCaption = field ? String(fieldCaptionIndex.get(field) ?? '').trim() : ''
    return String(
      calcFormulaIndex.get(field) ??
      (caption ? calcFormulaIndex.get(caption) : undefined) ??
      (resolvedCaption ? calcFormulaIndex.get(resolvedCaption) : undefined) ??
      '',
    ).trim()
  }
  const getParameterResolvedCalcFormulaForField = (
    fieldRaw: string | null | undefined,
    captionRaw?: string | null,
  ): string => {
    const formula = getCalcFormulaForField(fieldRaw, captionRaw)
    return formula ? substituteWorkbookParameterLiterals(formula) : ''
  }
  const hasCalcFormulaForField = (
    fieldRaw: string | null | undefined,
    captionRaw?: string | null,
  ): boolean => getCalcFormulaForField(fieldRaw, captionRaw).length > 0
  const semanticProjectionFieldIndex = buildSemanticProjectionFieldIndex(inputs.semanticSpec)
  const semanticProjectionEntries = Array.isArray((inputs.semanticSpec as any)?.projection)
    ? ((inputs.semanticSpec as any).projection as any[])
    : []
  const semanticProjectionEntryForField = (raw: unknown): any | null => {
    const resolved = lookupSemanticProjectionField(semanticProjectionFieldIndex, raw)
    if (!resolved) return null
    const resolvedKey = normalizeTableauFieldKey(resolved) || resolved.toLowerCase()
    for (const entry of semanticProjectionEntries) {
      if (!entry || typeof entry !== 'object') continue
      const alias = String((entry as any)?.alias ?? '').trim()
      const aliasKey = normalizeTableauFieldKey(alias) || alias.toLowerCase()
      if (aliasKey && aliasKey === resolvedKey) return entry
    }
    return null
  }
  const fieldDictionaryColumnByKey: Map<string, any> = (() => {
    const out = new Map<string, any>()
    if (!workbookIr || typeof workbookIr !== 'object') return out
    const fd = (workbookIr as { field_dictionary?: unknown }).field_dictionary
    if (!fd || typeof fd !== 'object') return out
    const cols = (fd as { columns?: unknown }).columns
    if (!Array.isArray(cols)) return out
    const add = (raw: unknown, col: any): void => {
      const s = String(raw ?? '').trim()
      if (!s) return
      const clean = s.replace(/^\[/, '').replace(/\]$/, '').trim()
      for (const key of [s, clean, normalizeTableauFieldKey(s), normalizeTableauFieldKey(clean)]) {
        if (key && !out.has(key)) out.set(key, col)
      }
    }
    for (const col of cols) {
      if (!col || typeof col !== 'object') continue
      add((col as any).name, col)
      add((col as any).caption, col)
    }
    return out
  })()
  const fieldDictionaryColumnInstanceByKey: Map<string, any> = (() => {
    const out = new Map<string, any>()
    if (!workbookIr || typeof workbookIr !== 'object') return out
    const fd = (workbookIr as { field_dictionary?: unknown }).field_dictionary
    if (!fd || typeof fd !== 'object') return out
    const columnInstances = Array.isArray((fd as any).column_instances)
      ? (fd as any).column_instances as any[]
      : []
    const add = (raw: unknown, ci: any): void => {
      const s = String(raw ?? '').trim()
      if (!s) return
      const clean = s.replace(/^\[/, '').replace(/\]$/, '').trim()
      for (const key of [s, clean, normalizeTableauFieldKey(s), normalizeTableauFieldKey(clean)]) {
        if (key && !out.has(key)) out.set(key, ci)
      }
    }
    for (const ci of columnInstances) {
      if (!ci || typeof ci !== 'object') continue
      add((ci as any).name, ci)
      add((ci as any).caption, ci)
    }
    return out
  })()
  const directFieldDictionaryColumnForField = (raw: unknown): any | null => {
    const s = String(raw ?? '').trim()
    if (!s) return null
    const dequoted = s
      .replace(/^"(.*)"$/, '$1')
      .replace(/^'(.*)'$/, '$1')
      .trim()
    const qualified = dequoted.match(/\]\.\[([^\]]+)\]\s*$/)
    const cleaned = dequoted
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .trim()
    const unqualified = qualified ? qualified[1] : cleaned
    for (const key of [
      s,
      cleaned,
      unqualified,
      normalizeTableauFieldKey(s),
      normalizeTableauFieldKey(cleaned),
      normalizeTableauFieldKey(unqualified),
    ]) {
      if (!key) continue
      const hit = fieldDictionaryColumnByKey.get(key)
      if (hit) return hit
    }
    return null
  }
  const fieldDictionaryColumnInstanceForField = (raw: unknown): any | null => {
    const s = String(raw ?? '').trim()
    if (!s) return null
    const dequoted = s
      .replace(/^"(.*)"$/, '$1')
      .replace(/^'(.*)'$/, '$1')
      .trim()
    const qualified = dequoted.match(/\]\.\[([^\]]+)\]\s*$/)
    const cleaned = dequoted
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .trim()
    const unqualified = qualified ? qualified[1] : cleaned
    for (const key of [
      s,
      cleaned,
      unqualified,
      normalizeTableauFieldKey(s),
      normalizeTableauFieldKey(cleaned),
      normalizeTableauFieldKey(unqualified),
    ]) {
      if (!key) continue
      const hit = fieldDictionaryColumnInstanceByKey.get(key)
      if (hit) return hit
    }
    return null
  }
  const allFieldDictionaryColumnInstances = (): any[] => {
    if (!workbookIr || typeof workbookIr !== 'object') return []
    const fd = (workbookIr as { field_dictionary?: unknown }).field_dictionary
    if (!fd || typeof fd !== 'object') return []
    const columnInstances = (fd as { column_instances?: unknown }).column_instances
    return Array.isArray(columnInstances) ? (columnInstances as any[]) : []
  }
  const fieldDictionaryColumnForField = (raw: unknown): any | null => {
    const direct = directFieldDictionaryColumnForField(raw)
    if (direct) return direct
    const ci = fieldDictionaryColumnInstanceForField(raw)
    if (!ci || typeof ci !== 'object') return null
    return directFieldDictionaryColumnForField((ci as any).column)
  }
  const isMeasureLikeFieldDictionaryColumn = (col: any): boolean => {
    if (!col || typeof col !== 'object') return false
    const role = String((col as any)?.role ?? '').trim().toLowerCase()
    if (role === 'dimension') return false
    if (role === 'measure') return true
    const type = String((col as any)?.type ?? (col as any)?.['r#type'] ?? '').trim().toLowerCase()
    if (type === 'quantitative') return true
    const aggregation = String((col as any)?.aggregation ?? '').trim()
    return Boolean(aggregation)
  }
  function resolvePhysicalField(name: string): string {
    const authored = String(name ?? '').trim()
    const captionResolved = fieldCaptionIndex.get(authored) ?? authored
    const fdCol = fieldDictionaryColumnForField(authored)
    const fdName = String((fdCol as any)?.name ?? '').trim()
    const projected =
      lookupSemanticProjectionField(semanticProjectionFieldIndex, authored) ??
      lookupSemanticProjectionField(semanticProjectionFieldIndex, captionResolved) ??
      lookupSemanticProjectionField(semanticProjectionFieldIndex, fdName)
    if (projected) return projected
    const ci = fieldDictionaryColumnInstanceForField(authored)
    if (ci) {
      const fdCaption = String((fdCol as any)?.caption ?? '').trim()
      if (fdCaption) return fdCaption
    }
    return captionResolved
  }
  const inferSortHelperAggregation = (rawField: string): AggResolution | null => {
    const entry = semanticProjectionEntryForField(rawField)
    const tokens = [
      (entry as any)?.field_id,
      (entry as any)?.alias,
      rawField,
    ]
    for (const tokenRaw of tokens) {
      const token = String(tokenRaw ?? '').trim()
      if (!token) continue
      const match = token.match(/\[?\s*(sum|avg|average|min|max|median|countd|count_distinct|count)\s*:/i)
      if (!match) continue
      const aggKey = String(match[1] ?? '').trim().toLowerCase()
      const normalized = aggKey === 'average' ? 'avg' : aggKey
      const mapped = AGG_MAP[normalized]
      if (mapped) return mapped
    }
    if (entry) {
      const role = String((entry as any)?.role ?? '').trim().toLowerCase()
      const type = String((entry as any)?.type ?? '').trim().toLowerCase()
      if (role === 'dimension' || type === 'nominal' || type === 'ordinal') return null
      return { agg: 'SUM', distinct: false }
    }
    const fdCol = fieldDictionaryColumnForField(rawField)
    if (isMeasureLikeFieldDictionaryColumn(fdCol)) return { agg: 'SUM', distinct: false }
    return null
  }
  // Reverse index: physical column name → authored caption. Used to
  // decide whether a `crosstab.measureValues` entry authored as the
  // physical field name (`newcustomercount`) belongs to the MNV
  // pivot's `Measure Names` custom_order (always spelled in caption
  // form, e.g. `Newcustomercount`), and to surface the right column
  // header when it does NOT.
  const physicalToCaptionIndex: Map<string, string> = (() => {
    const out = new Map<string, string>()
    if (!workbookIr || typeof workbookIr !== 'object') return out
    const fd = (workbookIr as { field_dictionary?: unknown }).field_dictionary
    if (!fd || typeof fd !== 'object') return out
    const cols = (fd as { columns?: unknown }).columns
    if (!Array.isArray(cols)) return out
    for (const c of cols) {
      if (!c || typeof c !== 'object') continue
      const nameRaw = (c as { name?: unknown }).name
      const captionRaw = (c as { caption?: unknown }).caption
      if (typeof nameRaw !== 'string') continue
      const physical = nameRaw.trim().replace(/^\[/, '').replace(/\]$/, '').trim()
      if (!physical) continue
      if (typeof captionRaw === 'string' && captionRaw.trim()) {
        // Preserve authored caption verbatim (including trailing spaces);
        // Tableau Desktop renders captions byte-for-byte and ground-truth
        // TSV exports reflect that (e.g. "Hair Cut % Diff " has a
        // deliberate trailing space that must appear in pivot labels).
        const caption = captionRaw
        out.set(physical, caption)
        // Calculated fields are addressed in shelves by their caption
        // (`Hair Cut % Diff`) rather than the internal column name
        // (`Calculation_...`). Also index by the trimmed caption so
        // `resolveCaptionForField("Hair Cut % Diff")` recovers the
        // authored caption "Hair Cut % Diff " with the trailing space.
        const trimmedCaption = caption.trim()
        if (trimmedCaption && trimmedCaption !== caption && !out.has(trimmedCaption)) {
          out.set(trimmedCaption, caption)
        }
      }
    }
    return out
  })()
  function resolveCaptionForField(name: string): string {
    return physicalToCaptionIndex.get(name) ?? name
  }
  const normalizeTableCalcRecordKey = (value: string | null | undefined): string =>
    String(value ?? '')
      .trim()
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .replace(/\s+/g, ' ')
      .toLowerCase()
  const dequalifiedTableauToken = (value: unknown): string => {
    const raw = String(value ?? '').trim()
    if (!raw) return ''
    const bracketed = Array.from(raw.matchAll(/\[([^\]]+)\]/g))
      .map((match) => String(match[1] ?? '').trim())
      .filter(Boolean)
    if (bracketed.length > 0) return bracketed[bracketed.length - 1] ?? ''
    const knownToken = raw.match(/(?:^|\.)(pcto:[^.]+|sum:[^.]+|avg:[^.]+|average:[^.]+|min:[^.]+|max:[^.]+|median:[^.]+|count:[^.]+|countd:[^.]+|ctd:[^.]+|none:[^.]+)$/i)
    return String(knownToken?.[1] ?? raw).trim()
  }
  const fieldNameFromTableauInstanceToken = (value: unknown): string => {
    const token = dequalifiedTableauToken(value)
    if (!token) return ''
    const parts = token.split(':')
    const aggIdx = parts.findIndex((part) => {
      const p = String(part ?? '').trim().toLowerCase()
      return ['sum', 'avg', 'average', 'min', 'max', 'median', 'count', 'countd', 'ctd'].includes(p)
    })
    if (aggIdx >= 0 && aggIdx + 1 < parts.length) {
      const fieldPart = parts.slice(aggIdx + 1, Math.max(aggIdx + 2, parts.length - 1)).join(':').trim()
      if (fieldPart) return fieldPart
    }
    if (parts[0]?.toLowerCase() === 'none' && parts.length >= 2) {
      return parts.slice(1, Math.max(2, parts.length - 1)).join(':').trim()
    }
    return token
  }
  const persistedTableCalcFieldKeys = (value: unknown): Set<string> => {
    const out = new Set<string>()
    const add = (raw: unknown): void => {
      const key = normalizeTableCalcRecordKey(String(raw ?? ''))
      if (key) out.add(key)
    }
    add(value)
    add(dequalifiedTableauToken(value))
    add(fieldNameFromTableauInstanceToken(value))
    return out
  }
  const columnInstanceHasPersistedPctTotal = (ci: any): boolean => {
    if (!ci || typeof ci !== 'object') return false
    const tableCalcs = Array.isArray((ci as any).table_calcs) ? (ci as any).table_calcs : []
    return tableCalcs.some((tc: any) => {
      const type = String((tc as any)?.type ?? (tc as any)?.attrs?.type ?? '').trim().toLowerCase()
      return type === 'pcttotal'
    })
  }
  const columnInstancePersistedRankKind = (ci: any): TypedWindowMeasure['kind'] | null => {
    if (!ci || typeof ci !== 'object') return null
    const tableCalcs = Array.isArray((ci as any).table_calcs) ? (ci as any).table_calcs : []
    for (const tc of tableCalcs) {
      const type = String((tc as any)?.type ?? (tc as any)?.attrs?.type ?? '').trim().toLowerCase()
      if (type !== 'rank') continue
      const options = String((tc as any)?.['rank-options'] ?? (tc as any)?.attrs?.['rank-options'] ?? '').toLowerCase()
      if (options.includes('dense')) return { kind: 'RANK_DENSE' }
      if (options.includes('unique')) return { kind: 'RANK_UNIQUE' }
      return { kind: 'RANK' }
    }
    return null
  }
  const columnInstanceAggResolution = (ci: any): AggResolution | null => {
    const derivation = String((ci as any)?.derivation ?? (ci as any)?.attrs?.derivation ?? '').trim().toLowerCase()
    switch (derivation) {
      case 'sum':
        return { agg: 'SUM', distinct: false }
      case 'avg':
      case 'average':
        return { agg: 'AVG', distinct: false }
      case 'min':
        return { agg: 'MIN', distinct: false }
      case 'max':
        return { agg: 'MAX', distinct: false }
      case 'count':
        return { agg: 'COUNT', distinct: false }
      case 'countd':
      case 'count_distinct':
      case 'ctd':
        return { agg: 'COUNT', distinct: true }
      default:
        return null
    }
  }
  const columnInstanceBaseField = (ci: any): string | null => {
    const field = fieldNameFromTableauInstanceToken((ci as any)?.column)
    return field || null
  }
  const activeWorksheetFieldTokens = (): unknown[] => {
    const out: unknown[] = []
    const scanAxis = (axis: any): void => {
      if (!axis || typeof axis !== 'object') return
      if (Array.isArray((axis as any).fields)) out.push(...(axis as any).fields)
      if (typeof (axis as any).expr === 'string') out.push((axis as any).expr)
    }
    scanAxis((activeWorkbookWorksheet as any)?.cols)
    scanAxis((activeWorkbookWorksheet as any)?.rows)
    return out
  }
  const persistedPctTotalColumnInstanceForField = (
    fieldRaw: string | null | undefined,
  ): any | null => {
    const wantedKeys = persistedTableCalcFieldKeys(fieldRaw)
    const caption = fieldRaw ? fieldCaptionIndex.get(fieldRaw) : null
    for (const raw of [caption, fieldRaw ? resolveCaptionForField(fieldRaw) : null, fieldRaw ? resolvePhysicalField(fieldRaw) : null]) {
      for (const key of persistedTableCalcFieldKeys(raw)) wantedKeys.add(key)
    }
    if (wantedKeys.size === 0) return null
    const matchesField = (ci: any): boolean => {
      if (!columnInstanceHasPersistedPctTotal(ci)) return false
      for (const raw of [(ci as any)?.column, (ci as any)?.name, (ci as any)?.caption]) {
        const keys = persistedTableCalcFieldKeys(raw)
        for (const key of keys) {
          if (wantedKeys.has(key)) return true
        }
      }
      return false
    }
    for (const token of activeWorksheetFieldTokens()) {
      const ci = fieldDictionaryColumnInstanceForField(token)
      if (matchesField(ci)) return ci
    }
    for (const ci of allFieldDictionaryColumnInstances()) {
      if (matchesField(ci)) return ci
    }
    return null
  }
  const persistedRankColumnInstanceForField = (
    fieldRaw: string | null | undefined,
  ): any | null => {
    const wantedKeys = persistedTableCalcFieldKeys(fieldRaw)
    const caption = fieldRaw ? fieldCaptionIndex.get(fieldRaw) : null
    for (const raw of [caption, fieldRaw ? resolveCaptionForField(fieldRaw) : null, fieldRaw ? resolvePhysicalField(fieldRaw) : null]) {
      for (const key of persistedTableCalcFieldKeys(raw)) wantedKeys.add(key)
    }
    if (wantedKeys.size === 0) return null
    const matchesField = (ci: any): boolean => {
      if (!columnInstancePersistedRankKind(ci)) return false
      for (const raw of [(ci as any)?.column, (ci as any)?.name, (ci as any)?.caption]) {
        const keys = persistedTableCalcFieldKeys(raw)
        for (const key of keys) {
          if (wantedKeys.has(key)) return true
        }
      }
      return false
    }
    for (const token of activeWorksheetFieldTokens()) {
      const ci = fieldDictionaryColumnInstanceForField(token)
      if (matchesField(ci)) return ci
    }
    for (const ci of allFieldDictionaryColumnInstances()) {
      if (matchesField(ci)) return ci
    }
    return null
  }
  // Some live worksheets surface table-calcs only through `measureValues` /
  // `Measure Names` metadata rather than the worksheet-reference scan. When
  // the calc formula itself is a LOOKUP/composite shape, synthesize the
  // missing table-calc record here so the producer lowers it through the
  // window/composite path instead of falling through to a bogus bare SUM over
  // the calc caption.
  {
    const existingKeys = new Set<string>()
    for (const rec of tableCalcRecords) {
      existingKeys.add(normalizeTableCalcRecordKey(rec.label))
      existingKeys.add(normalizeTableCalcRecordKey(rec.fieldName))
    }
    const maybeSynthesizeTableCalcRecord = (
      fieldRaw: string | null | undefined,
      captionRaw?: string | null,
    ): void => {
      const authoredField = String(fieldRaw ?? '').trim()
      if (!authoredField) return
      const authoredKey = normalizeTableCalcRecordKey(authoredField)
      const caption = String(captionRaw ?? '').trim() || resolveCaptionForField(authoredField)
      const captionKey = normalizeTableCalcRecordKey(caption)
      if (existingKeys.has(authoredKey) || existingKeys.has(captionKey)) return
      const formula =
        String(
          calcFormulaIndex.get(authoredField) ??
          calcFormulaIndex.get(caption) ??
          '',
        ).trim()
      if (!formula) return
      const isTableCalcFormula =
        classifyCalcFormula(formula).ok ||
        recognizeCompositePattern(formula).ok
      if (!isTableCalcFormula) return
      tableCalcRecords.push({
        label: caption,
        fieldName: authoredField,
        formula,
      })
      existingKeys.add(authoredKey)
      existingKeys.add(captionKey)
    }
    for (const mv of measureValuesFromMnv) {
      maybeSynthesizeTableCalcRecord(mv.field, mv.caption ?? null)
    }
    const scanShelfEntriesForTableCalcRecords = (entries: unknown): void => {
      if (!Array.isArray(entries)) return
      for (const entry of entries) {
        if (!isShelfFieldRef(entry)) continue
        const shelf = entry as ShelfField
        if (shelf.role !== 'measure') continue
        if (isVirtualMnvField(shelf.field_name)) continue
        const displayName =
          typeof shelf.display_name === 'string' && shelf.display_name.trim()
            ? shelf.display_name.trim()
            : null
        maybeSynthesizeTableCalcRecord(shelf.field_name, displayName)
      }
    }
    scanShelfEntriesForTableCalcRecords(crosstab.rows)
    scanShelfEntriesForTableCalcRecords(crosstab.columns)
  }
  // Set form of `measureNamesCustomOrder` for O(1) membership checks.
  // Empty set means "no pivot order authored; pivot everything on the
  // measureValues list", which is the prior behavior.
  const measureNamesPivotLabelSet = new Set<string>(measureNamesCustomOrder)
  // PR7 table-calc-lookup-semantics (step 2 — row-level expression
  // base). When a composite base resolves to a calc-ref whose own
  // formula is a pure row-level expression (`zn([a]) - zn([b])`),
  // synthesize a `TypedCalculatedField` with `semantic: 'measure_input'`
  // and a matching `TypedMeasure` that aggregates it via SUM. The
  // resolver then returns the synthesized measure's alias as the
  // explicit base, so downstream composite handlers stay on a single
  // `{ agg: 'SUM', field: <alias> }` path.
  //
  // The CF alias is derived from the calc-ref's name (physical
  // `Calculation_xxx` preferred over caption) with a `__me` suffix,
  // so repeated references to the same calc share one synthesized
  // CF/measure. The dedup cache keys on the calc-ref name.
  const synthesizedCalcFields: TypedCalculatedField[] = []
  const synthesizedCfAliasByCalcName = new Map<string, string>()
  const synthesizedDimCalcAliasByField = new Map<string, string>()
  const ALLOWED_DIM_CALC_FUNCS = new Set([
    'iif',
    'today',
    'now',
    'contains',
    'starts_with',
    'ends_with',
    'lower',
    'upper',
    'trim',
    'length',
    'left',
    'right',
    'substring',
    'replace',
    'concat',
    'abs',
    'ceil',
    'floor',
    'round',
    'zn',
    'coalesce',
    'ifnull',
    'isnull',
    'sign',
    'date_trunc',
    'date_add',
    'date_diff',
    'date_part',
    'cast_string',
    'cast_integer',
    'cast_float',
    'cast_boolean',
    'cast_date',
    'cast_timestamp',
  ])
  type DimensionCalcValueType = 'string' | 'number' | 'date' | 'timestamp' | 'bool' | 'unknown'
  const normalizeDimensionCalcValueType = (raw: unknown): DimensionCalcValueType => {
    const value = String(raw ?? '').trim().toLowerCase()
    if (!value) return 'unknown'
    if (
      value.includes('string') ||
      value.includes('varchar') ||
      value.includes('char') ||
      value.includes('text') ||
      value === 'nominal' ||
      value === 'ordinal'
    ) return 'string'
    if (value.includes('timestamp') || value.includes('datetime')) return 'timestamp'
    if (value.includes('date')) return 'date'
    if (value.includes('bool')) return 'bool'
    if (
      value.includes('int') ||
      value.includes('float') ||
      value.includes('double') ||
      value.includes('real') ||
      value.includes('decimal') ||
      value.includes('numeric') ||
      value.includes('number') ||
      value === 'quantitative'
    ) return 'number'
    return 'unknown'
  }
  const dimensionCalcFieldValueType = (fieldName: string): DimensionCalcValueType => {
    const fdCol = fieldDictionaryColumnForField(fieldName)
    const ci = fieldDictionaryColumnInstanceForField(fieldName)
    for (const source of [fdCol, ci]) {
      if (!source || typeof source !== 'object') continue
      for (const key of ['datatype', 'data_type', 'type', 'r#type', 'semantic_type']) {
        const inferred = normalizeDimensionCalcValueType((source as any)?.[key])
        if (inferred !== 'unknown') return inferred
      }
    }
    const projection = semanticProjectionEntryForField(fieldName)
    if (projection && typeof projection === 'object') {
      for (const key of ['data_type', 'type', 'semantic_type']) {
        const inferred = normalizeDimensionCalcValueType((projection as any)?.[key])
        if (inferred !== 'unknown') return inferred
      }
    }
    return 'unknown'
  }
  const inferDimensionCalcExprType = (
    node: DimensionCalcExprNode,
    seen: Set<string> = new Set(),
  ): DimensionCalcValueType => {
    switch (node.kind) {
      case 'string_lit':
        return 'string'
      case 'number_lit':
        return 'number'
      case 'bool_lit':
        return 'bool'
      case 'null':
      case 'param_ref':
        return 'unknown'
      case 'field_ref': {
        if (calcFormulaIndex.has(node.name) && !seen.has(node.name)) {
          seen.add(node.name)
          const parsed = parseDimensionCalcFormula(getParameterResolvedCalcFormulaForField(node.name))
          if (parsed) {
            const inferred = inferDimensionCalcExprType(parsed, seen)
            if (inferred !== 'unknown') return inferred
          }
        }
        return dimensionCalcFieldValueType(node.name)
      }
      case 'not':
        return 'bool'
      case 'neg':
        return 'number'
      case 'binary': {
        if (node.op === 'and' || node.op === 'or' || node.op === 'eq' || node.op === 'ne' || node.op === 'lt' || node.op === 'le' || node.op === 'gt' || node.op === 'ge') {
          return 'bool'
        }
        if (node.op === 'add') {
          const lt = inferDimensionCalcExprType(node.lhs, seen)
          const rt = inferDimensionCalcExprType(node.rhs, seen)
          if (lt === 'string' || rt === 'string') return 'string'
          if ((lt === 'date' || lt === 'timestamp') && rt === 'number') return lt
          if (lt === 'number' && (rt === 'date' || rt === 'timestamp')) return rt
        }
        if (node.op === 'sub') {
          const lt = inferDimensionCalcExprType(node.lhs, seen)
          const rt = inferDimensionCalcExprType(node.rhs, seen)
          if ((lt === 'date' || lt === 'timestamp') && rt === 'number') return lt
        }
        return 'number'
      }
      case 'func': {
        const name = node.name.toLowerCase()
        if (name === 'str' || name === 'cast_string' || name === 'left' || name === 'right' || name === 'substring' || name === 'replace' || name === 'lower' || name === 'upper' || name === 'trim' || name === 'concat') return 'string'
        if (name === 'contains' || name === 'starts_with' || name === 'ends_with' || name === 'isnull') return 'bool'
        if (name === 'date' || name === 'cast_date' || name === 'date_trunc' || name === 'datetrunc' || name === 'date_add' || name === 'dateadd' || name === 'today') return 'date'
        if (name === 'now' || name === 'cast_timestamp') return 'timestamp'
        if (name === 'int' || name === 'float' || name === 'cast_integer' || name === 'cast_float' || name === 'abs' || name === 'ceil' || name === 'floor' || name === 'round' || name === 'zn' || name === 'date_diff' || name === 'datediff' || name === 'date_part' || name === 'datepart' || name === 'length') return 'number'
        if ((name === 'ifnull' || name === 'coalesce') && node.args.length > 0) {
          for (const arg of node.args) {
            const inferred = inferDimensionCalcExprType(arg, seen)
            if (inferred !== 'unknown') return inferred
          }
        }
        return 'unknown'
      }
      case 'case_when':
        for (const branch of node.branches) {
          const inferred = inferDimensionCalcExprType(branch.then, seen)
          if (inferred !== 'unknown') return inferred
        }
        return node.else_ ? inferDimensionCalcExprType(node.else_, seen) : 'unknown'
      case 'case_switch':
        for (const branch of node.branches) {
          const inferred = inferDimensionCalcExprType(branch.then, seen)
          if (inferred !== 'unknown') return inferred
        }
        return node.else_ ? inferDimensionCalcExprType(node.else_, seen) : 'unknown'
    }
  }
  function dimCalcNodeToTypedCalcExpr(node: DimensionCalcExprNode): TypedCalcExpr | null {
    switch (node.kind) {
      case 'field_ref': {
        // If the referenced field is itself a calculated field,
        // recursively synthesize it and point this field_ref at
        // the synthesized `__dim_*` alias. Rust's topo-sort on
        // calculated_fields handles the dependency resolution so
        // both calcs land in the correct order at emit time.
        if (calcFormulaIndex.has(node.name)) {
          const cfAlias = ensureDimensionCalcField(node.name)
          if (cfAlias) return { kind: 'field_ref', name: cfAlias }
          return null
        }
        return { kind: 'field_ref', name: resolvePhysicalField(node.name) }
      }
      case 'param_ref':
        return { kind: 'param_ref', name: node.name } as TypedCalcExpr
      case 'string_lit':
        return { kind: 'literal', value: { kind: 'string', value: node.value } } as TypedCalcExpr
      case 'number_lit':
        return {
          kind: 'literal',
          value: Number.isInteger(node.value)
            ? { kind: 'integer', value: node.value }
            : { kind: 'float', value: node.value },
        } as TypedCalcExpr
      case 'bool_lit':
        return { kind: 'literal', value: { kind: 'bool', value: node.value } } as TypedCalcExpr
      case 'null':
        return { kind: 'literal', value: { kind: 'null' } } as TypedCalcExpr
      case 'not': {
        const inner = dimCalcNodeToTypedCalcExpr(node.inner)
        if (!inner) return null
        return { kind: 'unary', op: 'not', expr: inner } as TypedCalcExpr
      }
      case 'neg': {
        const inner = dimCalcNodeToTypedCalcExpr(node.inner)
        if (!inner) return null
        return { kind: 'unary', op: 'neg', expr: inner } as TypedCalcExpr
      }
      case 'binary': {
        // Tableau date arithmetic: `<date> - <int>` subtracts days.
        // DuckDB rejects `TIMESTAMP - INTEGER`, so rewrite as
        // DATE_ADD('day', -<int>, <date>) when the LHS is a clear
        // date expression (date_add / date_trunc result). This
        // mirrors how Tableau Desktop lowers the expression.
        if (
          (node.op === 'sub' || node.op === 'add') &&
          node.lhs.kind === 'func' &&
          (node.lhs.name === 'date_add' ||
            node.lhs.name === 'dateadd' ||
            node.lhs.name === 'date_trunc' ||
            node.lhs.name === 'datetrunc' ||
            node.lhs.name === 'today' ||
            node.lhs.name === 'now') &&
          node.rhs.kind === 'number_lit' &&
          Number.isInteger(node.rhs.value)
        ) {
          const deltaRaw = node.op === 'sub' ? -node.rhs.value : node.rhs.value
          const dateLhs = dimCalcNodeToTypedCalcExpr(node.lhs)
          if (!dateLhs) return null
          return {
            kind: 'func',
            name: 'date_add',
            args: [
              { kind: 'literal', value: { kind: 'string', value: 'day' } } as TypedCalcExpr,
              { kind: 'literal', value: { kind: 'integer', value: deltaRaw } } as TypedCalcExpr,
              dateLhs,
            ],
          } as TypedCalcExpr
        }
        const l = dimCalcNodeToTypedCalcExpr(node.lhs)
        const r = dimCalcNodeToTypedCalcExpr(node.rhs)
        if (!l || !r) return null
        const op = node.op === 'add' &&
          (inferDimensionCalcExprType(node.lhs) === 'string' || inferDimensionCalcExprType(node.rhs) === 'string')
          ? 'concat'
          : node.op
        return { kind: 'binary', op, lhs: l, rhs: r } as TypedCalcExpr
      }
      case 'func': {
        let name = node.name
        if (name === 'dateadd') name = 'date_add'
        else if (name === 'datetrunc') name = 'date_trunc'
        else if (name === 'datediff') name = 'date_diff'
        else if (name === 'datepart') name = 'date_part'
        else if (name === 'float') name = 'cast_float'
        else if (name === 'int') name = 'cast_integer'
        else if (name === 'str') name = 'cast_string'
        else if (name === 'bool') name = 'cast_boolean'
        else if (name === 'date') name = 'cast_date'
        if (!ALLOWED_DIM_CALC_FUNCS.has(name)) return null
        const args: TypedCalcExpr[] = []
        for (const a of node.args) {
          const t = dimCalcNodeToTypedCalcExpr(a)
          if (!t) return null
          args.push(t)
        }
        return { kind: 'func', name: name as any, args }
      }
      case 'case_when': {
        const branches: { when: TypedCalcExpr; then: TypedCalcExpr }[] = []
        for (const b of node.branches) {
          const w = dimCalcNodeToTypedCalcExpr(b.when)
          const t = dimCalcNodeToTypedCalcExpr(b.then)
          if (!w || !t) return null
          branches.push({ when: w, then: t })
        }
        const else_ = node.else_ ? dimCalcNodeToTypedCalcExpr(node.else_) : null
        return { kind: 'case', branches, default: else_ ?? undefined } as TypedCalcExpr
      }
      case 'case_switch': {
        const subject = dimCalcNodeToTypedCalcExpr(node.subject)
        if (!subject) return null
        const branches: { when: TypedCalcExpr; then: TypedCalcExpr }[] = []
        for (const b of node.branches) {
          const w = dimCalcNodeToTypedCalcExpr(b.when)
          const t = dimCalcNodeToTypedCalcExpr(b.then)
          if (!w || !t) return null
          branches.push({
            when: { kind: 'binary', op: 'eq', lhs: subject, rhs: w } as TypedCalcExpr,
            then: t,
          })
        }
        const else_ = node.else_ ? dimCalcNodeToTypedCalcExpr(node.else_) : null
        return { kind: 'case', branches, default: else_ ?? undefined } as TypedCalcExpr
      }
    }
  }
  // Synthesize a dimension calc field that date-truncates a physical
  // date column to a given grain. Mirrors Tableau Desktop's discrete
  // date pill behavior: a `reportenddate` shelf with `time_grain=month`
  // groups by `DATETRUNC('month', reportenddate)` and labels the
  // column as `Month of Reportenddate`. Returns `{ alias, caption }`
  // where `alias` is the synthesized calc field's internal name and
  // `caption` is the display header. Returns null for an unrecognized
  // grain.
  const DATE_TRUNC_GRAINS = new Set([
    'year',
    'quarter',
    'month',
    'week',
    'day',
    'hour',
    'minute',
    'second',
  ])
  function capitalizeFirst(value: string): string {
    return value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value
  }
  function ensureDateTruncDimCalcField(
    physField: string,
    grainRaw: string,
  ): { alias: string; caption: string } | null {
    const grain = String(grainRaw ?? '').trim().toLowerCase()
    if (!DATE_TRUNC_GRAINS.has(grain)) return null
    const resolvedCaption = resolveCaptionForField(physField)
    const displayCaption =
      resolvedCaption && resolvedCaption !== physField
        ? resolvedCaption
        : capitalizeFirst(physField)
    const header = `${capitalizeFirst(grain)} of ${displayCaption}`
    const cacheKey = `__date_trunc::${grain}::${physField}`
    const cached = synthesizedDimCalcAliasByField.get(cacheKey)
    if (cached) return { alias: cached, caption: header }
    const safeSuffix = `${grain}_${physField}`.replace(/[^A-Za-z0-9_]/g, '_')
    const cfAlias = `__dim_date_trunc_${safeSuffix}`
    const expr: TypedCalcExpr = {
      kind: 'func',
      name: 'date_trunc',
      args: [
        { kind: 'literal', value: { kind: 'string', value: grain } },
        { kind: 'field_ref', name: resolvePhysicalField(physField) },
      ],
    }
    synthesizedCalcFields.push({ alias: cfAlias, expr, semantic: 'dimension' })
    synthesizedDimCalcAliasByField.set(cacheKey, cfAlias)
    return { alias: cfAlias, caption: header }
  }
  function ensureDimensionCalcField(fieldName: string): string | null {
    const cached = synthesizedDimCalcAliasByField.get(fieldName)
    if (cached) return cached
    const formula = getParameterResolvedCalcFormulaForField(fieldName)
    if (!formula) return null
    const fixedLodComparison = parseFixedLodComparisonDimensionCalc(formula)
    if (fixedLodComparison) {
      const sourceColumn = ensureFixedLodSourceColumn(
        fixedLodComparison.partitionBy,
        fixedLodComparison.agg,
        fixedLodComparison.field,
      )
      const safeSuffix = fieldName.replace(/[^A-Za-z0-9_]/g, '_')
      const cfAlias = `__dim_${safeSuffix}`
      synthesizedCalcFields.push({
        alias: cfAlias,
        expr: {
          kind: 'binary',
          op: fixedLodComparison.op,
          lhs: { kind: 'field_ref', name: sourceColumn },
          rhs: {
            kind: 'literal',
            value: Number.isInteger(fixedLodComparison.value)
              ? { kind: 'integer', value: fixedLodComparison.value }
              : { kind: 'float', value: fixedLodComparison.value },
          },
        },
        semantic: 'dimension',
      })
      synthesizedDimCalcAliasByField.set(fieldName, cfAlias)
      return cfAlias
    }
    const parsed = parseDimensionCalcFormula(formula)
    if (!parsed) return null
    const expr = dimCalcNodeToTypedCalcExpr(parsed)
    if (!expr) return null
    const safeSuffix = fieldName.replace(/[^A-Za-z0-9_]/g, '_')
    const cfAlias = `__dim_${safeSuffix}`
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr,
      semantic: 'dimension',
    })
    synthesizedDimCalcAliasByField.set(fieldName, cfAlias)
    return cfAlias
  }
  function ensureGeneratedLatLonDimCalcField(fieldName: string): string | null {
    if (!isGeneratedLatLonFieldName(fieldName)) return null
    const cacheKey = `__generated_latlon::${fieldName}`
    const cached = synthesizedDimCalcAliasByField.get(cacheKey)
    if (cached) return cached
    const key = normalizeTableauFieldKey(fieldName) || fieldName.toLowerCase()
    const cfAlias = key === 'longitude' ? '__dim_generated_longitude' : '__dim_generated_latitude'
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr: { kind: 'literal', value: { kind: 'null' } },
      semantic: 'dimension',
    })
    synthesizedDimCalcAliasByField.set(cacheKey, cfAlias)
    return cfAlias
  }
  function ensureGeneratedForecastIndicatorDimCalcField(fieldName: string): string | null {
    const ci = fieldDictionaryColumnInstanceForField(fieldName)
    const forecastType = String((ci as any)?.attrs?.['forecast-column-type'] ?? '').trim().toLowerCase()
    if (forecastType !== 'forecast-indicator') return null
    const cacheKey = `__generated_forecast_indicator::${fieldName}`
    const cached = synthesizedDimCalcAliasByField.get(cacheKey)
    if (cached) return cached
    const cfAlias = '__dim_generated_forecast_indicator'
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr: { kind: 'literal', value: { kind: 'string', value: 'Actual' } },
      semantic: 'dimension',
    })
    synthesizedDimCalcAliasByField.set(cacheKey, cfAlias)
    return cfAlias
  }
  const normalizeDisplayLiteralForCompare = (raw: unknown): string =>
    String(raw ?? '')
      .trim()
      .replace(/^['"]|['"]$/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\s*\|\s*/g, ' | ')
      .trim()
  function rewriteFilterValueForStringLiteralCalc(filter: TypedFilter, fieldName: string): void {
    const formula = getParameterResolvedCalcFormulaForField(fieldName)
    if (!formula) return
    const parsed = parseDimensionCalcFormula(formula)
    if (!parsed || parsed.kind !== 'string_lit') return
    const literalValue = parsed.value
    const literalKey = normalizeDisplayLiteralForCompare(literalValue)
    if (!literalKey) return
    const rewriteScalar = (value: unknown): unknown => {
      if (typeof value !== 'string') return value
      return normalizeDisplayLiteralForCompare(value) === literalKey ? literalValue : value
    }
    if (Array.isArray(filter.value)) {
      filter.value = filter.value.map((value) => rewriteScalar(value)) as any
    } else {
      filter.value = rewriteScalar(filter.value) as any
    }
  }
  function rowExprNodeToTypedCalcExpr(node: RowLevelExprNode): TypedCalcExpr {
    switch (node.kind) {
      case 'field': {
        // If the referenced field is itself a row-level calc, inline
        // its expression recursively instead of emitting a
        // field_ref to a non-existent physical column. Falls back to
        // the physical resolver when the name is not a calc.
        if (calcFormulaIndex.has(node.name)) {
          const formula = calcFormulaIndex.get(node.name) ?? ''
          const innerRow = parseRowLevelExpr(formula)
          if (innerRow) {
            return rowExprNodeToTypedCalcExpr(innerRow)
          }
          // Row-level parser is narrow (ZN/field/+/-). Fall back to the
          // broader dimension-calc parser to handle casts, date/string
          // functions, etc. when the calc is otherwise row-level but
          // uses functions outside the row-level grammar (e.g.
          // `float([x])`, `str([x])`, `date_trunc(...)`).
          const dimExpr = parseDimensionCalcFormula(formula)
          if (dimExpr) {
            const converted = dimCalcNodeToTypedCalcExpr(dimExpr)
            if (converted) return converted
          }
        }
        return { kind: 'field_ref', name: resolvePhysicalField(node.name) }
      }
      case 'zn':
        return {
          kind: 'func',
          name: 'zn',
          args: [rowExprNodeToTypedCalcExpr(node.inner)],
        }
      case 'binary':
        return {
          kind: 'binary',
          op: node.op === '+' ? 'add' : 'sub',
          lhs: rowExprNodeToTypedCalcExpr(node.lhs),
          rhs: rowExprNodeToTypedCalcExpr(node.rhs),
        }
    }
  }
  function synthesizeMeasureFromRowExprCalc(calcName: string): string | null {
    const cached = synthesizedCfAliasByCalcName.get(calcName)
    if (cached != null) return cached
    const formula = calcFormulaIndex.get(calcName)
    if (!formula) return null
    const rowExpr = parseRowLevelExpr(formula)
    if (!rowExpr) return null
    const safeSuffix = calcName.replace(/[^A-Za-z0-9_]/g, '_')
    const cfAlias = `__me_${safeSuffix}`
    const measureAlias = `sum_${cfAlias}`
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr: rowExprNodeToTypedCalcExpr(rowExpr),
      semantic: 'measure_input',
    })
    measures.push({
      field: cfAlias,
      agg: 'SUM',
      alias: measureAlias,
      distinct: false,
    })
    synthesizedCfAliasByCalcName.set(calcName, cfAlias)
    return cfAlias
  }
  // PR7 table-calc-lookup-semantics (step 3 — aggregated-ratio base):
  // when a composite base resolves to a calc-ref whose own formula is
  // an already-aggregated expression (ratio-of-aggregates, optionally
  // wrapped in an `iif(abs(ratio)>cap, null, ratio)` guard), synthesize
  // one SUM measure per distinct physical-field leaf (dedup across
  // calls) and rewrite the tree to a `TypedDerivedExpr` over those
  // measure aliases. The resolver returns the derived expression so
  // downstream composite handlers can feed it to
  // `TypedWindowMeasure.measureExpr` (slice 3d) and to the derived
  // `znBase` leg of Shape B / Shape B capped emission.
  // Source-level FIXED LODs synthesized from `{FIXED [dims]:agg(f)}`
  // leaves inside aggregated calc-ref formulas. Keyed by a canonical
  // signature (partitionBy|agg|field) so repeated references to the
  // same LOD within a formula collapse to a single join.
  const fixedLods: TypedFixedLod[] = []
  const fixedLodAliasBySig = new Map<string, string>()
  const fixedLodSourceColumnBySig = new Map<string, string>()
  function fixedLodSignature(
    partitionBy: string[],
    agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD',
    field: string,
  ): { sig: string; resolvedField: string; resolvedPartition: string[] } {
    const resolvedField = resolvePhysicalField(field)
    const resolvedPartition = partitionBy.map((p) => resolvePhysicalField(p))
    const sig = `${resolvedPartition.join('\u241f')}|${agg}|${resolvedField}`
    return { sig, resolvedField, resolvedPartition }
  }
  function ensureFixedLodSourceColumn(
    partitionBy: string[],
    agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD',
    field: string,
  ): string {
    const { sig, resolvedField, resolvedPartition } = fixedLodSignature(partitionBy, agg, field)
    const cached = fixedLodSourceColumnBySig.get(sig)
    if (cached != null) return cached
    const lodAlias = `__fg_lod_${fixedLods.length}_${resolvedField}`
    const lodOutputCol = `${lodAlias}__val`
    fixedLods.push({
      alias: lodAlias,
      partitionBy: resolvedPartition,
      measure: {
        field: resolvedField,
        agg: agg as TypedAggregation,
        alias: lodOutputCol,
        distinct: agg === 'COUNTD',
      },
      kind: { kind: 'fixed' },
      sourceLevel: true,
    })
    fixedLodSourceColumnBySig.set(sig, lodOutputCol)
    return lodOutputCol
  }
  function ensureFixedLodAndOuterSum(
    partitionBy: string[],
    agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD',
    field: string,
  ): string {
    const { sig, resolvedField } = fixedLodSignature(partitionBy, agg, field)
    const cached = fixedLodAliasBySig.get(sig)
    if (cached != null) return cached
    const lodOutputCol = ensureFixedLodSourceColumn(partitionBy, agg, field)
    const lodAlias = fixedLods.find((lod) => {
      const outputCol = lod.measure.alias || lod.alias
      return outputCol === lodOutputCol
    })?.alias ?? `__fg_lod_${fixedLods.length}_${resolvedField}`
    const outerSumAlias = `sum_${lodAlias}`
    measures.push({
      field: lodOutputCol,
      agg: 'SUM',
      alias: outerSumAlias,
      distinct: false,
    })
    fixedLodAliasBySig.set(sig, outerSumAlias)
    return outerSumAlias
  }

  const aggLeafAliasByField = new Map<string, string>()
  const aggLeafAliasByAggField = new Map<string, string>()
  const rowExprSumAliasBySig = new Map<string, string>()
  const measureInputCalcAliasBySig = new Map<string, string>()
  const parameterMeasureAliasBySig = new Map<string, string>()
  function ensureMeasureInputCalcField(
    label: string,
    expr: TypedCalcExpr,
  ): string {
    const sig = JSON.stringify(expr)
    const cached = measureInputCalcAliasBySig.get(sig)
    if (cached != null) return cached
    const safeSuffix = String(label || `calc_${measureInputCalcAliasBySig.size}`)
      .replace(/[^A-Za-z0-9_]/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80)
    const cfAlias = `__me_${safeSuffix || measureInputCalcAliasBySig.size}`
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr,
      semantic: 'measure_input',
    })
    measureInputCalcAliasBySig.set(sig, cfAlias)
    return cfAlias
  }
  function ensureParameterMeasure(
    name: string,
    aggRaw: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD' = 'AVG',
  ): string {
    const agg = aggRaw === 'COUNTD' ? 'COUNT' : aggRaw
    const distinct = aggRaw === 'COUNTD'
    const sig = `${aggRaw}:${name}`
    const cached = parameterMeasureAliasBySig.get(sig)
    if (cached != null) return cached
    const cfAlias = ensureMeasureInputCalcField(`param_${name}`, {
      kind: 'param_ref',
      name,
    } as TypedCalcExpr)
    const alias = `${aggRaw.toLowerCase()}_${cfAlias}`
    const existing = measures.find(
      (m) =>
        m.field === cfAlias &&
        String(m.agg).toUpperCase() === agg &&
        Boolean(m.distinct) === distinct,
    )
    if (existing) {
      if (!existing.alias) existing.alias = alias
      parameterMeasureAliasBySig.set(sig, existing.alias)
      return existing.alias
    }
    measures.push({ field: cfAlias, agg: agg as TypedAggregation, alias, distinct })
    parameterMeasureAliasBySig.set(sig, alias)
    return alias
  }
  function ensureMeasureForAggField(
    aggRaw: 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD',
    physField: string,
  ): string {
    const formula = getParameterResolvedCalcFormulaForField(physField)
    if (formula) {
      const dimExpr = parseDimensionCalcFormula(formula)
      if (dimExpr && !dimensionCalcContainsAggregate(dimExpr)) {
        const converted = dimCalcNodeToTypedCalcExpr(dimExpr)
        if (converted) {
          const cfAlias = ensureMeasureInputCalcField(physField, converted)
          const agg = aggRaw === 'COUNTD' ? 'COUNT' : aggRaw
          const distinct = aggRaw === 'COUNTD'
          const sig = `${aggRaw}:${cfAlias}`
          const cached = aggLeafAliasByAggField.get(sig)
          if (cached != null) return cached
          const alias = `${aggRaw.toLowerCase()}_${cfAlias}`
          const existing = measures.find(
            (m) =>
              m.field === cfAlias &&
              String(m.agg).toUpperCase() === agg &&
              Boolean(m.distinct) === distinct,
          )
          if (existing) {
            if (!existing.alias) existing.alias = alias
            aggLeafAliasByAggField.set(sig, existing.alias)
            return existing.alias
          }
          measures.push({ field: cfAlias, agg: agg as TypedAggregation, alias, distinct })
          aggLeafAliasByAggField.set(sig, alias)
          return alias
        }
      }
    }
    const resolved = resolvePhysicalField(physField)
    const agg = aggRaw === 'COUNTD' ? 'COUNT' : aggRaw
    const distinct = aggRaw === 'COUNTD'
    const sig = `${aggRaw}:${resolved}`
    const cached = aggLeafAliasByAggField.get(sig)
    if (cached != null) return cached
    const alias = `${aggRaw.toLowerCase()}_${resolved}`
    const existing = measures.find(
      (m) =>
        (m.field === resolved || resolvePhysicalField(m.field) === resolved) &&
        String(m.agg).toUpperCase() === agg &&
        Boolean(m.distinct) === distinct,
    )
    if (existing) {
      if (!existing.alias) existing.alias = alias
      aggLeafAliasByAggField.set(sig, existing.alias)
      return existing.alias
    }
    measures.push({
      field: resolved,
      agg: agg as TypedAggregation,
      alias,
      distinct,
    })
    aggLeafAliasByAggField.set(sig, alias)
    return alias
  }
  function ensureSumMeasureForField(physField: string): string {
    // If `physField` is itself a calc field, route through the
    // row-level / aggregated-calc synthesizers so we produce a SUM
    // over the calc's expression rather than a broken `SUM(<calc
    // caption>)` over a non-existent physical column.
    if (calcFormulaIndex.has(physField)) {
      const formula = calcFormulaIndex.get(physField) ?? ''
      const rowExpr = parseRowLevelExpr(formula)
      if (rowExpr) {
        return ensureSumMeasureForRowExpr(rowExpr)
      }
      // Broader dimension-calc grammar (casts, date/string funcs,
      // iif, case, ...). If we can synthesize a TypedCalcExpr,
      // materialize a `measure_input` calc field and SUM over it.
      const dimExpr = parseDimensionCalcFormula(formula)
      if (dimExpr) {
        const alias = ensureSumMeasureForDimExpr(dimExpr)
        if (alias) return alias
      }
      const aggExpr = parseAggregatedCalcExpr(formula)
      if (aggExpr && aggregatedCalcExprHasAggregateLeaf(aggExpr)) {
        // Aggregated calc used as a `sum([calc])` leaf would mean
        // `sum(sum(...))` which is ill-formed. Fall through to the
        // normal derived-measure route — the caller should really be
        // using it as a derived base, not a sum leaf. Emit a best-
        // effort derived expression wrapped in an outer bin_op-neutral
        // shape so we don't silently corrupt semantics.
        // This path is rarely exercised; most shapes either route
        // through `resolveCompositeBase` or `parseAggregatedCalcExpr`
        // on the outer formula itself.
        void aggExpr
      }
    }
    const resolved = resolvePhysicalField(physField)
    const cached = aggLeafAliasByField.get(resolved)
    if (cached != null) return cached
    const alias =
      measures.find(
        (m) =>
          (m.field === resolved || resolvePhysicalField(m.field) === resolved) &&
          String(m.agg).toUpperCase() === 'SUM',
      )?.alias ?? `sum_${resolved}`
    const exists = measures.some(
      (m) =>
        (m.field === resolved || resolvePhysicalField(m.field) === resolved) &&
        String(m.agg).toUpperCase() === 'SUM',
    )
    if (!exists) {
      measures.push({ field: resolved, agg: 'SUM', alias, distinct: false })
    }
    aggLeafAliasByField.set(resolved, alias)
    return alias
  }
  // Synthesize a `measure_input` calc field for an arbitrary row-level
  // expression (e.g. `ZN([x]) + ZN([y])`) and a SUM measure over that
  // field. Dedup by the structural signature of the row expr so
  // repeated use within a formula collapses to one measure.
  function ensureSumMeasureForRowExpr(rowExpr: RowLevelExprNode): string {
    const sig = JSON.stringify(rowExpr)
    const cached = rowExprSumAliasBySig.get(sig)
    if (cached != null) return cached
    const idx = rowExprSumAliasBySig.size
    const cfAlias = `__me_rowexpr_${idx}`
    const measureAlias = `sum_${cfAlias}`
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr: rowExprNodeToTypedCalcExpr(rowExpr),
      semantic: 'measure_input',
    })
    measures.push({ field: cfAlias, agg: 'SUM', alias: measureAlias, distinct: false })
    rowExprSumAliasBySig.set(sig, measureAlias)
    return measureAlias
  }
  function ensureSumMeasureForDimExpr(dimExpr: DimensionCalcExprNode): string | null {
    const converted = dimCalcNodeToTypedCalcExpr(dimExpr)
    if (!converted) return null
    const sig = `dim:${JSON.stringify(converted)}`
    const cached = rowExprSumAliasBySig.get(sig)
    if (cached != null) return cached
    const idx = rowExprSumAliasBySig.size
    const cfAlias = `__me_dimexpr_${idx}`
    const measureAlias = `sum_${cfAlias}`
    synthesizedCalcFields.push({
      alias: cfAlias,
      expr: converted,
      semantic: 'measure_input',
    })
    measures.push({ field: cfAlias, agg: 'SUM', alias: measureAlias, distinct: false })
    rowExprSumAliasBySig.set(sig, measureAlias)
    return measureAlias
  }
  function appendCalcAwareMeasure(
    fieldRaw: string,
    mapped: AggResolution,
    alias?: string | null,
    caption?: string | null,
  ): { handled: true; measure?: TypedMeasure; alias: string } | { handled: false } {
    const formula = getParameterResolvedCalcFormulaForField(fieldRaw, caption ?? alias ?? null)
    const outputAlias = alias && alias.length > 0 ? alias : null
    if (formula) {
      const aggExpr = parseAggregatedCalcExpr(formula)
      if (aggExpr && aggregatedCalcExprHasAggregateLeaf(aggExpr)) {
        const derivedAlias = outputAlias ?? fieldRaw
        derivedMeasures.push({ alias: derivedAlias, expr: aggNodeToTypedDerivedExpr(aggExpr) })
        return { handled: true, alias: derivedAlias }
      }
      const dimExpr = parseDimensionCalcFormula(formula)
      if (dimExpr) {
        if (dimensionCalcContainsAggregate(dimExpr)) {
          const derivedExpr = aggregateDimensionNodeToDerivedExpr(dimExpr)
          if (derivedExpr) {
            const derivedAlias = outputAlias ?? fieldRaw
            derivedMeasures.push({ alias: derivedAlias, expr: derivedExpr })
            return { handled: true, alias: derivedAlias }
          }
        } else {
          const converted = dimCalcNodeToTypedCalcExpr(dimExpr)
          if (converted) {
            const cfAlias = ensureMeasureInputCalcField(fieldRaw, converted)
            const existing = measures.find((m) =>
              m.field === cfAlias &&
              m.agg === mapped.agg &&
              Boolean(m.distinct) === Boolean(mapped.distinct)
            )
            if (existing) {
              if (outputAlias) existing.alias = outputAlias
              return {
                handled: true,
                measure: existing,
                alias: existing.alias ?? `${existing.agg.toLowerCase()}_${existing.field}`,
              }
            }
            const entry: TypedMeasure = { field: cfAlias, agg: mapped.agg }
            if (mapped.distinct) entry.distinct = true
            if (outputAlias) entry.alias = outputAlias
            measures.push(entry)
            return {
              handled: true,
              measure: entry,
              alias: entry.alias ?? `${entry.agg.toLowerCase()}_${entry.field}`,
            }
          }
        }
      }
      const rowExpr = parseRowLevelExpr(formula)
      if (rowExpr) {
        const cfAlias = ensureMeasureInputCalcField(fieldRaw, rowExprNodeToTypedCalcExpr(rowExpr))
        const existing = measures.find((m) =>
          m.field === cfAlias &&
          m.agg === mapped.agg &&
          Boolean(m.distinct) === Boolean(mapped.distinct)
        )
        if (existing) {
          if (outputAlias) existing.alias = outputAlias
          return {
            handled: true,
            measure: existing,
            alias: existing.alias ?? `${existing.agg.toLowerCase()}_${existing.field}`,
          }
        }
        const entry: TypedMeasure = { field: cfAlias, agg: mapped.agg }
        if (mapped.distinct) entry.distinct = true
        if (outputAlias) entry.alias = outputAlias
        measures.push(entry)
        return {
          handled: true,
          measure: entry,
          alias: entry.alias ?? `${entry.agg.toLowerCase()}_${entry.field}`,
        }
      }
    }
    return { handled: false }
  }
  const pendingHelperWindowMeasures: Array<{
    alias: string
    kind: TypedWindowMeasure['kind']
    measureRef: string
    partitionBy?: string[]
    orderByMode?: 'clone' | 'window'
  }> = []
  const pendingPersistedRankWindowMeasures: Array<{
    alias: string
    kind: TypedWindowMeasure['kind']
    agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
    baseField: string
  }> = []
  const helperWindowAliasBySig = new Map<string, string>()
  function ensureMeasureForClassifiedAggField(
    agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD',
    field: string,
  ): string {
    return agg === 'SUM'
      ? ensureSumMeasureForField(field)
      : ensureMeasureForAggField(agg, field)
  }
  function findAuthoredMeasureForClassifiedAggField(
    aggRaw: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD',
    field: string,
  ): TypedMeasure | undefined {
    const resolved = resolvePhysicalField(field)
    const typedAgg = aggRaw === 'COUNTD' ? 'COUNT' : aggRaw
    const distinct = aggRaw === 'COUNTD'
    return measures.find(
      (m) =>
        (m.field === resolved || resolvePhysicalField(m.field) === resolved) &&
        String(m.agg).toUpperCase() === typedAgg &&
        Boolean(m.distinct) === distinct,
    )
  }
  function resolveExplicitWindowBasePayload(
    base: ClassifiedCalcAggregation & { kind: 'explicit' },
    allowSyntheticBaseMeasure: boolean,
  ): Pick<TypedWindowMeasure, 'measureRef' | 'measureExpr'> | null {
    const baseField = resolvePhysicalField(base.field)
    const authored = findAuthoredMeasureForClassifiedAggField(base.agg, baseField)
    if (authored) {
      if (!authored.alias) authored.alias = `${base.agg.toLowerCase()}_${baseField}`
      return { measureRef: authored.alias }
    }
    if (!allowSyntheticBaseMeasure) return null
    const alias = ensureMeasureForClassifiedAggField(base.agg, baseField)
    // MNV-bound table calcs can require an aggregate input that is not
    // itself displayed. Use the existing window measureExpr channel so
    // the support aggregate is explicitly an input to the calc, while
    // measurePivot continues to expose only authored Measure Values.
    return { measureExpr: { kind: 'measure_ref', field: alias } }
  }
  function resolveExplicitSumWindowBase(
    base: ClassifiedCalcAggregation & { kind: 'explicit' },
    allowSyntheticBaseMeasure: boolean,
  ):
    | {
        payload: Pick<TypedWindowMeasure, 'measureRef' | 'measureExpr'>
        currentRef: TypedDerivedExpr
      }
    | null {
    if (base.agg !== 'SUM') return null
    const baseField = resolvePhysicalField(base.field)
    const authored = findAuthoredMeasureForClassifiedAggField('SUM', baseField)
    if (authored) {
      if (!authored.alias) authored.alias = `sum_${baseField}`
      const currentRef: TypedDerivedExpr = { kind: 'measure_ref', field: authored.alias }
      return { payload: { measureRef: authored.alias }, currentRef }
    }
    if (!allowSyntheticBaseMeasure) return null
    const alias = ensureSumMeasureForField(baseField)
    const currentRef: TypedDerivedExpr = { kind: 'measure_ref', field: alias }
    return { payload: { measureExpr: currentRef }, currentRef }
  }
  function ensureWindowAggregateForFixedLod(
    node: Extract<AggregatedCalcExprNode, { kind: 'window_agg_fixed_lod' }>,
  ): string {
    const baseAlias = ensureMeasureForClassifiedAggField(node.agg, node.field)
    const partitionSig = node.partitionBy
      .map((p) =>
        p.kind === 'field'
          ? `field:${resolvePhysicalField(p.field)}`
          : `date_trunc:${p.grain}:${resolvePhysicalField(p.field)}`,
      )
      .join('\u241f')
    const sig = `${node.windowAgg}|${partitionSig}|${node.agg}|${baseAlias}`
    const cached = helperWindowAliasBySig.get(sig)
    if (cached != null) return cached
    const alias = `__fg_win_${node.windowAgg.toLowerCase()}_${baseAlias}`
      .replace(/[^A-Za-z0-9_]/g, '_')
    pendingHelperWindowMeasures.push({
      alias,
      kind: { kind: 'WINDOW_AVG' },
      measureRef: baseAlias,
      partitionBy: [],
    })
    helperWindowAliasBySig.set(sig, alias)
    return alias
  }
  function aggNodeToTypedDerivedExpr(node: AggregatedCalcExprNode): TypedDerivedExpr {
    switch (node.kind) {
      case 'sum_field':
        return { kind: 'measure_ref', field: ensureSumMeasureForField(node.field) }
      case 'agg_field':
        return { kind: 'measure_ref', field: ensureMeasureForAggField(node.agg, node.field) }
      case 'sum_row_expr':
        return { kind: 'measure_ref', field: ensureSumMeasureForRowExpr(node.inner) }
      case 'sum_dim_expr':
        return { kind: 'measure_ref', field: ensureSumMeasureForDimExpr(node.inner)! }
      case 'fixed_lod':
        return {
          kind: 'measure_ref',
          field: ensureFixedLodAndOuterSum(node.partitionBy, node.agg, node.field),
        }
      case 'window_agg_fixed_lod':
        return { kind: 'measure_ref', field: ensureWindowAggregateForFixedLod(node) }
      case 'lit':
        return decimalNumberToDerivedExpr(node.value) ?? { kind: 'lit', value: Math.trunc(node.value) }
      case 'abs':
        return { kind: 'abs', inner: aggNodeToTypedDerivedExpr(node.inner) }
      case 'coalesce':
        return {
          kind: 'coalesce',
          inner: aggNodeToTypedDerivedExpr(node.inner),
          fallback: aggNodeToTypedDerivedExpr(node.fallback),
        }
      case 'bin_op':
        return {
          kind: 'bin_op',
          op: node.op,
          lhs: aggNodeToTypedDerivedExpr(node.lhs),
          rhs: aggNodeToTypedDerivedExpr(node.rhs),
        }
      case 'iif_abs_gt_cap_then_null':
        return {
          kind: 'iif_abs_gt_cap_then_null',
          inner: aggNodeToTypedDerivedExpr(node.inner),
          cap: node.cap,
          body: aggNodeToTypedDerivedExpr(node.body),
        }
    }
  }
  function aggregatedCalcExprHasAggregateLeaf(node: AggregatedCalcExprNode): boolean {
    switch (node.kind) {
      case 'sum_field':
      case 'agg_field':
      case 'fixed_lod':
      case 'window_agg_fixed_lod':
      case 'sum_row_expr':
      case 'sum_dim_expr':
        return true
      case 'lit':
        return false
      case 'abs':
        return aggregatedCalcExprHasAggregateLeaf(node.inner)
      case 'coalesce':
        return (
          aggregatedCalcExprHasAggregateLeaf(node.inner) ||
          aggregatedCalcExprHasAggregateLeaf(node.fallback)
        )
      case 'bin_op':
        return (
          aggregatedCalcExprHasAggregateLeaf(node.lhs) ||
          aggregatedCalcExprHasAggregateLeaf(node.rhs)
        )
      case 'iif_abs_gt_cap_then_null':
        return (
          aggregatedCalcExprHasAggregateLeaf(node.inner) ||
          aggregatedCalcExprHasAggregateLeaf(node.body)
        )
    }
  }
  function resolveCompositeBase(
    base: ClassifiedCalcAggregation,
  ):
    | { ok: true; base: ClassifiedCalcAggregation & { kind: 'explicit' } }
    | { ok: true; kind: 'derived'; baseExpr: TypedDerivedExpr }
    | {
        ok: false
        isIndexedCalcRef: boolean
        hint?: 'fixed_lod' | 'parameter_ref' | 'unsupported_shape'
      } {
    if (base.kind === 'explicit') {
      // `SUM([CalcRef])` where CalcRef is itself a calc field. Tableau
      // semantics collapse `<outerAgg>([Calc=<innerAgg>(phys)])` to
      // `<innerAgg>(phys)` because CalcRef is already aggregated and
      // re-wrapping with another aggregation is a no-op on the
      // pre-aggregated value.
      if (calcFormulaIndex.has(base.field)) {
        const innerResolved = resolveBareAggregationViaCalcIndex(base.field, calcFormulaIndex)
        if (innerResolved && innerResolved.kind === 'explicit') {
          return { ok: true, base: innerResolved }
        }
        // Row-level expression base: synthesize a measure_input CF +
        // SUM-wrapping measure. The synthesized measure's alias
        // substitutes for the calc-ref as the composite base; its
        // `field` is the CF alias (what the composite handler's
        // authored-measure lookup compares against).
        if (base.agg === 'SUM') {
          const synthCfAlias = synthesizeMeasureFromRowExprCalc(base.field)
          if (synthCfAlias) {
            return {
              ok: true,
              base: { kind: 'explicit', agg: 'SUM', field: synthCfAlias },
            }
          }
          const formula = calcFormulaIndex.get(base.field) ?? ''
          const aggExpr = parseAggregatedCalcExpr(formula)
          if (aggExpr && aggregatedCalcExprHasAggregateLeaf(aggExpr)) {
            return { ok: true, kind: 'derived', baseExpr: aggNodeToTypedDerivedExpr(aggExpr) }
          }
          return {
            ok: false,
            isIndexedCalcRef: true,
            hint: aggregatedCalcExprBlockerHint(formula),
          }
        }
        return { ok: false, isIndexedCalcRef: true }
      }
      return { ok: true, base }
    }
    const resolved = resolveBareAggregationViaCalcIndex(base.field, calcFormulaIndex)
    if (resolved && resolved.kind === 'explicit') {
      return { ok: true, base: resolved }
    }
    if (calcFormulaIndex.has(base.field)) {
      // Bare `([Calc])` where Calc is a row-level expression. Same
      // synthesis path as the explicit `SUM([Calc])` case — Tableau
      // infers the SUM aggregation when a bare ref to a row-level
      // calc is used inside LOOKUP/ZN.
      const synthCfAlias = synthesizeMeasureFromRowExprCalc(base.field)
      if (synthCfAlias) {
        return {
          ok: true,
          base: { kind: 'explicit', agg: 'SUM', field: synthCfAlias },
        }
      }
      // Bare `([Calc])` where Calc is an already-aggregated expression.
      // Tableau's outer `SUM([Calc])` is a no-op on an aggregated
      // value, same as the explicit case above — route through the
      // derived-base synthesis.
      const formula = calcFormulaIndex.get(base.field) ?? ''
      const aggExpr = parseAggregatedCalcExpr(formula)
      if (aggExpr && aggregatedCalcExprHasAggregateLeaf(aggExpr)) {
        return { ok: true, kind: 'derived', baseExpr: aggNodeToTypedDerivedExpr(aggExpr) }
      }
      return {
        ok: false,
        isIndexedCalcRef: true,
        hint: aggregatedCalcExprBlockerHint(formula),
      }
    }
    return { ok: false, isIndexedCalcRef: false }
  }
  const findTableCalcRecordForMeasureValue = (
    fieldRaw: string | null | undefined,
  ): WorksheetTableCalcFieldRecord | undefined => {
    const matchKey = normalizeTableCalcRecordKey(fieldRaw)
    if (!matchKey) return undefined
    return tableCalcRecords.find((record) => {
      const labelKey = normalizeTableCalcRecordKey(record.label)
      const fieldKey = normalizeTableCalcRecordKey(record.fieldName)
      return labelKey === matchKey || fieldKey === matchKey
    })
  }
  // Pre-walk: for each MNV entry that references a table-calc record
  // (by label or fieldName), stamp `__mnvAlias` so the composite /
  // window-measure emission below projects with the MNV caption. The
  // MNV loop itself later skips these entries so we don't emit a
  // redundant bare-SUM over the same caption.
  for (const mv of measureValuesFromMnv) {
    const tcr = findTableCalcRecordForMeasureValue(mv.field)
    if (tcr) tcr.__mnvAlias = resolveCaptionForField(mv.field)
  }
  const stampShelfTableCalcProjectionAliases = (entries: unknown): void => {
    if (!Array.isArray(entries)) return
    for (const entry of entries) {
      if (!isShelfFieldRef(entry)) continue
      const shelf = entry as ShelfField
      if (shelf.role !== 'measure') continue
      if (isVirtualMnvField(shelf.field_name)) continue
      const tcr = findTableCalcRecordForMeasureValue(shelf.field_name)
      if (!tcr) continue
      const alias = firstNonEmptyString(
        shelf.display_name,
        fieldCaptionIndex.get(shelf.field_name),
        resolveCaptionForField(shelf.field_name),
      )
      if (alias && alias !== shelf.field_name) tcr.__projectionAlias = alias
    }
  }
  stampShelfTableCalcProjectionAliases(crosstab.rows)
  stampShelfTableCalcProjectionAliases(crosstab.columns)
  for (const rec of tableCalcRecords) {
    const labelSafe = rec.label.replace(/[:|]/g, '_').replace(/\s+/g, ' ').trim()
    if (persistedPctTotalColumnInstanceForField(rec.fieldName || rec.label)) {
      rec.__persistedPctTotal = true
      continue
    }
    if (!rec.formula) {
      const persistedRankCi =
        persistedRankColumnInstanceForField(rec.columnInstanceName || rec.fieldName || rec.label) ||
        persistedRankColumnInstanceForField(rec.fieldName || rec.label)
      const rankKind = columnInstancePersistedRankKind(persistedRankCi)
      if (persistedRankCi && rankKind) {
        const rankAgg = columnInstanceAggResolution(persistedRankCi) ?? { agg: 'SUM', distinct: false }
        const baseField = columnInstanceBaseField(persistedRankCi) ?? rec.columnName ?? rec.fieldName ?? rec.label
        const classifiedAgg = rankAgg.distinct
          ? 'COUNTD'
          : rankAgg.agg === 'MEDIAN'
          ? null
          : rankAgg.agg
        if (!classifiedAgg) {
          return unsupported(`table_calc_persisted_rank_aggregation_unsupported:${labelSafe}`)
        }
        pendingPersistedRankWindowMeasures.push({
          alias: rec.__mnvAlias || rec.__projectionAlias || rec.label || rec.fieldName,
          kind: rankKind,
          agg: classifiedAgg,
          baseField,
        })
        continue
      }
      // Tableau persists percent-of-total table calcs as column-instance
      // metadata (`type="PctTotal"`) instead of as an inline calculation
      // formula. Route that shared workbook-IR shape through the shelf
      // lowering below, where it becomes a typed PERCENT_OF_TOTAL window
      // measure over the referenced aggregate.
      return unsupported(`table_calc_persisted_no_formula:${labelSafe}`)
    }
    const outcome = classifyCalcFormula(rec.formula)
    if (!outcome.ok) {
      // Before falling back to the generic `calc_formula_shape_unsupported`
      // reason, check whether the formula is one of the two dominant
      // HK post-window arithmetic shapes. Surfacing them as distinct
      // tags keeps conformance telemetry actionable and gives the
      // upcoming post-window arithmetic slice a precise switching
      // point (see docs/compiler-rewrite/conformance-snapshot.md
      // "Partially resolved architectural blocker: table calcs").
      const composite = recognizeCompositePattern(rec.formula)
      if (composite.ok) {
        // PR7 slice 3: `py_diff` is fully wired through the producer —
        // recognized → LAG/LEAD TypedWindowMeasure + TypedDerivedMeasure
        // post-window arithmetic. The remaining two shapes
        // (`first_diff_nullable` + `_abs_capped`) still gate because
        // they need a `FIRST_VALUE(...)` window kind which the
        // typed-spec does not yet expose (`TypedWindowKind::First`
        // today lowers to `(1 - ROW_NUMBER())`, i.e. the Tableau
        // `FIRST()` function — a different primitive from Tableau's
        // `LOOKUP(expr, FIRST())`).
        if (
          composite.pattern.kind === 'py_diff' ||
          composite.pattern.kind === 'first_diff_nullable' ||
          composite.pattern.kind === 'first_diff_nullable_abs_capped'
        ) {
          pendingComposites.push({
            label: rec.label,
            labelSafe,
            alias: rec.__mnvAlias || rec.__projectionAlias || rec.fieldName || rec.label,
            pattern: composite.pattern,
            allowSyntheticBaseMeasure:
              Boolean(rec.__mnvAlias || rec.__projectionAlias) || hasVirtualMnvAuthoringContext,
          })
          continue
        }
      }
      const parsedDimensionCalc = parseDimensionCalcFormula(rec.formula)
      if (parsedDimensionCalc && /\bINDEX\s*\(/i.test(rec.formula)) {
        pendingTableCalcDimensionRecords.push({
          label: rec.label,
          labelSafe,
          fieldName: rec.fieldName || rec.label,
          alias: rec.__mnvAlias || rec.__projectionAlias || rec.fieldName || rec.label,
          parsed: parsedDimensionCalc,
        })
        continue
      }
      return unsupported(`table_calc_unsupported:${labelSafe}:${outcome.reason}`)
    }
    pendingWindowMeasures.push({
      label: rec.label,
      alias: rec.__mnvAlias || rec.__projectionAlias || rec.fieldName || rec.label,
      classified: outcome.classified,
      record: rec,
      allowSyntheticBaseMeasure:
        Boolean(rec.__mnvAlias || rec.__projectionAlias) || hasVirtualMnvAuthoringContext,
    })
  }

  const extractUri = inputs.extractUri
  const baseFromSql = String(inputs.baseFromSql ?? '').trim().replace(/;+\s*$/, '')
  if (!baseFromSql && (typeof extractUri !== 'string' || extractUri.length === 0)) {
    return unsupported('missing_extract_uri')
  }

  const rows = Array.isArray(crosstab.rows) ? crosstab.rows : []
  const cols = Array.isArray(crosstab.columns) ? crosstab.columns : []

  const shelves: ShelfField[] = []
  const colShelfFieldNames = new Set<string>()
  for (const entry of rows) {
    if (!isShelfFieldRef(entry)) return unsupported('non_field_shelf_entry_not_yet_typed')
    if (isVirtualMnvField(entry.field_name)) continue
    shelves.push(entry)
  }
  for (const entry of cols) {
    if (!isShelfFieldRef(entry)) return unsupported('non_field_shelf_entry_not_yet_typed')
    if (isVirtualMnvField(entry.field_name)) continue
    shelves.push(entry)
    colShelfFieldNames.add(entry.field_name)
  }
  // Phase 6a densify: for crosstab worksheets, column-axis dimensions
  // form a cartesian product with row-axis dimensions per Tableau
  // Desktop's crosstab semantics. Track the alias each column dim
  // projects under so we can emit a `TypedDensify` entry with
  // `from_agg_base` domain (= SELECT DISTINCT on that column's values
  // from the agg-base). Non-densified row dims stay as existing
  // DISTINCT tuples, yielding per-partition densification (e.g. every
  // (East/West, Field Supervisor, Location) × every Time Period
  // (Monthly)) that matches Tableau's crosstab output even when some
  // partitions have no rows for certain column values.
  const densifyColDimAliases: string[] = []

  const dimensions: TypedDimension[] = []
  const measures: TypedMeasure[] = []
  const derivedMeasures: TypedDerivedMeasure[] = []
  const dimensionProjectionKeys = new Set<string>()
  const addTypedDimension = (entry: TypedDimension): boolean => {
    const key = JSON.stringify({
      field: String(entry.field ?? '').trim(),
      alias: String(entry.alias ?? '').trim(),
    })
    if (dimensionProjectionKeys.has(key)) return false
    dimensionProjectionKeys.add(key)
    dimensions.push(entry)
    return true
  }
  const aggregateDimensionDerivedAliasByField = new Map<string, string>()
  const tableCalcDimensionDerivedAliasByField = new Map<string, string>()
  const pendingTableCalcIndexWindowAliases = new Map<string, string>()
  const authoredMeasureAliasByField = new Map<string, string>()
  const numberToDerivedLiteral = (value: number): TypedDerivedExpr =>
    Number.isInteger(value)
      ? { kind: 'lit', value }
      : { kind: 'float_lit', value } as TypedDerivedExpr
  const decimalNumberToDerivedExpr = (value: number): TypedDerivedExpr | null => {
    if (!Number.isFinite(value)) return null
    if (Number.isInteger(value)) return numberToDerivedLiteral(value)
    const raw = String(value).includes('e')
      ? value.toFixed(12).replace(/0+$/, '').replace(/\.$/, '')
      : String(value)
    if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return null
    const negative = raw.startsWith('-')
    const unsigned = negative ? raw.slice(1) : raw
    const [wholeRaw, rawFrac = ''] = unsigned.split('.')
    const fracRaw = rawFrac.slice(0, 12).replace(/0+$/, '')
    if (!fracRaw) return { kind: 'lit', value: negative ? -Number(wholeRaw) : Number(wholeRaw) }
    const denominator = 10 ** fracRaw.length
    if (!Number.isSafeInteger(denominator)) return null
    const numerator = Number(`${wholeRaw}${fracRaw}`)
    if (!Number.isSafeInteger(numerator)) return null
    const expr: TypedDerivedExpr = {
      kind: 'bin_op',
      op: '/',
      lhs: numberToDerivedLiteral(negative ? -numerator : numerator),
      rhs: numberToDerivedLiteral(denominator),
    }
    return expr
  }
  const dimensionCalcContainsAggregate = (node: DimensionCalcExprNode | null): boolean => {
    if (!node) return false
    if (node.kind === 'func') {
      const name = String(node.name ?? '').toLowerCase()
      if (['sum', 'avg', 'min', 'max', 'count', 'countd'].includes(name)) return true
      return node.args.some((arg) => dimensionCalcContainsAggregate(arg))
    }
    if (node.kind === 'binary') {
      return dimensionCalcContainsAggregate(node.lhs) || dimensionCalcContainsAggregate(node.rhs)
    }
    if (node.kind === 'unary') return dimensionCalcContainsAggregate((node as any).expr)
    if (node.kind === 'not' || node.kind === 'neg') return dimensionCalcContainsAggregate(node.inner)
    if (node.kind === 'case_when') {
      return (
        node.branches.some((branch) =>
          dimensionCalcContainsAggregate(branch.when) ||
          dimensionCalcContainsAggregate(branch.then),
        ) ||
        dimensionCalcContainsAggregate(node.else_)
      )
    }
    if (node.kind === 'case_switch') {
      return (
        dimensionCalcContainsAggregate(node.subject) ||
        node.branches.some((branch) =>
          dimensionCalcContainsAggregate(branch.when) ||
          dimensionCalcContainsAggregate(branch.then),
        ) ||
        dimensionCalcContainsAggregate(node.else_)
      )
    }
    return false
  }
  const dimensionCalcContainsIndex = (
    node: DimensionCalcExprNode | null,
    visiting: Set<string> = new Set(),
  ): boolean => {
    if (!node) return false
    if (node.kind === 'func') {
      const name = String(node.name ?? '').toLowerCase()
      if (name === 'index') return true
      return node.args.some((arg) => dimensionCalcContainsIndex(arg, visiting))
    }
    if (node.kind === 'field_ref') {
      const formula = getCalcFormulaForField(node.name)
      const visitKey = normalizeTableauFieldKeyStrict(node.name) || String(node.name ?? '').trim().toLowerCase()
      if (visitKey) {
        if (visiting.has(visitKey)) return false
        visiting.add(visitKey)
      }
      const parsed = formula ? parseDimensionCalcFormula(formula) : null
      const containsIndex = dimensionCalcContainsIndex(parsed, visiting)
      if (visitKey) visiting.delete(visitKey)
      return containsIndex
    }
    if (node.kind === 'binary') {
      return dimensionCalcContainsIndex(node.lhs, visiting) || dimensionCalcContainsIndex(node.rhs, visiting)
    }
    if (node.kind === 'unary') return dimensionCalcContainsIndex((node as any).expr, visiting)
    if (node.kind === 'not' || node.kind === 'neg') return dimensionCalcContainsIndex(node.inner, visiting)
    if (node.kind === 'case_when') {
      return (
        node.branches.some((branch) =>
          dimensionCalcContainsIndex(branch.when, visiting) || dimensionCalcContainsIndex(branch.then, visiting),
        ) ||
        dimensionCalcContainsIndex(node.else_, visiting)
      )
    }
    if (node.kind === 'case_switch') {
      return (
        dimensionCalcContainsIndex(node.subject, visiting) ||
        node.branches.some((branch) =>
          dimensionCalcContainsIndex(branch.when, visiting) || dimensionCalcContainsIndex(branch.then, visiting),
        ) ||
        dimensionCalcContainsIndex(node.else_, visiting)
      )
    }
    return false
  }
  function aggregateDimensionNodeToDerivedExpr(
    node: DimensionCalcExprNode,
  ): TypedDerivedExpr | null {
    switch (node.kind) {
      case 'field_ref': {
        if (calcFormulaIndex.has(node.name)) {
          const formula = calcFormulaIndex.get(node.name) ?? ''
          const parsed = parseDimensionCalcFormula(formula)
          return parsed ? aggregateDimensionNodeToDerivedExpr(parsed) : null
        }
        return null
      }
      case 'param_ref':
        return { kind: 'measure_ref', field: ensureParameterMeasure(node.name) }
      case 'string_lit':
        return { kind: 'string_lit', value: node.value }
      case 'number_lit':
        return decimalNumberToDerivedExpr(node.value)
      case 'bool_lit':
        return { kind: 'bool_lit', value: node.value }
      case 'null':
        return { kind: 'null_lit' }
      case 'not': {
        const inner = aggregateDimensionNodeToDerivedExpr(node.inner)
        return inner ? { kind: 'not', inner } : null
      }
      case 'neg': {
        const inner = aggregateDimensionNodeToDerivedExpr(node.inner)
        return inner ? { kind: 'bin_op', op: '*', lhs: { kind: 'lit', value: -1 }, rhs: inner } : null
      }
      case 'binary': {
        const lhs = aggregateDimensionNodeToDerivedExpr(node.lhs)
        const rhs = aggregateDimensionNodeToDerivedExpr(node.rhs)
        if (!lhs || !rhs) return null
        type DerivedBinOp = Extract<TypedDerivedExpr, { kind: 'bin_op' }>['op']
        const opMap: Record<string, DerivedBinOp> = {
          add: '+',
          sub: '-',
          mul: '*',
          div: '/',
          eq: '=',
          ne: '<>',
          lt: '<',
          le: '<=',
          gt: '>',
          ge: '>=',
          and: 'AND',
          or: 'OR',
        }
        const op = opMap[node.op]
        return op ? { kind: 'bin_op', op, lhs, rhs } : null
      }
      case 'func': {
        const name = node.name.toLowerCase()
        const arg0 = node.args[0]
        if (
          ['sum', 'avg', 'min', 'max', 'count', 'countd'].includes(name) &&
          node.args.length === 1 &&
          (arg0?.kind === 'field_ref' || arg0?.kind === 'param_ref')
        ) {
          const agg = name.toUpperCase() as 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
          if (arg0.kind === 'param_ref') {
            return { kind: 'measure_ref', field: ensureParameterMeasure(arg0.name, agg) }
          }
          return {
            kind: 'measure_ref',
            field:
              agg === 'SUM'
                ? ensureSumMeasureForField(arg0.name)
                : ensureMeasureForAggField(agg, arg0.name),
          }
        }
        if (name === 'abs' && node.args.length === 1) {
          const inner = aggregateDimensionNodeToDerivedExpr(node.args[0]!)
          return inner ? { kind: 'abs', inner } : null
        }
        if ((name === 'zn' || name === 'ifnull') && node.args.length >= 1) {
          const inner = aggregateDimensionNodeToDerivedExpr(node.args[0]!)
          if (!inner) return null
          const fallback =
            name === 'ifnull' && node.args[1]
              ? aggregateDimensionNodeToDerivedExpr(node.args[1])
              : ({ kind: 'lit', value: 0 } as TypedDerivedExpr)
          return fallback ? { kind: 'coalesce', inner, fallback } : null
        }
        if (name === 'coalesce' && node.args.length >= 2) {
          const inner = aggregateDimensionNodeToDerivedExpr(node.args[0]!)
          const fallback = aggregateDimensionNodeToDerivedExpr(node.args[1]!)
          return inner && fallback ? { kind: 'coalesce', inner, fallback } : null
        }
        if (name === 'iif' && node.args.length === 3) {
          const when = aggregateDimensionNodeToDerivedExpr(node.args[0]!)
          const then = aggregateDimensionNodeToDerivedExpr(node.args[1]!)
          const otherwise = aggregateDimensionNodeToDerivedExpr(node.args[2]!)
          return when && then && otherwise
            ? { kind: 'case_when', branches: [{ when, then }], default: otherwise }
            : null
        }
        return null
      }
      case 'case_when': {
        const branches: { when: TypedDerivedExpr; then: TypedDerivedExpr }[] = []
        for (const branch of node.branches) {
          const when = aggregateDimensionNodeToDerivedExpr(branch.when)
          const then = aggregateDimensionNodeToDerivedExpr(branch.then)
          if (!when || !then) return null
          branches.push({ when, then })
        }
        const defaultExpr = node.else_ ? aggregateDimensionNodeToDerivedExpr(node.else_) : null
        return { kind: 'case_when', branches, default: defaultExpr ?? undefined }
      }
      case 'case_switch': {
        const subject = aggregateDimensionNodeToDerivedExpr(node.subject)
        if (!subject) return null
        const branches: { when: TypedDerivedExpr; then: TypedDerivedExpr }[] = []
        for (const branch of node.branches) {
          const whenValue = aggregateDimensionNodeToDerivedExpr(branch.when)
          const then = aggregateDimensionNodeToDerivedExpr(branch.then)
          if (!whenValue || !then) return null
          branches.push({
            when: { kind: 'bin_op', op: '=', lhs: subject, rhs: whenValue },
            then,
          })
        }
        const defaultExpr = node.else_ ? aggregateDimensionNodeToDerivedExpr(node.else_) : null
        return { kind: 'case_when', branches, default: defaultExpr ?? undefined }
      }
    }
  }
  const ensureTableCalcIndexWindowAlias = (label: string): string => {
    const key = normalizeTableCalcRecordKey(label) || String(label ?? '').trim()
    const cached = pendingTableCalcIndexWindowAliases.get(key)
    if (cached) return cached
    const safeSuffix =
      String(label || `index_${pendingTableCalcIndexWindowAliases.size}`)
        .replace(/[^A-Za-z0-9_]/g, '_')
        .replace(/^_+|_+$/g, '') || `index_${pendingTableCalcIndexWindowAliases.size}`
    let alias = `__tc_index_${safeSuffix}`
    let n = 2
    while ([...pendingTableCalcIndexWindowAliases.values()].includes(alias)) {
      alias = `__tc_index_${safeSuffix}_${n++}`
    }
    pendingTableCalcIndexWindowAliases.set(key, alias)
    return alias
  }
  function tableCalcDimensionNodeToDerivedExpr(
    node: DimensionCalcExprNode,
    indexLabel: string,
  ): TypedDerivedExpr | null {
    switch (node.kind) {
      case 'field_ref': {
        const formula = getCalcFormulaForField(node.name)
        const parsed = formula ? parseDimensionCalcFormula(formula) : null
        return parsed ? tableCalcDimensionNodeToDerivedExpr(parsed, indexLabel) : null
      }
      case 'param_ref':
        return { kind: 'measure_ref', field: ensureParameterMeasure(node.name) }
      case 'string_lit':
        return { kind: 'string_lit', value: node.value }
      case 'number_lit':
        return decimalNumberToDerivedExpr(node.value)
      case 'bool_lit':
        return { kind: 'bool_lit', value: node.value }
      case 'null':
        return { kind: 'null_lit' }
      case 'not': {
        const inner = tableCalcDimensionNodeToDerivedExpr(node.inner, indexLabel)
        return inner ? { kind: 'not', inner } : null
      }
      case 'neg': {
        const inner = tableCalcDimensionNodeToDerivedExpr(node.inner, indexLabel)
        return inner ? { kind: 'bin_op', op: '*', lhs: { kind: 'lit', value: -1 }, rhs: inner } : null
      }
      case 'binary': {
        const lhs = tableCalcDimensionNodeToDerivedExpr(node.lhs, indexLabel)
        const rhs = tableCalcDimensionNodeToDerivedExpr(node.rhs, indexLabel)
        if (!lhs || !rhs) return null
        type DerivedBinOp = Extract<TypedDerivedExpr, { kind: 'bin_op' }>['op']
        const opMap: Record<string, DerivedBinOp> = {
          add: '+',
          sub: '-',
          mul: '*',
          div: '/',
          eq: '=',
          ne: '<>',
          lt: '<',
          le: '<=',
          gt: '>',
          ge: '>=',
          and: 'AND',
          or: 'OR',
        }
        const op = opMap[node.op]
        return op ? { kind: 'bin_op', op, lhs, rhs } : null
      }
      case 'func': {
        const name = node.name.toLowerCase()
        if (name === 'index' && node.args.length === 0) {
          return { kind: 'measure_ref', field: ensureTableCalcIndexWindowAlias(indexLabel) }
        }
        if (
          ['sum', 'avg', 'min', 'max', 'count', 'countd'].includes(name) &&
          node.args.length === 1 &&
          node.args[0]?.kind === 'field_ref'
        ) {
          const agg = name.toUpperCase() as 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
          return {
            kind: 'measure_ref',
            field:
              agg === 'SUM'
                ? ensureSumMeasureForField(node.args[0].name)
                : ensureMeasureForAggField(agg, node.args[0].name),
          }
        }
        if (name === 'round' && node.args.length >= 1) {
          const inner = tableCalcDimensionNodeToDerivedExpr(node.args[0]!, indexLabel)
          if (!inner) return null
          const scaleArg = node.args[1]
          const scale =
            scaleArg?.kind === 'number_lit' && Number.isInteger(scaleArg.value)
              ? scaleArg.value
              : 0
          return { kind: 'round', inner, scale }
        }
        if (name === 'abs' && node.args.length === 1) {
          const inner = tableCalcDimensionNodeToDerivedExpr(node.args[0]!, indexLabel)
          return inner ? { kind: 'abs', inner } : null
        }
        if ((name === 'zn' || name === 'ifnull') && node.args.length >= 1) {
          const inner = tableCalcDimensionNodeToDerivedExpr(node.args[0]!, indexLabel)
          if (!inner) return null
          const fallback =
            name === 'ifnull' && node.args[1]
              ? tableCalcDimensionNodeToDerivedExpr(node.args[1], indexLabel)
              : ({ kind: 'lit', value: 0 } as TypedDerivedExpr)
          return fallback ? { kind: 'coalesce', inner, fallback } : null
        }
        if (name === 'coalesce' && node.args.length >= 2) {
          const inner = tableCalcDimensionNodeToDerivedExpr(node.args[0]!, indexLabel)
          const fallback = tableCalcDimensionNodeToDerivedExpr(node.args[1]!, indexLabel)
          return inner && fallback ? { kind: 'coalesce', inner, fallback } : null
        }
        if (name === 'iif' && node.args.length === 3) {
          const when = tableCalcDimensionNodeToDerivedExpr(node.args[0]!, indexLabel)
          const then = tableCalcDimensionNodeToDerivedExpr(node.args[1]!, indexLabel)
          const otherwise = tableCalcDimensionNodeToDerivedExpr(node.args[2]!, indexLabel)
          return when && then && otherwise
            ? { kind: 'case_when', branches: [{ when, then }], default: otherwise }
            : null
        }
        return null
      }
      case 'case_when': {
        const branches: { when: TypedDerivedExpr; then: TypedDerivedExpr }[] = []
        for (const branch of node.branches) {
          const when = tableCalcDimensionNodeToDerivedExpr(branch.when, indexLabel)
          const then = tableCalcDimensionNodeToDerivedExpr(branch.then, indexLabel)
          if (!when || !then) return null
          branches.push({ when, then })
        }
        const defaultExpr = node.else_
          ? tableCalcDimensionNodeToDerivedExpr(node.else_, indexLabel)
          : null
        return { kind: 'case_when', branches, default: defaultExpr ?? undefined }
      }
      case 'case_switch': {
        const subject = tableCalcDimensionNodeToDerivedExpr(node.subject, indexLabel)
        if (!subject) return null
        const branches: { when: TypedDerivedExpr; then: TypedDerivedExpr }[] = []
        for (const branch of node.branches) {
          const whenValue = tableCalcDimensionNodeToDerivedExpr(branch.when, indexLabel)
          const then = tableCalcDimensionNodeToDerivedExpr(branch.then, indexLabel)
          if (!whenValue || !then) return null
          branches.push({
            when: { kind: 'bin_op', op: '=', lhs: subject, rhs: whenValue },
            then,
          })
        }
        const defaultExpr = node.else_
          ? tableCalcDimensionNodeToDerivedExpr(node.else_, indexLabel)
          : null
        return { kind: 'case_when', branches, default: defaultExpr ?? undefined }
      }
    }
  }
  function ensureTableCalcDimensionDerivedField(
    fieldName: string,
    outputAlias: string,
  ): string | null {
    const cached = tableCalcDimensionDerivedAliasByField.get(fieldName)
    if (cached) return cached
    const formula = getParameterResolvedCalcFormulaForField(fieldName, outputAlias)
    if (!formula) return null
    const parsed = parseDimensionCalcFormula(formula)
    if (!parsed || !dimensionCalcContainsIndex(parsed)) return null
    const alias = outputAlias || fieldName
    const expr = tableCalcDimensionNodeToDerivedExpr(parsed, alias)
    if (!expr) return null
    derivedMeasures.push({ alias, expr })
    tableCalcDimensionDerivedAliasByField.set(fieldName, alias)
    tableCalcDimensionDerivedAliasByField.set(alias, alias)
    return alias
  }
  for (const pending of pendingTableCalcDimensionRecords) {
    if (!dimensionCalcContainsIndex(pending.parsed)) {
      return unsupported(`table_calc_unsupported:${pending.labelSafe}:calc_formula_shape_unsupported`)
    }
    const expr = tableCalcDimensionNodeToDerivedExpr(pending.parsed, pending.alias)
    if (!expr) {
      return unsupported(`table_calc_unsupported:${pending.labelSafe}:calc_formula_shape_unsupported`)
    }
    derivedMeasures.push({ alias: pending.alias, expr })
    tableCalcDimensionDerivedAliasByField.set(pending.fieldName, pending.alias)
    tableCalcDimensionDerivedAliasByField.set(pending.alias, pending.alias)
    tableCalcDimensionDerivedAliasByField.set(pending.label, pending.alias)
  }
  function ensureAggregateDimensionDerivedField(
    fieldName: string,
    outputAlias: string,
  ): string | null {
    const cached = aggregateDimensionDerivedAliasByField.get(fieldName)
    if (cached) return cached
    const formula = calcFormulaIndex.get(fieldName)
    if (!formula || !/\b(?:SUM|AVG|MIN|MAX|COUNT|COUNTD)\s*\(/i.test(formula)) return null
    const parsed = parseDimensionCalcFormula(formula)
    if (!parsed) return null
    const expr = aggregateDimensionNodeToDerivedExpr(parsed)
    if (!expr) return null
    const alias = outputAlias || fieldName
    derivedMeasures.push({ alias, expr })
    aggregateDimensionDerivedAliasByField.set(fieldName, alias)
    return alias
  }
  // Pending sort descriptors collected per-shelf; resolved to
  // TypedOrderBy entries once all measures are known (so by_field can
  // reference a measure's effective alias).
  type PendingSort = { shelf: ShelfField; descriptor: SortDescriptor }
  const pendingDimensionSorts: PendingSort[] = []
  const pendingMeasureSorts: PendingSort[] = []

  const addRawWorksheetAxisFieldsMissingFromNormalizedShelves = (): void => {
    if (!activeWorkbookWorksheet || typeof activeWorkbookWorksheet !== 'object') return
    const shelfKeys = new Set<string>()
    const addShelfKey = (raw: unknown): void => {
      const value = String(raw ?? '').trim()
      if (!value) return
      for (const key of [
        value,
        fieldNameFromTableauInstanceToken(value),
        normalizeTableauFieldKey(value),
        normalizeTableauFieldKey(fieldNameFromTableauInstanceToken(value)),
      ]) {
        if (key) shelfKeys.add(key)
      }
    }
    for (const shelf of shelves) addShelfKey(shelf.field_name)

    const addAxisFields = (axis: 'rows' | 'cols'): void => {
      const rawAxis = (activeWorkbookWorksheet as any)?.[axis]
      const fields = Array.isArray(rawAxis?.fields) ? rawAxis.fields : []
      for (const rawField of fields) {
        const authoredField = fieldNameFromTableauInstanceToken(rawField)
        if (isVirtualMnvFieldName(rawField) || isVirtualMnvFieldName(authoredField)) continue
        if (!authoredField || isWorkbookParameterFieldName(authoredField) || isWorkbookParameterFieldName(rawField)) continue
        const keys = [
          rawField,
          authoredField,
          normalizeTableauFieldKey(rawField),
          normalizeTableauFieldKey(authoredField),
        ].filter(Boolean).map(String)
        if (keys.some((key) => shelfKeys.has(key))) continue
        const fdCol = fieldDictionaryColumnForField(rawField) ?? fieldDictionaryColumnForField(authoredField)
        if (isMeasureLikeFieldDictionaryColumn(fdCol)) continue
        if (hasCalcFormulaForField(authoredField)) continue
        const caption = String((fdCol as any)?.caption ?? '').trim()
        const shelf: ShelfField = {
          field_name: authoredField,
          role: 'dimension',
          type: 'discrete',
          ...(caption && caption !== authoredField ? { display_name: caption } : {}),
        }
        shelves.push(shelf)
        addShelfKey(authoredField)
        if (axis === 'cols') colShelfFieldNames.add(authoredField)
      }
    }

    addAxisFields('rows')
    addAxisFields('cols')
  }

  addRawWorksheetAxisFieldsMissingFromNormalizedShelves()

  for (const f of loweredFilters) {
    if (!f || typeof f.field !== 'string') continue
    const authoredField = f.field
    const cfAlias = ensureDimensionCalcField(authoredField)
    if (cfAlias) rewriteFilterValueForStringLiteralCalc(f, authoredField)
    if (cfAlias) f.field = cfAlias
    else f.field = resolvePhysicalField(f.field)
  }
  for (const f of loweredRuntimeFilters) {
    if (!f || typeof f.field !== 'string') continue
    const authoredField = f.field
    const cfAlias = ensureDimensionCalcField(authoredField)
    if (cfAlias) rewriteFilterValueForStringLiteralCalc(f, authoredField)
    if (cfAlias) f.field = cfAlias
    else f.field = resolvePhysicalField(f.field)
  }
  for (const shelf of shelves) {
    const sortOutcome = tryLowerShelfSort(shelf)
    if (!sortOutcome.ok) return unsupported(sortOutcome.reason)
    const aliasRaw = typeof shelf.display_name === 'string' ? shelf.display_name.trim() : ''
    const alias = aliasRaw.length > 0 && aliasRaw !== shelf.field_name ? aliasRaw : null
    const timeGrainRaw = (shelf as unknown as { time_grain?: unknown }).time_grain
    const hasShelfDateTimeGrain = typeof timeGrainRaw === 'string' && timeGrainRaw.trim().length > 0
    const effectiveShelfRole = hasShelfDateTimeGrain && !isVirtualMnvField(shelf.field_name)
      ? 'dimension'
      : shelf.role
    if (effectiveShelfRole === 'dimension') {
      if (isWorkbookParameterFieldName(shelf.field_name) || isWorkbookParameterFieldName(alias)) {
        continue
      }
      const grainStr =
        typeof timeGrainRaw === 'string' ? timeGrainRaw.trim().toLowerCase() : ''
      if (grainStr && !calcFormulaIndex.has(shelf.field_name)) {
        const dt = ensureDateTruncDimCalcField(shelf.field_name, grainStr)
        if (dt) {
          const entry: TypedDimension = { field: dt.alias, alias: dt.caption }
          const added = addTypedDimension(entry)
          if (added && colShelfFieldNames.has(shelf.field_name)) {
            densifyColDimAliases.push(entry.alias ?? entry.field)
          }
          if (sortOutcome.descriptor.kind !== 'none') {
            pendingDimensionSorts.push({ shelf, descriptor: sortOutcome.descriptor })
          }
          continue
        }
      }
      const tableCalcDimAlias = ensureTableCalcDimensionDerivedField(
        shelf.field_name,
        alias ?? shelf.field_name,
      )
      if (tableCalcDimAlias) {
        if (sortOutcome.descriptor.kind !== 'none') {
          pendingMeasureSorts.push({
            shelf: { ...shelf, field_name: tableCalcDimAlias, role: 'measure' },
            descriptor: sortOutcome.descriptor,
          })
        }
        continue
      }
      const aggregateDimAlias = ensureAggregateDimensionDerivedField(
        shelf.field_name,
        alias ?? shelf.field_name,
      )
      if (aggregateDimAlias) {
        if (sortOutcome.descriptor.kind !== 'none') {
          pendingMeasureSorts.push({
            shelf: { ...shelf, field_name: aggregateDimAlias, role: 'measure' },
            descriptor: sortOutcome.descriptor,
          })
        }
        continue
      }
      const generatedLatLonAlias = ensureGeneratedLatLonDimCalcField(shelf.field_name)
      const generatedForecastAlias = generatedLatLonAlias ? null : ensureGeneratedForecastIndicatorDimCalcField(shelf.field_name)
      const cfAlias = generatedLatLonAlias ?? generatedForecastAlias ?? ensureDimensionCalcField(shelf.field_name)
      const resolvedDimField = cfAlias ?? resolvePhysicalField(shelf.field_name)
      const entry: TypedDimension = { field: resolvedDimField }
      // PR7: when a calc dim is synthesized, prefer the authored
      // display caption (display_name) for the output column over the
      // raw internal field_name. This keeps the projection name aligned
      // with Tableau Desktop's header output (e.g. "Time Period (Monthly)"
      // instead of "Time Period (copy)_20041018..."). Falls back to the
      // raw field_name if no display caption is authored.
      if (cfAlias) entry.alias = alias ?? shelf.field_name
      else if (alias) entry.alias = alias
      else if (resolvedDimField !== shelf.field_name) entry.alias = shelf.field_name
      const added = addTypedDimension(entry)
      if (added && colShelfFieldNames.has(shelf.field_name)) {
        densifyColDimAliases.push(entry.alias ?? entry.field)
      }
      if (sortOutcome.descriptor.kind !== 'none') {
        pendingDimensionSorts.push({ shelf, descriptor: sortOutcome.descriptor })
      }
      continue
    }
    if (effectiveShelfRole === 'measure') {
      if (isWorkbookParameterFieldName(shelf.field_name)) {
        const aggRaw = String(shelf.aggregation ?? 'avg').trim().toUpperCase()
        const agg = (aggRaw === 'SUM' || aggRaw === 'MIN' || aggRaw === 'MAX' || aggRaw === 'COUNT' || aggRaw === 'COUNTD')
          ? aggRaw
          : 'AVG'
        ensureParameterMeasure(shelf.field_name, agg as 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD')
        continue
      }
      let aggRaw = String(shelf.aggregation ?? 'sum').toLowerCase()
      if (aggRaw === 'agg') {
        const shelfCaption = fieldCaptionIndex.get(shelf.field_name) ?? ''
        const formula = getParameterResolvedCalcFormulaForField(shelf.field_name, alias ?? shelfCaption)
        if (formula) {
          const tcrForShelf = findTableCalcRecordForMeasureValue(shelf.field_name)
          if (tcrForShelf) {
            const tableCalcAlias =
              tcrForShelf.__projectionAlias || tcrForShelf.__mnvAlias || tcrForShelf.fieldName || tcrForShelf.label
            authoredMeasureAliasByField.set(shelf.field_name, tableCalcAlias)
            if (alias) authoredMeasureAliasByField.set(alias, tableCalcAlias)
            continue
          }
          const aggExpr = parseAggregatedCalcExpr(formula)
          if (!aggExpr) {
            return unsupported(`measure_aggregation_unsupported:${aggRaw}`)
          }
          const derivedAlias = alias ?? shelf.field_name
          derivedMeasures.push({ alias: derivedAlias, expr: aggNodeToTypedDerivedExpr(aggExpr) })
          authoredMeasureAliasByField.set(shelf.field_name, derivedAlias)
          if (alias) authoredMeasureAliasByField.set(alias, derivedAlias)
          continue
        }
        if (looksLikeTableauCalcFieldRef(shelf.field_name)) {
          return unsupported(`measure_aggregation_agg_formula_missing:${shelf.field_name}`)
        }
        aggRaw = 'sum'
      }
      const mapped = aggRaw in AGG_MAP ? AGG_MAP[aggRaw] : null
      if (!mapped) {
        return unsupported(`measure_aggregation_unsupported:${aggRaw}`)
      }
      const persistedPctTotalCi = persistedPctTotalColumnInstanceForField(shelf.field_name)
      if (persistedPctTotalCi) {
        const pctAgg = columnInstanceAggResolution(persistedPctTotalCi) ?? mapped
        const baseField = columnInstanceBaseField(persistedPctTotalCi) ?? shelf.field_name
        const visibleAlias = firstNonEmptyString(
          alias,
          fieldCaptionIndex.get(shelf.field_name),
          resolveCaptionForField(shelf.field_name),
          shelf.field_name,
        ) ?? shelf.field_name
        const safeBaseAliasSuffix = String(visibleAlias || baseField || 'pct_total')
          .replace(/[^A-Za-z0-9_]/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 80)
        const baseAlias = `__pct_base_${safeBaseAliasSuffix || 'measure'}`
        let measureRef: string
        const baseFormula = getParameterResolvedCalcFormulaForField(
          baseField,
          fieldCaptionIndex.get(baseField) ?? fieldCaptionIndex.get(shelf.field_name) ?? null,
        )
        const baseDimExpr = baseFormula ? parseDimensionCalcFormula(baseFormula) : null
        if (baseDimExpr && !dimensionCalcContainsAggregate(baseDimExpr)) {
          const converted = dimCalcNodeToTypedCalcExpr(baseDimExpr)
          if (!converted) {
            return unsupported(`table_calc_persisted_pct_total_base_calc_unsupported:${baseField}`)
          }
          const cfAlias = ensureMeasureInputCalcField(baseField, converted)
          const existing = measures.find(
            (m) =>
              m.field === cfAlias &&
              String(m.agg).toUpperCase() === pctAgg.agg &&
              Boolean(m.distinct) === Boolean(pctAgg.distinct),
          )
          if (existing) {
            if (!existing.alias) existing.alias = baseAlias
            measureRef = existing.alias
          } else {
            const entry: TypedMeasure = { field: cfAlias, agg: pctAgg.agg, alias: baseAlias }
            if (pctAgg.distinct) entry.distinct = true
            measures.push(entry)
            measureRef = baseAlias
          }
        } else {
          const calcBase = appendCalcAwareMeasure(
            baseField,
            pctAgg,
            baseAlias,
            fieldCaptionIndex.get(baseField) ?? fieldCaptionIndex.get(shelf.field_name) ?? null,
          )
          if (calcBase.handled && !calcBase.measure) {
            return unsupported(`table_calc_persisted_pct_total_base_not_measure:${baseField}`)
          }
          if (calcBase.handled) {
          measureRef = calcBase.alias
          } else {
          const physField = resolvePhysicalField(baseField)
          const existing = measures.find(
            (m) =>
              (m.field === physField || resolvePhysicalField(m.field) === physField) &&
              String(m.agg).toUpperCase() === pctAgg.agg &&
              Boolean(m.distinct) === Boolean(pctAgg.distinct),
          )
          if (existing) {
            if (!existing.alias) existing.alias = baseAlias
            measureRef = existing.alias
          } else {
            const entry: TypedMeasure = { field: physField, agg: pctAgg.agg, alias: baseAlias }
            if (pctAgg.distinct) entry.distinct = true
            measures.push(entry)
            measureRef = baseAlias
          }
        }
        }
        pendingHelperWindowMeasures.push({
          alias: visibleAlias,
          kind: { kind: 'PERCENT_OF_TOTAL' },
          measureRef,
          partitionBy: [],
        })
        authoredMeasureAliasByField.set(shelf.field_name, visibleAlias)
        if (alias) authoredMeasureAliasByField.set(alias, visibleAlias)
        if (sortOutcome.descriptor.kind !== 'none') {
          pendingMeasureSorts.push({
            shelf: { ...shelf, field_name: visibleAlias, display_name: visibleAlias },
            descriptor: sortOutcome.descriptor,
          })
        }
        continue
      }
      const calcMeasure = appendCalcAwareMeasure(
        shelf.field_name,
        mapped,
        alias,
        fieldCaptionIndex.get(shelf.field_name) ?? null,
      )
      if (calcMeasure.handled) {
        authoredMeasureAliasByField.set(shelf.field_name, calcMeasure.alias)
        if (alias) authoredMeasureAliasByField.set(alias, calcMeasure.alias)
        if (sortOutcome.descriptor.kind !== 'none') {
          pendingMeasureSorts.push({ shelf, descriptor: sortOutcome.descriptor })
        }
        continue
      }
      const physField = resolvePhysicalField(shelf.field_name)
      const entry: TypedMeasure = { field: physField, agg: mapped.agg }
      if (mapped.distinct) entry.distinct = true
      // Only surface an explicit alias when the author provided a
      // display_name. When absent, the Rust `default_measure_alias`
      // computes the same `<agg>_<physField>` value at lowering time,
      // so the two compilers agree without the TS side injecting a
      // default the tests did not author.
      if (alias) entry.alias = alias
      measures.push(entry)
      if (sortOutcome.descriptor.kind !== 'none') {
        pendingMeasureSorts.push({ shelf, descriptor: sortOutcome.descriptor })
      }
      continue
    }
    return unsupported(`shelf_role_unsupported:${shelf.role}`)
  }

  // Unfold the classic Measure Names / Measure Values pattern: when
  // the authoring spec declares measures exclusively in
  // `crosstab.measureValues`, emit each as a TypedMeasure after the
  // shelf-derived measures. Tableau Desktop renders these as N
  // side-by-side columns in `measureValues` order, so we preserve that
  // order here.
  //
  // `mnvPivotEntries` tracks the per-entry (alias, label) pairs the
  // outer code will fold into `measurePivot`. We capture the label at
  // unfold time (the author-provided `mv.field`, which is the caption
  // Tableau emits in the `Measure Names` column) and resolve the alias
  // after the measure / derived / composite branches settle on their
  // effective alias.
  type MnvPivotBinding =
    | { kind: 'measure'; label: string; measure: TypedMeasure }
    | { kind: 'derived'; label: string; alias: string }
    | { kind: 'tablecalc'; label: string; record: WorksheetTableCalcFieldRecord }
  const mnvPivotBindings: MnvPivotBinding[] = []
  for (const mv of measureValuesFromMnv) {
    const aggRaw = String(mv.aggregation ?? 'sum').toLowerCase()
    const mapped = aggRaw in AGG_MAP ? AGG_MAP[aggRaw] : null
    if (!mapped) {
      return unsupported(`measure_values_aggregation_unsupported:${mv.field}:${aggRaw}`)
    }
    // Resolve this entry's effective caption (what Tableau Desktop
    // shows in the `Measure Names` column / side-by-side header). The
    // authored string is either the caption directly (`Hair Cut Count`)
    // or the physical field name (`newcustomercount`) — accept both
    // and normalize to caption form.
    const mvCaption = String(mv.caption ?? '').trim() || resolveCaptionForField(mv.field)
    // When a `Measure Names` custom_order is authored, it is the
    // authoritative pivot set. Any measureValues entry whose caption
    // is NOT in that set is kept out of the pivot and projected as a
    // standalone non-melted measure column. When no custom_order is
    // authored the set is empty → preserve prior behavior (pivot
    // everything).
    const participatesInPivot =
      measureNamesPivotLabelSet.size === 0 ||
      measureNamesPivotLabelSet.has(mvCaption) ||
      measureNamesPivotLabelSet.has(mv.field)
    if (!participatesInPivot) {
      // `Measure Names` `custom_order` is a *sort* override, not a
      // pivot-inclusion gate — Tableau Desktop still pivots new
      // `measureValues` entries authored after the sort was saved.
      // Staleness is common (e.g. `Product Count % Diff` added to
      // `measureValues` after the last `custom_order` edit). We
      // therefore treat calculated/table-calc entries that aren't in
      // `custom_order` as ordinary pivot participants (they appear at
      // the tail in measureValues order). The only entries that stay
      // non-pivoted in this branch are *bare* physical-column
      // measures — those surface as separate side-by-side columns
      // alongside the MNV pivot (e.g. `Newcustomercount`,
      // `Repeatcustomercount` which also appear as tooltip encodings
      // elsewhere in the workbook IR).
      const tcrForMv = findTableCalcRecordForMeasureValue(mv.field)
      if (tcrForMv) {
        tcrForMv.__mnvAlias = mvCaption
        mnvPivotBindings.push({ kind: 'tablecalc', label: mvCaption, record: tcrForMv })
        continue
      }
      const formula = getParameterResolvedCalcFormulaForField(mv.field, mvCaption)
      if (formula) {
        const aggExpr = parseAggregatedCalcExpr(formula)
        if (aggExpr && aggregatedCalcExprHasAggregateLeaf(aggExpr)) {
          const expr = aggNodeToTypedDerivedExpr(aggExpr)
          derivedMeasures.push({ alias: mvCaption, expr })
          mnvPivotBindings.push({ kind: 'derived', label: mvCaption, alias: mvCaption })
          continue
        }
        const synth = synthesizeMeasureFromRowExprCalc(mv.field)
        if (synth) {
          const sumEntry = measures.find(
            (m) => m.field === synth && String(m.agg).toUpperCase() === 'SUM',
          )
          if (sumEntry) {
            sumEntry.alias = mvCaption
            mnvPivotBindings.push({ kind: 'measure', label: mvCaption, measure: sumEntry })
          }
          continue
        }
        const calcMeasure = appendCalcAwareMeasure(mv.field, mapped, mvCaption, mvCaption)
        if (calcMeasure.handled) {
          if (calcMeasure.measure) {
            mnvPivotBindings.push({ kind: 'measure', label: mvCaption, measure: calcMeasure.measure })
          } else {
            mnvPivotBindings.push({ kind: 'derived', label: mvCaption, alias: calcMeasure.alias })
          }
          continue
        }
      }
      if (looksLikeTableauCalcFieldRef(mv.field)) {
        return unsupported(`measure_values_calc_formula_missing:${mv.field}`)
      }
      const physField = resolvePhysicalField(mv.field)
      const entry: TypedMeasure = { field: physField, agg: mapped.agg, alias: mvCaption }
      if (mapped.distinct) entry.distinct = true
      measures.push(entry)
      continue
    }
    // If this measure is a calculated field, try to route it through
    // the typed pipelines before falling back to a bare-SUM over a
    // physical column that does not exist.
    //
    // Routing order:
    //  1. Table calcs (LOOKUP-style or recognized composite patterns)
    //     already collected by `resolveWorksheetTableCalcFields`: defer
    //     to the `pendingWindowMeasures` / `pendingComposites` loops
    //     and ensure their alias matches the MNV caption so the
    //     projected column name resolves. Skip the MNV-local branch.
    //  2. Aggregated-expression calcs (ratio-of-aggregates, FIXED LOD
    //     terms): synthesize a TypedDerivedMeasure.
    //  3. Row-level calcs (e.g. `zn(x)+zn(y)`, `zn(x)-zn(y)`):
    //     synthesize via `synthesizeMeasureFromRowExprCalc` which
    //     adds a measure_input calc field and a SUM over its alias.
    //     Rename that measure to the MNV caption so the outer SELECT
    //     projects as expected.
    //  4. Otherwise: fall through to the physical-field path so any
    //     remaining mismatch surfaces cleanly (not as a silent
    //     broken `SUM("<calc caption>")`).
    const tcrForMv = findTableCalcRecordForMeasureValue(mv.field)
    if (tcrForMv) {
      // Re-alias the pending window/composite entry (emitted later)
      // so its projected column matches the MNV caption the crosstab
      // will request. Use label when the MNV reference is the label,
      // otherwise leave the record's default fieldName alias in place.
      tcrForMv.__mnvAlias = mvCaption
      mnvPivotBindings.push({ kind: 'tablecalc', label: mvCaption, record: tcrForMv })
      continue
    }
    const formula = getParameterResolvedCalcFormulaForField(mv.field, mvCaption)
    if (formula) {
      const aggExpr = parseAggregatedCalcExpr(formula)
      if (aggExpr && aggregatedCalcExprHasAggregateLeaf(aggExpr)) {
        const expr = aggNodeToTypedDerivedExpr(aggExpr)
        derivedMeasures.push({ alias: mvCaption, expr })
        mnvPivotBindings.push({ kind: 'derived', label: mvCaption, alias: mvCaption })
        continue
      }
      // Row-level expression like `zn(x)+zn(y)` or `zn(x)-zn(y)`.
      // `synthesizeMeasureFromRowExprCalc` pushes a calc field of
      // semantic `measure_input` and a SUM over it with alias
      // `sum___me_<safe>`. Re-alias that SUM to the MNV caption so
      // the projected column name matches what the crosstab expects.
      const synth = synthesizeMeasureFromRowExprCalc(mv.field)
      if (synth) {
        const sumEntry = measures.find(
          (m) => m.field === synth && String(m.agg).toUpperCase() === 'SUM',
        )
        if (sumEntry) {
          sumEntry.alias = mvCaption
          mnvPivotBindings.push({ kind: 'measure', label: mvCaption, measure: sumEntry })
        }
        continue
      }
      const calcMeasure = appendCalcAwareMeasure(mv.field, mapped, mvCaption, mvCaption)
      if (calcMeasure.handled) {
        if (calcMeasure.measure) {
          mnvPivotBindings.push({ kind: 'measure', label: mvCaption, measure: calcMeasure.measure })
        } else {
          mnvPivotBindings.push({ kind: 'derived', label: mvCaption, alias: calcMeasure.alias })
        }
        continue
      }
    }
    if (looksLikeTableauCalcFieldRef(mv.field)) {
      return unsupported(`measure_values_calc_formula_missing:${mv.field}`)
    }
    const physField = resolvePhysicalField(mv.field)
    const entry: TypedMeasure = { field: physField, agg: mapped.agg }
    if (mapped.distinct) entry.distinct = true
    measures.push(entry)
    mnvPivotBindings.push({ kind: 'measure', label: mvCaption, measure: entry })
  }

  // Build an alias map for measures: field_name -> effective SQL alias.
  // Authored aliases (via display_name) win; otherwise the typed
  // compiler's `<agg>_<field>` default is the stable projected name.
  const measureAliasByField = new Map<string, string>()
  const setMeasureAlias = (raw: string | null | undefined, effective: string): void => {
    const key = String(raw ?? '').trim()
    if (!key) return
    const keys = [
      key,
      normalizeTableauFieldKey(key),
      normalizeTableauFieldKeyStrict(key),
    ].filter(Boolean) as string[]
    for (const k of keys) {
      if (!measureAliasByField.has(k)) measureAliasByField.set(k, effective)
    }
  }
  const getMeasureAlias = (raw: string | null | undefined): string | undefined => {
    const key = String(raw ?? '').trim()
    if (!key) return undefined
    return (
      measureAliasByField.get(key) ??
      measureAliasByField.get(normalizeTableauFieldKey(key)) ??
      measureAliasByField.get(normalizeTableauFieldKeyStrict(key))
    )
  }
  for (const m of measures) {
    const effective = (m.alias && m.alias.length > 0)
      ? m.alias
      : `${m.agg.toLowerCase()}_${m.field}`
    setMeasureAlias(m.field, effective)
    setMeasureAlias(resolveCaptionForField(m.field), effective)
    setMeasureAlias(m.alias, effective)
  }
  for (const m of derivedMeasures) {
    setMeasureAlias(m.alias, m.alias)
  }
  for (const [field, alias] of authoredMeasureAliasByField) {
    setMeasureAlias(field, alias)
  }
  const ensureSortHelperMeasure = (rawField: string): string | null => {
    const byField = String(rawField ?? '').trim()
    if (!byField) return null
    const existingAlias = getMeasureAlias(byField)
    if (existingAlias) return existingAlias
    let countLabelEntry = inferCountMeasureEntryFromMeasureNameLabel(byField)
    if (!countLabelEntry) {
      const countMatch = byField.match(/^count\s+of\s+(.+)$/i)
      const targetRaw = String(countMatch?.[1] ?? '').trim()
      if (targetRaw) {
        const targetForms = new Set<string>()
        const addTarget = (value: string): void => {
          const key = normalizeTableauFieldKey(value)
          if (!key) return
          targetForms.add(key)
          const singular = key.replace(/s$/i, '').trim()
          if (singular) targetForms.add(singular)
        }
        addTarget(targetRaw)
        addTarget(targetRaw.replace(/\bcustomers\b/i, 'customer name'))
        addTarget(targetRaw.replace(/\bcustomer\b/i, 'customer name'))
        const existingCount = measures.find((m) => {
          if (m.agg !== 'COUNT' || !m.distinct) return false
          const keys = [
            normalizeTableauFieldKey(m.field),
            normalizeTableauFieldKey(resolveCaptionForField(m.field)),
            normalizeTableauFieldKey(m.alias),
          ].filter(Boolean) as string[]
          return keys.some((key) => {
            const singular = key.replace(/s$/i, '').trim()
            return targetForms.has(key) || (singular && targetForms.has(singular))
          })
        })
        if (existingCount) {
          countLabelEntry = {
            field: existingCount.field,
            aggregation: 'countd',
            caption: byField,
          }
        }
      }
    }
    const physical = countLabelEntry ? resolvePhysicalField(countLabelEntry.field) : resolvePhysicalField(byField)
    const agg = countLabelEntry
      ? AGG_MAP[String(countLabelEntry.aggregation ?? 'countd').trim().toLowerCase()] ?? null
      : inferSortHelperAggregation(byField)
    if (!agg) return null
    const existing = measures.find((m) =>
      m.field === physical &&
      m.agg === agg.agg &&
      Boolean(m.distinct) === Boolean(agg.distinct)
    )
    const alias = /[\[\]:]/.test(byField) ? physical : byField
    if (existing) {
      const effective = existing.alias || alias
      if (!existing.alias) existing.alias = effective
      setMeasureAlias(byField, effective)
      setMeasureAlias(physical, effective)
      setMeasureAlias(alias, effective)
      return effective
    } else {
      const helper: TypedMeasure = { field: physical, agg: agg.agg, alias }
      if (agg.distinct) helper.distinct = true
      measures.push(helper)
    }
    setMeasureAlias(byField, alias)
    setMeasureAlias(physical, alias)
    setMeasureAlias(alias, alias)
    return alias
  }
  const resolveAmbiguousComputedSortField = (
    rawField: string,
    shelfField: string | null | undefined,
  ): string | null => {
    const key = String(rawField ?? '').trim().replace(/^\[|\]$/g, '').replace(/\s+/g, ' ').toLowerCase()
    if (!new Set(['', 'calculation', 'measure', 'measure value', 'measure values']).has(key)) return null
    const shelfKey = normalizeTableauFieldKey(shelfField)
    if (shelfKey && activeWorkbookWorksheet && typeof activeWorkbookWorksheet === 'object') {
      const sortUsingCandidates: string[] = []
      const sorts = Array.isArray((activeWorkbookWorksheet as any).sorts)
        ? (activeWorkbookWorksheet as any).sorts as any[]
        : []
      for (const sort of sorts) {
        if (!sort || typeof sort !== 'object') continue
        const columnKey = normalizeTableauFieldKey((sort as any).column)
        if (columnKey !== shelfKey) continue
        const sortUsing = String((sort as any).sort_using ?? (sort as any).using ?? (sort as any).attrs?.using ?? '').trim()
        if (!sortUsing || isVirtualMnvField(sortUsing)) continue
        if (!sortUsingCandidates.includes(sortUsing)) sortUsingCandidates.push(sortUsing)
      }
      if (sortUsingCandidates.length === 1) return sortUsingCandidates[0] || null
    }
    const tooltip = (worksheetSpec as any)?.style?.tooltip
    const candidates: string[] = []
    const addCandidate = (raw: unknown): void => {
      const token = String(raw ?? '').trim()
      if (!token || isVirtualMnvField(token)) return
      const fdCol = fieldDictionaryColumnForField(token)
      if (!isMeasureLikeFieldDictionaryColumn(fdCol) && !inferSortHelperAggregation(token)) return
      const caption = String((fdCol as any)?.caption ?? '').trim()
      const candidate = caption || token
      if (candidate && !candidates.includes(candidate)) candidates.push(candidate)
    }
    const scanTemplate = (rawTemplate: unknown): void => {
      const template = String(rawTemplate ?? '')
      if (!template) return
      const tokenRe = /\{\{\s*([^}]+?)\s*\}\}|<\s*([^>]+?)\s*>/g
      let match: RegExpExecArray | null
      while ((match = tokenRe.exec(template)) !== null) {
        const rawToken = String(match[1] ?? match[2] ?? '').trim()
        if (!rawToken) continue
        const aggMatch = rawToken.match(/^[A-Za-z_][A-Za-z0-9_]*\s*\(\s*(.*?)\s*\)$/)
        addCandidate(aggMatch ? aggMatch[1] : rawToken)
      }
    }
    scanTemplate(tooltip?.template)
    scanTemplate(tooltip?.template_html)
    if (candidates.length === 1) return candidates[0] || null
    return null
  }

  const explicitDimOrderBy = new Map<string, TypedOrderBy>()
  for (const { shelf, descriptor } of pendingDimensionSorts) {
    if (descriptor.kind === 'self') {
      const ob: TypedOrderBy = { field: shelf.field_name }
      if (descriptor.descending) ob.descending = true
      explicitDimOrderBy.set(shelf.field_name, ob)
    } else if (descriptor.kind === 'custom_order') {
      const ob: TypedOrderBy = { field: shelf.field_name, customOrder: descriptor.values.slice() }
      if (descriptor.descending) ob.descending = true
      explicitDimOrderBy.set(shelf.field_name, ob)
    } else if (descriptor.kind === 'by_field') {
      const measureAlias = getMeasureAlias(descriptor.byField)
      if (measureAlias) {
        const ob: TypedOrderBy = { field: measureAlias }
        if (descriptor.descending) ob.descending = true
        explicitDimOrderBy.set(shelf.field_name, ob)
        continue
      }
      // `by_field` may also reference another dimension already in the
      // projection — e.g. "sort Region by Country". ORDER BY the named
      // dimension column directly. Any other target (dimension not in
      // projection, measure not yet projected) still rejects so we
      // never silently invent columns.
      const dimHit = dimensions.find((d) => d.field === descriptor.byField)
      if (dimHit) {
        const ob: TypedOrderBy = { field: dimHit.field }
        if (descriptor.descending) ob.descending = true
        explicitDimOrderBy.set(shelf.field_name, ob)
        continue
      }
      const sortHelperAlias = ensureSortHelperMeasure(descriptor.byField)
      if (sortHelperAlias) {
        const ob: TypedOrderBy = { field: sortHelperAlias }
        if (descriptor.descending) ob.descending = true
        explicitDimOrderBy.set(shelf.field_name, ob)
        continue
      }
      const resolvedAmbiguousSortField = resolveAmbiguousComputedSortField(
        descriptor.byField,
        shelf.field_name,
      )
      const ambiguousSortHelperAlias = resolvedAmbiguousSortField
        ? ensureSortHelperMeasure(resolvedAmbiguousSortField)
        : null
      if (ambiguousSortHelperAlias) {
        const ob: TypedOrderBy = { field: ambiguousSortHelperAlias }
        if (descriptor.descending) ob.descending = true
        explicitDimOrderBy.set(shelf.field_name, ob)
        continue
      }
      return unsupported(`shelf_sort_by_field_not_in_projection:${descriptor.byField}`)
    }
  }
  const measureOrderBy: TypedOrderBy[] = []
  for (const { shelf, descriptor } of pendingMeasureSorts) {
    if (descriptor.kind === 'custom_order') {
      // Manual sequence on a measure shelf is authoring-rare (measures
      // are numeric / computed, not categorical), and applying a CASE
      // ordinal over an aggregated numeric column is semantically
      // ambiguous. Reject explicitly rather than silently collapsing
      // to self-sort.
      return unsupported(`shelf_sort_custom_order_on_measure_not_supported:${shelf.field_name}`)
    }
    if (descriptor.kind === 'self') {
      const effectiveAlias = getMeasureAlias(shelf.field_name)!
      const ob: TypedOrderBy = { field: effectiveAlias }
      if (descriptor.descending) ob.descending = true
      measureOrderBy.push(ob)
    } else if (descriptor.kind === 'by_field') {
      // Measure shelf sorting BY another already-projected measure or
      // dimension resolves to an explicit ORDER BY on that alias/field,
      // mirroring the dimension `by_field` path above. Any target that
      // isn't already projected still rejects to avoid silently inventing
      // columns.
      const measureAlias = getMeasureAlias(descriptor.byField)
      if (measureAlias) {
        const ob: TypedOrderBy = { field: measureAlias }
        if (descriptor.descending) ob.descending = true
        measureOrderBy.push(ob)
        continue
      }
      const dimHit = dimensions.find((d) => d.field === descriptor.byField)
      if (dimHit) {
        const ob: TypedOrderBy = { field: dimHit.field }
        if (descriptor.descending) ob.descending = true
        measureOrderBy.push(ob)
        continue
      }
      const sortHelperAlias = ensureSortHelperMeasure(descriptor.byField)
      if (sortHelperAlias) {
        const ob: TypedOrderBy = { field: sortHelperAlias }
        if (descriptor.descending) ob.descending = true
        measureOrderBy.push(ob)
        continue
      }
      return unsupported(`shelf_sort_by_field_not_in_projection:${descriptor.byField}`)
    }
  }

  if (
    dimensions.length === 0 &&
    measures.length === 0 &&
    derivedMeasures.length === 0 &&
    pendingWindowMeasures.length === 0 &&
    pendingComposites.length === 0 &&
    pendingHelperWindowMeasures.length === 0
  ) {
    return unsupported('empty_typed_spec_projection')
  }
  // De-duplicate measures by (field, agg, distinct). Two measures
  // with the same field but different aggregations (e.g. SUM vs AVG)
  // are legitimate; two with identical field+agg+distinct are a
  // producer bug to surface. When the collision happens on identical
  // shape, drop the later occurrence silently so recursive synthesis
  // (derived measures pulling in leaf SUMs that the shelf loop
  // already declared) doesn't trip this check.
  {
    const seen = new Set<string>()
    const deduped: TypedMeasure[] = []
    for (const m of measures) {
      const key = `${m.field}\u241f${m.agg}\u241f${m.distinct ? 1 : 0}`
      if (seen.has(key)) continue
      seen.add(key)
      deduped.push(m)
    }
    measures.length = 0
    measures.push(...deduped)
  }

  // PR4 Workstream D step 1 seam: paging-driven projection requirement.
  // Validate every named field resolves to a column already projected
  // (dimension field, measure field_name, or measure alias). A missing
  // field is a sign the caller relied on TS post-compile column
  // stripping — explicitly reject so that path stays on ts-legacy
  // until paging is lifted into authoring or the compile-input
  // augmentation channel.
  const pagingFields = inputs.pagingProjection?.fields
  const normalizedPagingFields: string[] = []
  if (Array.isArray(pagingFields) && pagingFields.length > 0) {
    const dimensionFieldSet = new Set(dimensions.map((d) => d.field))
    const measureFieldSet = new Set(measures.map((m) => m.field))
    const measureAliasSet = new Set(measureAliasByField.values())
    for (const rawField of pagingFields) {
      const field = typeof rawField === 'string' ? rawField.trim() : ''
      if (!field) {
        return unsupported('paging_projection_field_invalid')
      }
      if (
        !dimensionFieldSet.has(field) &&
        !measureFieldSet.has(field) &&
        !measureAliasSet.has(field)
      ) {
        return unsupported(`paging_projection_field_not_in_projection:${field}`)
      }
      normalizedPagingFields.push(field)
    }
  }

  // Default order-by mirrors the legacy compiler: ascending on every
  // dimension in projection order. Explicit per-shelf sorts override
  // that default for the fields they name while unsorted dimensions
  // keep the natural ascending order. This preserves identity for
  // worksheets whose sort config is equivalent to the default.
  const orderBy: TypedOrderBy[] = dimensions.map((d) => {
    // PR7: the DEFAULT orderBy for a dim refers to its agg-base output
    // column name. For plain dims with no alias this is still the raw
    // `field`; for calc dims whose `field` is `__dim_*` this maps to
    // the dim's display `alias` so downstream windows / LODs can
    // ORDER BY the post-aggregation name. Explicit sort descriptors
    // (`sort_by_field` / `custom_order`) resolved earlier already
    // point at the correct target field/alias; pass them through
    // unchanged so their semantics are preserved.
    const outName = d.alias ?? d.field
    const explicit = explicitDimOrderBy.get(d.field) ?? explicitDimOrderBy.get(outName)
    return explicit ?? { field: outName }
  })
  for (const ob of measureOrderBy) orderBy.push(ob)

  // Validate view.tooltip template tokens against the final projection.
  // Every non-virtual token must resolve to an already-projected
  // dim / measure / alias. Tokens are matched case-insensitively
  // because IR templates frequently reference the on-disk physical
  // column name (lowercase) while the authoring spec uses the caption
  // (mixed case); both map to the same data column in the runtime.
  // Recorded tokens feed `presentation.tooltipTokens` so downstream
  // consumers know which fields the UI will surface.
  if (pendingViewTooltipTemplateTokens.length > 0) {
    const projectedLower = new Set<string>()
    for (const d of dimensions) projectedLower.add(d.field.toLowerCase())
    for (const m of measures) {
      projectedLower.add(m.field.toLowerCase())
      const alias =
        m.alias && m.alias.length > 0
          ? m.alias
          : `${m.agg.toLowerCase()}_${m.field}`
      projectedLower.add(alias.toLowerCase())
    }
    for (const token of pendingViewTooltipTemplateTokens) {
      const hit = projectedLower.has(token.toLowerCase())
      if (!hit) {
        // Token references a field that isn't projected. Record as a
        // tooltip token anyway — tooltip rendering is presentation-only
        // and the runtime falls back gracefully when a token can't be
        // resolved (legacy parity: the legacy builder also emits these
        // as free-standing SELECT columns whose aggregation wrapping
        // comes from UI defaults, not authoring). Surfacing the token
        // keeps the presentation layer informed without blocking the
        // Rust switch on every tooltip-only field drift.
      }
      if (!seenTooltipTokens.has(token)) {
        seenTooltipTokens.add(token)
        tooltipTokenFields.push(token)
      }
    }
  }

  const limitRaw = inputs.limit
  let limit: number | null = null
  if (limitRaw !== undefined && limitRaw !== null) {
    if (typeof limitRaw !== 'number' || !Number.isFinite(limitRaw) || limitRaw < 0) {
      return unsupported(`limit_not_supported:${String(limitRaw)}`)
    }
    limit = Math.floor(limitRaw)
  }

  // Materialize classified table-calc fields into TypedWindowMeasure
  // entries. INDEX() requires a non-empty ORDER BY
  // (`typed_spec.rs::requires_order_by()` returns true for Index); we
  // inherit the worksheet's `orderBy` so partition-less row ordinals
  // match the dimensional addressing Tableau Desktop uses for the
  // default "Compute using → Table" pattern.
  const windowMeasures: TypedWindowMeasure[] = []
  const cloneOrderBy = (): TypedOrderBy[] =>
    orderBy.map((o) => ({
      field: o.field,
      ...(o.descending ? { descending: true } : {}),
      ...(o.customOrder && o.customOrder.length > 0
        ? { customOrder: [...o.customOrder] }
        : {}),
    }))
  const firstMeasureOrderBy = (): TypedOrderBy | null => {
    for (const m of measures) {
      const field = (m.alias && m.alias.length > 0)
        ? m.alias
        : `${m.agg.toLowerCase()}_${m.field}`
      if (field) return { field }
    }
    return null
  }
  const cloneWindowOrderBy = (): TypedOrderBy[] => {
    const cloned = cloneOrderBy()
    if (cloned.length > 0) return cloned
    const fallback = firstMeasureOrderBy()
    return fallback ? [fallback] : []
  }
  for (const [, alias] of pendingTableCalcIndexWindowAliases) {
    const winOrder = cloneWindowOrderBy()
    if (winOrder.length === 0) {
      return unsupported(`table_calc_index_requires_order_by:${alias}`)
    }
    windowMeasures.push({
      alias,
      kind: { kind: 'INDEX' },
      orderBy: winOrder,
    })
  }
  const flushPendingHelperWindowMeasures = (): void => {
    const occupiedOutputAliases = (): Set<string> => {
      const out = new Set<string>()
      for (const d of dimensions) out.add(d.alias ?? d.field)
      for (const m of measures) out.add((m.alias && m.alias.length > 0) ? m.alias : `${m.agg.toLowerCase()}_${m.field}`)
      for (const d of derivedMeasures) out.add(d.alias)
      for (const w of windowMeasures) out.add(w.alias)
      return out
    }
    const allocateWindowAlias = (desired: string, kind: TypedWindowMeasure['kind'], measureRef: string): string => {
      const used = occupiedOutputAliases()
      if (!used.has(desired)) return desired
      const kindName = String((kind as any)?.kind ?? 'window').toLowerCase()
      const safe = `${kindName}_${measureRef || desired}`
        .replace(/[^A-Za-z0-9_]/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80)
      let candidate = `__fg_${safe || 'window_measure'}`
      let i = 2
      while (used.has(candidate)) candidate = `__fg_${safe || 'window_measure'}_${i++}`
      return candidate
    }
    for (const helper of pendingPersistedRankWindowMeasures) {
      const measureRef = ensureMeasureForClassifiedAggField(helper.agg, helper.baseField)
      pendingHelperWindowMeasures.push({
        alias: allocateWindowAlias(helper.alias, helper.kind, measureRef),
        kind: helper.kind,
        measureRef,
        partitionBy: [],
        orderByMode: 'window',
      })
    }
    for (const helper of pendingHelperWindowMeasures) {
      if (windowMeasures.some((w) => w.alias === helper.alias)) continue
      const helperOrderBy = helper.orderByMode === 'window' ? cloneWindowOrderBy() : cloneOrderBy()
      windowMeasures.push({
        alias: helper.alias,
        kind: helper.kind,
        measureRef: helper.measureRef,
        partitionBy: helper.partitionBy ?? [],
        orderBy: helperOrderBy,
      })
    }
  }
  flushPendingHelperWindowMeasures()
  for (const pwm of pendingWindowMeasures) {
    if (pwm.classified.kind === 'index') {
      const winOrder = cloneWindowOrderBy()
      if (winOrder.length === 0) {
        return unsupported(`table_calc_index_requires_order_by:${pwm.label}`)
      }
      windowMeasures.push({
        alias: pwm.alias,
        kind: { kind: 'INDEX' },
        orderBy: winOrder,
      })
      continue
    }
    if (pwm.classified.kind === 'window_agg') {
      if (orderBy.length === 0) {
        return unsupported(`table_calc_window_agg_requires_order_by:${pwm.label}`)
      }
      const resolvedWindowBase = resolveCompositeBase(pwm.classified.base)
      if (!resolvedWindowBase.ok) {
        if (resolvedWindowBase.isIndexedCalcRef) {
          const hint = resolvedWindowBase.hint ?? 'unsupported_shape'
          return unsupported(
            `table_calc_window_agg_calc_ref_non_simple_alias:${pwm.label}:${pwm.classified.base.field}:${hint}`,
          )
        }
        return unsupported(`table_calc_window_agg_bare_agg_unsupported:${pwm.label}`)
      }
      flushPendingHelperWindowMeasures()
      const windowAlias = pwm.classified.negate ? `${pwm.alias}__win` : pwm.alias
      const basePayload: Pick<TypedWindowMeasure, 'measureRef' | 'measureExpr'> | null = (() => {
        if ('kind' in resolvedWindowBase && resolvedWindowBase.kind === 'derived') {
          return { measureExpr: resolvedWindowBase.baseExpr }
        }
        const base = (resolvedWindowBase as { base: ClassifiedCalcAggregation & { kind: 'explicit' } }).base
        return resolveExplicitWindowBasePayload(base, pwm.allowSyntheticBaseMeasure)
      })()
      if (!basePayload) {
        return unsupported(
          `table_calc_window_agg_base_measure_not_authored:${pwm.label}:${pwm.classified.base.field}`,
        )
      }
      windowMeasures.push({
        alias: windowAlias,
        kind: { kind: pwm.classified.windowKind },
        ...basePayload,
        partitionBy: [],
        orderBy: cloneOrderBy(),
      })
      if (pwm.classified.negate) {
        derivedMeasures.push({
          alias: pwm.alias,
          expr: {
            kind: 'bin_op',
            op: '-',
            lhs: { kind: 'lit', value: 0 },
            rhs: { kind: 'measure_ref', field: windowAlias },
          },
        })
      }
      continue
    }
    // pwm.classified.kind === 'lookup'
    const { base: rawLookupBase, offset } = pwm.classified
    const resolvedLookupBase = resolveCompositeBase(rawLookupBase)
    if (!resolvedLookupBase.ok) {
      if (resolvedLookupBase.isIndexedCalcRef) {
        const hint = resolvedLookupBase.hint ?? 'unsupported_shape'
        return unsupported(
          `table_calc_lookup_calc_ref_non_simple_alias:${pwm.label}:${rawLookupBase.field}:${hint}`,
        )
      }
      return unsupported(`table_calc_lookup_bare_agg_unsupported:${pwm.label}`)
    }
    if ('kind' in resolvedLookupBase && resolvedLookupBase.kind === 'derived') {
      flushPendingHelperWindowMeasures()
      // Plain-LOOKUP (LAG/LEAD/FIRST/LAST) over an aggregated-ratio
      // calc-ref base. Producer feeds the full derived expression to
      // `TypedWindowMeasure.measureExpr`; the Rust emitter sees this
      // as `<window_fn>(<derived_expr>) OVER (...)` and aggregates
      // the derived base per row at view grain. Requires orderBy
      // for LAG/LEAD (FIRST/LAST tolerate an empty one).
      if (
        orderBy.length === 0 &&
        pwm.classified.offset.kind !== 'first' &&
        pwm.classified.offset.kind !== 'last'
      ) {
        return unsupported(`table_calc_lookup_requires_order_by:${pwm.label}`)
      }
      let kind:
        | { kind: 'LAG'; periods: number }
        | { kind: 'LEAD'; periods: number }
        | { kind: 'FIRST' }
        | { kind: 'LAST' }
      const off = pwm.classified.offset
      if (off.kind === 'int') {
        if (off.value === 0) {
          return unsupported(`table_calc_lookup_zero_offset_not_supported:${pwm.label}`)
        }
        kind =
          off.value > 0
            ? { kind: 'LEAD', periods: off.value }
            : { kind: 'LAG', periods: Math.abs(off.value) }
      } else if (off.kind === 'first') {
        kind = { kind: 'FIRST' }
      } else {
        kind = { kind: 'LAST' }
      }
      windowMeasures.push({
        alias: pwm.alias,
        kind,
        measureExpr: resolvedLookupBase.baseExpr,
        partitionBy: [],
        orderBy: cloneOrderBy(),
      })
      continue
    }
    const base = (resolvedLookupBase as { base: ClassifiedCalcAggregation & { kind: 'explicit' } }).base
    if (base.agg !== 'SUM') {
      // Rust/TS typed spec currently only carries SUM/AVG/MIN/MAX
      // as measure aggregations in `TypedMeasure`; LOOKUP over
      // non-SUM aggregations needs a separate mapping slice.
      return unsupported(`table_calc_lookup_base_agg_unsupported:${pwm.label}:${base.agg}`)
    }
    const basePayload = resolveExplicitSumWindowBase(
      base,
      pwm.allowSyntheticBaseMeasure,
    )?.payload
    if (!basePayload) {
      return unsupported(
        `table_calc_lookup_base_measure_not_authored:${pwm.label}:${resolvePhysicalField(base.field)}`,
      )
    }
    if (orderBy.length === 0 && offset.kind !== 'first' && offset.kind !== 'last') {
      return unsupported(`table_calc_lookup_requires_order_by:${pwm.label}`)
    }
    let kind:
      | { kind: 'LAG'; periods: number }
      | { kind: 'LEAD'; periods: number }
      | { kind: 'FIRST' }
      | { kind: 'LAST' }
    if (offset.kind === 'int') {
      if (offset.value === 0) {
        return unsupported(`table_calc_lookup_zero_offset_not_supported:${pwm.label}`)
      }
      // Tableau `LOOKUP(expr, N)` semantics: positive N looks forward
      // (`LEAD`), negative N looks backward (`LAG`). The window
      // function's `periods` is always non-negative.
      kind =
        offset.value > 0
          ? { kind: 'LEAD', periods: offset.value }
          : { kind: 'LAG', periods: Math.abs(offset.value) }
    } else if (offset.kind === 'first') {
      kind = { kind: 'FIRST' }
    } else {
      kind = { kind: 'LAST' }
    }
    windowMeasures.push({
      alias: pwm.alias,
      kind,
      ...basePayload,
      partitionBy: [],
      orderBy: cloneOrderBy(),
    })
  }

  // PR7 slice 3 producer: materialize each recognized `py_diff`
  // composite into a LAG/LEAD TypedWindowMeasure + a TypedDerivedMeasure.
  //
  // Shape A:  (ZN(SUM([m])) - LOOKUP(ZN(SUM([m])), N)) / ABS(LOOKUP(ZN(SUM([m])), N))
  //
  //   →  window  = { kind: LAG(|N|) or LEAD(N), measureRef: <base_alias>,
  //                  orderBy: <worksheet orderBy> }
  //      derived = (<base_alias> - <window_alias>) / ABS(<window_alias>)
  //
  // The base measure MUST already be authored on the worksheet (we do
  // not synthesize hidden SUM measures — that would change
  // presentation cardinality). The worksheet MUST have a non-empty
  // `orderBy` (LAG/LEAD are dimensional-ordering-sensitive and the
  // window emit path rejects without an ORDER BY). These two
  // requirements mirror the LOOKUP-classification path.
  // Resolve the authored SUM base measure for a `py_diff` or
  // `first_diff_nullable` composite and return its alias + a
  // deterministic synthesized window alias (`<calcAlias>__win`). The
  // gates below mirror LOOKUP's and are surfaced as static-prefix
  // rejection tags via `return unsupported(...)` so the
  // rejection-inventory scanner picks them up. `__win` is reserved
  // for producer-synthesized window measures.
  for (const pc of pendingComposites) {
    if (pc.pattern.kind === 'py_diff') {
      const rawBase = pc.pattern.base
      const resolvedBase = resolveCompositeBase(rawBase)
      if (!resolvedBase.ok) {
        if (resolvedBase.isIndexedCalcRef) {
          const hint = resolvedBase.hint ?? 'unsupported_shape'
          return unsupported(
            `table_calc_py_diff_calc_ref_non_simple_alias:${pc.labelSafe}:${rawBase.field}:${hint}`,
          )
        }
        return unsupported(`table_calc_py_diff_bare_agg_unsupported:${pc.labelSafe}`)
      }
      let pyBaseExpr: TypedDerivedExpr | null = null
      let pyBasePayload: Pick<TypedWindowMeasure, 'measureRef' | 'measureExpr'> | null = null
      let pyCurrentRef: TypedDerivedExpr | null = null
      if ('kind' in resolvedBase && resolvedBase.kind === 'derived') {
        pyBaseExpr = resolvedBase.baseExpr
        pyBasePayload = { measureExpr: pyBaseExpr }
        pyCurrentRef = pyBaseExpr
      } else {
        const base = (resolvedBase as { base: ClassifiedCalcAggregation & { kind: 'explicit' } })
          .base
        if (base.agg !== 'SUM') {
          return unsupported(
            `table_calc_py_diff_base_agg_unsupported:${pc.labelSafe}:${base.agg}`,
          )
        }
        const resolved = resolveExplicitSumWindowBase(base, pc.allowSyntheticBaseMeasure)
        if (!resolved) {
          return unsupported(
            `table_calc_py_diff_base_measure_not_authored:${pc.labelSafe}:${resolvePhysicalField(base.field)}`,
          )
        }
        pyBasePayload = resolved.payload
        pyCurrentRef = resolved.currentRef
      }
      if (orderBy.length === 0) {
        return unsupported(`table_calc_py_diff_requires_order_by:${pc.labelSafe}`)
      }
      const windowAlias = `${pc.alias}__win`
      const n = pc.pattern.offset
      const windowKind: { kind: 'LAG'; periods: number } | { kind: 'LEAD'; periods: number } =
        n > 0 ? { kind: 'LEAD', periods: n } : { kind: 'LAG', periods: Math.abs(n) }
      windowMeasures.push({
        alias: windowAlias,
        kind: windowKind,
        ...pyBasePayload!,
        partitionBy: [],
        orderBy: cloneOrderBy(),
      })
      // Current-row base reference: either a measure_ref for authored
      // SUM bases, or the derived expression itself (which the outer
      // DerivedProject re-evaluates row by row over the agg-base).
      const currentRef: TypedDerivedExpr = pyCurrentRef!
      const expr: TypedDerivedExpr = {
        kind: 'bin_op',
        op: '/',
        lhs: {
          kind: 'bin_op',
          op: '-',
          lhs: currentRef,
          rhs: { kind: 'measure_ref', field: windowAlias },
        },
        rhs: {
          kind: 'abs',
          inner: { kind: 'measure_ref', field: windowAlias },
        },
      }
      derivedMeasures.push({ alias: pc.alias, expr })
      continue
    }
    if (pc.pattern.kind === 'first_diff_nullable') {
      const rawBase = pc.pattern.base
      const resolvedBase = resolveCompositeBase(rawBase)
      if (!resolvedBase.ok) {
        if (resolvedBase.isIndexedCalcRef) {
          const hint = resolvedBase.hint ?? 'unsupported_shape'
          return unsupported(
            `table_calc_first_diff_nullable_calc_ref_non_simple_alias:${pc.labelSafe}:${rawBase.field}:${hint}`,
          )
        }
        return unsupported(
          `table_calc_first_diff_nullable_bare_agg_unsupported:${pc.labelSafe}`,
        )
      }
      let baseExpr: TypedDerivedExpr | null = null
      let basePayload: Pick<TypedWindowMeasure, 'measureRef' | 'measureExpr'> | null = null
      let baseRef: TypedDerivedExpr | null = null
      if ('kind' in resolvedBase && resolvedBase.kind === 'derived') {
        baseExpr = resolvedBase.baseExpr
        basePayload = { measureExpr: baseExpr }
        baseRef = baseExpr
      } else {
        const base = (resolvedBase as { base: ClassifiedCalcAggregation & { kind: 'explicit' } }).base
        if (base.agg !== 'SUM') {
          return unsupported(
            `table_calc_first_diff_nullable_base_agg_unsupported:${pc.labelSafe}:${base.agg}`,
          )
        }
        const resolved = resolveExplicitSumWindowBase(base, pc.allowSyntheticBaseMeasure)
        if (!resolved) {
          return unsupported(
            `table_calc_first_diff_nullable_base_measure_not_authored:${pc.labelSafe}:${resolvePhysicalField(base.field)}`,
          )
        }
        basePayload = resolved.payload
        baseRef = resolved.currentRef
      }
      if (orderBy.length === 0) {
        return unsupported(
          `table_calc_first_diff_nullable_requires_order_by:${pc.labelSafe}`,
        )
      }
      const windowAlias = `${pc.alias}__win`
      // Shape B:
      //   iif((LOOKUP(ZN(SUM([m])), FIRST()) / ZN(SUM([m]))) - 1 = 0,
      //       null,
      //       (LOOKUP(ZN(SUM([m])), FIRST()) / ZN(SUM([m]))) - 1)
      //
      // Lowering:
      //   window   = FIRST_VALUE(base) OVER (ORDER BY <ws orderBy>)
      //   ratio    = COALESCE(window, 0) / COALESCE(base, 0)
      //   ratio_m1 = ratio - 1
      //   derived  = CASE WHEN ratio_m1 = 0 THEN NULL ELSE ratio_m1 END
      //
      // ZN(X) = COALESCE(X, 0). Since FIRST_VALUE is deterministic and
      // reads a single row's value, `COALESCE(FIRST_VALUE(expr), 0)`
      // is equivalent to `FIRST_VALUE(COALESCE(expr, 0))` — so
      // wrapping at the derived layer is byte-stable against
      // Tableau's inside-LOOKUP ZN.
      windowMeasures.push({
        alias: windowAlias,
        kind: { kind: 'FIRST_VALUE' },
        ...basePayload!,
        partitionBy: [],
        orderBy: cloneOrderBy(),
      })
      const znWindow: TypedDerivedExpr = {
        kind: 'coalesce',
        inner: { kind: 'measure_ref', field: windowAlias },
        fallback: { kind: 'lit', value: 0 },
      }
      const znBase: TypedDerivedExpr = {
        kind: 'coalesce',
        inner: baseRef!,
        fallback: { kind: 'lit', value: 0 },
      }
      const ratioMinusOne: TypedDerivedExpr = {
        kind: 'bin_op',
        op: '-',
        lhs: {
          kind: 'bin_op',
          op: '/',
          lhs: znWindow,
          rhs: znBase,
        },
        rhs: { kind: 'lit', value: 1 },
      }
      derivedMeasures.push({
        alias: pc.alias,
        expr: {
          kind: 'iif_zero_then_null',
          cond: ratioMinusOne,
          body: ratioMinusOne,
        },
      })
      continue
    }
    if (pc.pattern.kind === 'first_diff_nullable_abs_capped') {
      const rawBase = pc.pattern.base
      const resolvedBase = resolveCompositeBase(rawBase)
      if (!resolvedBase.ok) {
        if (resolvedBase.isIndexedCalcRef) {
          const hint = resolvedBase.hint ?? 'unsupported_shape'
          return unsupported(
            `table_calc_first_diff_nullable_abs_capped_calc_ref_non_simple_alias:${pc.labelSafe}:${rawBase.field}:${hint}`,
          )
        }
        return unsupported(
          `table_calc_first_diff_nullable_abs_capped_bare_agg_unsupported:${pc.labelSafe}`,
        )
      }
      let baseExpr: TypedDerivedExpr | null = null
      let basePayload: Pick<TypedWindowMeasure, 'measureRef' | 'measureExpr'> | null = null
      let baseRef: TypedDerivedExpr | null = null
      if ('kind' in resolvedBase && resolvedBase.kind === 'derived') {
        baseExpr = resolvedBase.baseExpr
        basePayload = { measureExpr: baseExpr }
        baseRef = baseExpr
      } else {
        const base = (resolvedBase as { base: ClassifiedCalcAggregation & { kind: 'explicit' } }).base
        if (base.agg !== 'SUM') {
          return unsupported(
            `table_calc_first_diff_nullable_abs_capped_base_agg_unsupported:${pc.labelSafe}:${base.agg}`,
          )
        }
        const resolved = resolveExplicitSumWindowBase(base, pc.allowSyntheticBaseMeasure)
        if (!resolved) {
          return unsupported(
            `table_calc_first_diff_nullable_abs_capped_base_measure_not_authored:${pc.labelSafe}:${resolvePhysicalField(base.field)}`,
          )
        }
        basePayload = resolved.payload
        baseRef = resolved.currentRef
      }
      if (orderBy.length === 0) {
        return unsupported(
          `table_calc_first_diff_nullable_abs_capped_requires_order_by:${pc.labelSafe}`,
        )
      }
      const cap = pc.pattern.absCap
      if (!Number.isFinite(cap) || !Number.isInteger(cap) || cap <= 0) {
        return unsupported(
          `table_calc_first_diff_nullable_abs_capped_non_integer_cap:${pc.labelSafe}:${cap}`,
        )
      }
      const windowAlias = `${pc.alias}__win`
      // Shape B capped:
      //   iif(ratio-1 = 0, null,
      //       iif(abs(ratio-1) > cap, null, ratio-1))
      //
      //   →  window   = FIRST_VALUE(base)
      //      ratio_m1 = COALESCE(window,0) / COALESCE(base,0) - 1
      //      inner    = CASE WHEN ABS(ratio_m1) > cap THEN NULL ELSE ratio_m1 END
      //      derived  = CASE WHEN ratio_m1 = 0 THEN NULL ELSE inner END
      windowMeasures.push({
        alias: windowAlias,
        kind: { kind: 'FIRST_VALUE' },
        ...basePayload!,
        partitionBy: [],
        orderBy: cloneOrderBy(),
      })
      const znWindow: TypedDerivedExpr = {
        kind: 'coalesce',
        inner: { kind: 'measure_ref', field: windowAlias },
        fallback: { kind: 'lit', value: 0 },
      }
      const znBase: TypedDerivedExpr = {
        kind: 'coalesce',
        inner: baseRef!,
        fallback: { kind: 'lit', value: 0 },
      }
      const ratioMinusOne: TypedDerivedExpr = {
        kind: 'bin_op',
        op: '-',
        lhs: {
          kind: 'bin_op',
          op: '/',
          lhs: znWindow,
          rhs: znBase,
        },
        rhs: { kind: 'lit', value: 1 },
      }
      const capped: TypedDerivedExpr = {
        kind: 'iif_abs_gt_cap_then_null',
        inner: ratioMinusOne,
        cap,
        body: ratioMinusOne,
      }
      derivedMeasures.push({
        alias: pc.alias,
        expr: {
          kind: 'iif_zero_then_null',
          cond: ratioMinusOne,
          body: capped,
        },
      })
      continue
    }
  }

  const typedParameters: any[] = []
  const wbParams = (workbookIr as any)?.parameters
  if (Array.isArray(wbParams)) {
    const referenced = new Set<string>()
    function collectParamRefs(n: any): void {
      if (!n || typeof n !== 'object') return
      if (n.kind === 'param_ref' && typeof n.name === 'string') referenced.add(n.name)
      for (const k of Object.keys(n)) collectParamRefs((n as any)[k])
    }
    collectParamRefs(synthesizedCalcFields)
    for (const p of wbParams) {
      const name = typeof p?.name === 'string' ? p.name : ''
      if (!name || !referenced.has(name)) continue
      const dt = String(p?.data_type ?? p?.type ?? 'string').toLowerCase()
      let ty: string = 'string'
      if (dt === 'integer' || dt === 'int') ty = 'integer'
      else if (dt === 'real' || dt === 'float' || dt === 'double') ty = 'float'
      else if (dt === 'bool' || dt === 'boolean') ty = 'bool'
      else if (dt === 'date') ty = 'date'
      else if (dt === 'datetime' || dt === 'timestamp') ty = 'timestamp'
      const rawDefault = p?.default_value ?? p?.current_value ?? null
      let defaultLit: any = { kind: 'null' }
      if (rawDefault !== null && rawDefault !== undefined) {
        if (ty === 'integer') {
          const n = Number(String(rawDefault).replace(/[^-\d]/g, ''))
          defaultLit = Number.isFinite(n) ? { kind: 'integer', value: n } : { kind: 'null' }
        } else if (ty === 'float') {
          const n = Number(String(rawDefault).replace(/[^-\d.]/g, ''))
          defaultLit = Number.isFinite(n) ? { kind: 'float', value: n } : { kind: 'null' }
        } else if (ty === 'bool') {
          defaultLit = { kind: 'bool', value: Boolean(rawDefault) }
        } else if (ty === 'date' || ty === 'timestamp') {
          const s = String(rawDefault).replace(/^#|#$/g, '').trim()
          defaultLit = { kind: 'string', value: s }
        } else {
          defaultLit = { kind: 'string', value: String(rawDefault) }
        }
      }
      typedParameters.push({ name, ty, default: defaultLit })
    }
  }
  // PR7 MNV pivot wire-up: when `crosstab.measureValues` drove the
  // projection, fold the unfolded measures into a single
  // (`Measure Names`, `Measure Values`) pair via `measurePivot`. This
  // matches Tableau Desktop's rendering where the N measure columns
  // are melted into two virtual columns. The label is the author's
  // caption (what they wrote in `measureValues`); the `measureRef` is
  // the effective alias the measure will project under.
  //
  // Deduplicate labels: Tableau rejects two identical Measure Names
  // rows, and the typed spec validator enforces unique labels. If the
  // author somehow duplicates an MV entry, we keep the first binding
  // and skip the rest.
  let measurePivot: TypedMeasurePivot | null = null
  if (mnvPivotBindings.length > 0) {
    const entries: TypedMeasurePivotEntry[] = []
    const seenLabels = new Set<string>()
    for (const b of mnvPivotBindings) {
      let alias: string
      if (b.kind === 'measure') {
        alias =
          (b.measure.alias && b.measure.alias.length > 0)
            ? b.measure.alias
            : `${String(b.measure.agg).toLowerCase()}_${b.measure.field}`
      } else if (b.kind === 'derived') {
        alias = b.alias
      } else {
        alias = b.record.__mnvAlias || b.record.fieldName || b.record.label
      }
      if (!alias) continue
      if (seenLabels.has(b.label)) continue
      seenLabels.add(b.label)
      entries.push({ measureRef: alias, label: b.label })
    }
    if (entries.length > 0) {
      measurePivot = {
        nameColumn: 'Measure Names',
        valueColumn: 'Measure Values',
        entries,
      }
      // Thread `Measure Names` custom sort into the wrapper ORDER BY so
      // the crosstab export lays the melt entries out in the author's
      // configured order (Tableau Desktop honors `measureNamesCustomOrder`
      // as an explicit sort, not an alphabetical default). The
      // `measurePivot.entries` list is already author-ordered — derive
      // the custom_order from the entry labels to stay consistent even
      // when `measureNamesCustomOrder` is stale (and silently omits
      // recently-added measureValues entries).
      // When the author configured a `Measure Names` custom sort, use
      // that as the canonical pivot label order (Tableau Desktop's
      // crosstab export honors it verbatim). Entries missing from the
      // custom order are appended in shelf-iteration order so new
      // measures added post-custom-order don't silently disappear from
      // the sort key.
      // Tableau Desktop's crosstab-export direction for the `Measure
      // Names` sort depends on the column-axis shape:
      //   - When `cols` contains ONLY the virtual `Measure Names` pill
      //     (i.e. no real column dimensions alongside it), Tableau
      //     exports the pivot entries in REVERSE of the authored
      //     `manual_order`. Empirically confirmed against the HK
      //     `Monthly Trend` worksheet: the authored order ends with
      //     `Product Count`, but the CSV lists `Product Count` first.
      //   - When `cols` contains additional dimensions beside
      //     `Measure Names` (e.g. `Time Period` trees on the
      //     `*-comparison` worksheets), the pivot entries export in
      //     the authored forward order.
      // This is a presentation-layer quirk of the CSV export path that
      // is not otherwise recoverable from the workbook IR.
      // `colShelfFieldNames` is populated above as the set of
      // non-virtual column-axis shelf fields — an empty set means
      // Measure Names is the sole col dim.
      const reverseMeasureNamesOrder = colShelfFieldNames.size === 0
      const customOrderIdx = new Map<string, number>()
      const orderedLabels = reverseMeasureNamesOrder
        ? [...measureNamesCustomOrder].reverse()
        : measureNamesCustomOrder
      orderedLabels.forEach((label, i) => {
        if (!customOrderIdx.has(label)) customOrderIdx.set(label, i)
      })
      const pivotLabelOrder: string[] = [...entries]
        .map((e, i) => ({
          label: e.label,
          co: customOrderIdx.has(e.label)
            ? (customOrderIdx.get(e.label) as number)
            : Number.MAX_SAFE_INTEGER,
          shelfIdx: i,
        }))
        .sort((a, b) => (a.co !== b.co ? a.co - b.co : a.shelfIdx - b.shelfIdx))
        .map((x) => x.label)
      if (pivotLabelOrder.length > 0) {
        // Tableau Desktop's crosstab export treats column-axis fields
        // (incl. `Measure Names`) as the outermost sort keys so that
        // each pivot entry groups its row dims together. Prepend the
        // `Measure Names` sort so downstream ORDER BY mirrors that
        // layout instead of the author's incidental shelf order.
        orderBy.unshift({
          field: 'Measure Names',
          customOrder: pivotLabelOrder,
        })
      }
    }
  }

  const typedSpec: TypedWorksheetSpec = {
    source: baseFromSql
      ? { kind: 'subquery', sql: baseFromSql }
      : { kind: 'parquet_uri', uri: String(extractUri ?? '') },
    dimensions,
    measures,
    orderBy,
    ...(typedParameters.length > 0 ? { parameters: typedParameters as any } : {}),
    ...(synthesizedCalcFields.length > 0
      ? { calculatedFields: synthesizedCalcFields }
      : {}),
    ...(windowMeasures.length > 0 ? { windowMeasures } : {}),
    ...(derivedMeasures.length > 0 ? { derivedMeasures } : {}),
    ...(measurePivot ? { measurePivot } : {}),
    ...(densifyColDimAliases.length > 0 && dimensions.length > densifyColDimAliases.length
      ? {
          densify: {
            dimensions: densifyColDimAliases.map((alias) => ({
              alias,
              domain: { kind: 'from_agg_base' as const },
            })),
          },
        }
      : {}),
    ...(fixedLods.length > 0 ? { fixedLods } : {}),
    ...(loweredFilters.length > 0 || loweredRuntimeFilters.length > 0
      ? { filters: [...loweredFilters, ...loweredRuntimeFilters] }
      : {}),
    ...(limit !== null ? { limit } : {}),
    ...(loweredTotals && windowMeasures.length === 0
      ? { totals: loweredTotals }
      : {}),
    ...(normalizedPagingFields.length > 0 || tooltipTokenFields.length > 0
      ? {
          presentation: {
            ...(normalizedPagingFields.length > 0
              ? { pagingProjection: { fields: normalizedPagingFields } }
              : {}),
            ...(tooltipTokenFields.length > 0
              ? { tooltipTokens: { fields: tooltipTokenFields } }
              : {}),
          },
        }
      : {}),
  }
  return { typedSpec, reason: null }
}

type TotalsLoweringOutcome =
  | { ok: true; totals: TypedTotals | null }
  | { ok: false; reason: string }

/**
 * Authored crosstab totals -> TypedTotals lowering. Narrow first gate:
 * only accepts `includeGrandTotal` + the `rowSubtotals` prefix array
 * with a simple boolean contract. Anything richer (column subtotals,
 * per-dimension subtotal toggles on measure-shelf dimensions, the
 * legacy view-level totals mirror) rejects with a stable reason.
 */
function tryLowerAuthoredTotals(totals: any): TotalsLoweringOutcome {
  if (totals === null || totals === undefined) return { ok: true, totals: null }
  if (typeof totals !== 'object') {
    return { ok: false, reason: 'totals_shape_invalid' }
  }
  const allowed = new Set(['rowSubtotals', 'includeGrandTotal'])
  for (const key of Object.keys(totals)) {
    if (!allowed.has(key)) {
      return { ok: false, reason: `totals_field_not_yet_typed:${key}` }
    }
  }
  const out: TypedTotals = {}
  if (typeof totals.includeGrandTotal === 'boolean' && totals.includeGrandTotal) {
    out.grandTotal = true
  } else if (
    totals.includeGrandTotal !== undefined &&
    typeof totals.includeGrandTotal !== 'boolean'
  ) {
    return { ok: false, reason: 'totals_grand_total_value_invalid' }
  }
  if (Array.isArray(totals.rowSubtotals)) {
    const prefixes: number[] = []
    for (let i = 0; i < totals.rowSubtotals.length; i++) {
      const flag = totals.rowSubtotals[i]
      if (typeof flag !== 'boolean') {
        return { ok: false, reason: 'totals_row_subtotals_value_invalid' }
      }
      if (flag) prefixes.push(i + 1)
    }
    if (prefixes.length > 0) out.subtotalPrefixes = prefixes
  } else if (totals.rowSubtotals !== undefined) {
    return { ok: false, reason: 'totals_row_subtotals_shape_invalid' }
  }
  if (out.grandTotal === undefined && out.subtotalPrefixes === undefined) {
    return { ok: true, totals: null }
  }
  return { ok: true, totals: out }
}

type FilterLoweringOutcome =
  | { ok: true; filters: TypedFilter[] }
  | { ok: false; reason: string }

type RangeEndpoint = string | number

type RangeEndpointsOutcome =
  | { ok: true; min: RangeEndpoint | null; max: RangeEndpoint | null }
  | { ok: false; reason: string }

function isRangeEndpoint(value: unknown): value is RangeEndpoint {
  return typeof value === 'number' || typeof value === 'string'
}

function normalizeRangeEndpoints(
  value: unknown,
  reasons: {
    shapeInvalid: string
    empty: string
    minNotScalar: string
    maxNotScalar: string
  },
  options: { allowSingleItemArray?: boolean } = {},
): RangeEndpointsOutcome {
  let min: unknown
  let max: unknown

  if (Array.isArray(value)) {
    if (
      value.length === 0 ||
      value.length > 2 ||
      (value.length === 1 && options.allowSingleItemArray !== true)
    ) {
      return { ok: false, reason: reasons.shapeInvalid }
    }
    ;[min, max] = value.length === 1 ? [value[0], null] : value
  } else if (value !== null && typeof value === 'object') {
    ;({ min, max } = value as { min?: unknown; max?: unknown })
  } else {
    return { ok: false, reason: reasons.shapeInvalid }
  }

  const hasMin = min !== undefined && min !== null
  const hasMax = max !== undefined && max !== null
  if (!hasMin && !hasMax) {
    return { ok: false, reason: reasons.empty }
  }
  if (hasMin && !isRangeEndpoint(min)) {
    return { ok: false, reason: reasons.minNotScalar }
  }
  if (hasMax && !isRangeEndpoint(max)) {
    return { ok: false, reason: reasons.maxNotScalar }
  }
  return {
    ok: true,
    min: hasMin ? min : null,
    max: hasMax ? max : null,
  }
}

function lowerRangeEndpointsToTypedFilters(
  field: string,
  endpoints: Extract<RangeEndpointsOutcome, { ok: true }>,
  options: { hideDisplay?: boolean } = {},
): TypedFilter[] {
  const filters: TypedFilter[] = []
  if (endpoints.min !== null) {
    const f: TypedFilter = {
      field,
      operator: 'greater_than_or_equal',
      value: endpoints.min,
    }
    if (options.hideDisplay) f.hideDisplay = true
    filters.push(f)
  }
  if (endpoints.max !== null) {
    const f: TypedFilter = {
      field,
      operator: 'less_than_or_equal',
      value: endpoints.max,
    }
    if (options.hideDisplay) f.hideDisplay = true
    filters.push(f)
  }
  return filters
}

/**
 * Authored filter -> typed filter lowering. Narrow on purpose: the
 * initial gate handles categorical equality / in-list semantics on
 * dimension fields, including legacy Tableau shapes that put `IN` /
 * `NOT IN` in the `type` slot, plus `quantitative` filters with a
 * `{ min?, max? }` scalar range value (lowered to two TypedFilters
 * joined by AND — which is the semantics the typed compiler already
 * emits for a filter list). Hide-display filters carry the PR4
 * explicit contract (`hideDisplay: true`) so they participate in
 * identity but never reach SQL. Everything else rejects with a stable
 * diagnostic reason.
 */
function tryLowerAuthoredFilter(filter: FilterSpec | any): FilterLoweringOutcome {
  if (!filter || typeof filter !== 'object') {
    return { ok: false, reason: 'filter_shape_invalid' }
  }
  const field = String((filter as any).field_name ?? '').trim()
  if (!field) return { ok: false, reason: 'filter_missing_field_name' }

  const hideDisplay = isHideDisplayFilter(filter)
  // Hide-display measure filters are presentation-only (elided from
  // SQL by the typed compiler) — accept them with a placeholder
  // predicate rather than rejecting on `is_measure`.
  if ((filter as any).is_measure === true) {
    if (hideDisplay) {
      return {
        ok: true,
        filters: [{ field, operator: 'equals', value: null, hideDisplay: true }],
      }
    }
    return { ok: false, reason: 'measure_filter_not_yet_typed' }
  }
  const normalizeFilterToken = (raw: unknown): string =>
    String(raw ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
  const typeToken = normalizeFilterToken((filter as any).type)
  const filterTypeToken = normalizeFilterToken((filter as any).filter_type ?? (filter as any).type)
  const typeCarriesOperator =
    typeToken === 'in' ||
    typeToken === 'not_in' ||
    typeToken === 'include' ||
    typeToken === 'exclude' ||
    typeToken === 'equals' ||
    typeToken === 'not_equals'
  const filterType = typeCarriesOperator && filterTypeToken === typeToken
    ? 'categorical'
    : filterTypeToken
  const value = (filter as any).value

  if (filterType === 'quantitative') {
    // Explicit single-endpoint operator form: `{ operator: 'gte',
    // value: 10 }`. Used by tooltip command buttons / actions, which
    // don't know about min/max. Map the short operator strings to the
    // typed compiler's full names.
    const explicitOperator = String((filter as any).operator ?? '').trim().toLowerCase()
    if (value === null || value === undefined) {
      return { ok: true, filters: [] }
    }
    const OPERATOR_ALIASES: Record<string, TypedFilterOperator> = {
      gt: 'greater_than',
      greater_than: 'greater_than',
      gte: 'greater_than_or_equal',
      ge: 'greater_than_or_equal',
      greater_than_or_equal: 'greater_than_or_equal',
      lt: 'less_than',
      less_than: 'less_than',
      lte: 'less_than_or_equal',
      le: 'less_than_or_equal',
      less_than_or_equal: 'less_than_or_equal',
      eq: 'equals',
      equals: 'equals',
      '=': 'equals',
      ne: 'not_equals',
      not_equals: 'not_equals',
      '!=': 'not_equals',
      '<>': 'not_equals',
    }
    if (explicitOperator === 'between' || explicitOperator === 'range') {
      const endpoints = normalizeRangeEndpoints(value, {
        shapeInvalid: 'quantitative_filter_value_shape_invalid',
        empty: 'quantitative_filter_empty_range',
        minNotScalar: 'quantitative_filter_min_not_scalar',
        maxNotScalar: 'quantitative_filter_max_not_scalar',
      })
      if (!endpoints.ok) return endpoints
      return {
        ok: true,
        filters: lowerRangeEndpointsToTypedFilters(field, endpoints, { hideDisplay }),
      }
    }
    if (explicitOperator && explicitOperator in OPERATOR_ALIASES) {
      if (typeof value !== 'number' && typeof value !== 'string') {
        return { ok: false, reason: 'quantitative_filter_value_not_scalar' }
      }
      const typed: TypedFilter = {
        field,
        operator: OPERATOR_ALIASES[explicitOperator],
        value,
      }
      if (hideDisplay) typed.hideDisplay = true
      return { ok: true, filters: [typed] }
    }
    if (explicitOperator) {
      return { ok: false, reason: `quantitative_filter_operator_not_yet_typed:${explicitOperator}` }
    }
    // Range-literal form: `{ min?, max? }`. Rejects any operator
    // override above — the typed compiler emits AND of two filters.
    const endpoints = normalizeRangeEndpoints(value, {
      shapeInvalid: 'quantitative_filter_value_shape_invalid',
      empty: 'quantitative_filter_empty_range',
      minNotScalar: 'quantitative_filter_min_not_scalar',
      maxNotScalar: 'quantitative_filter_max_not_scalar',
    })
    if (!endpoints.ok) return endpoints
    return { ok: true, filters: lowerRangeEndpointsToTypedFilters(field, endpoints, { hideDisplay }) }
  }

  if (filterType && filterType !== 'categorical') {
    // Hide-display filters never contribute SQL (the typed compiler
    // elides them via `hideDisplay: true`), so their `type` shape is
    // immaterial for compilation — `relative_date` and other
    // non-categorical hide filters are pure presentation artifacts.
    // Accept them with a placeholder predicate so they still
    // participate in switch-readiness identity without tripping the
    // narrower SQL-shape gates.
    if (hideDisplay) {
      return {
        ok: true,
        filters: [{ field, operator: 'equals', value: null, hideDisplay: true }],
      }
    }
    return { ok: false, reason: `filter_type_not_yet_typed:${filterType}` }
  }

  // Normalize operator tokens to a canonical underscore form: both the
  // authoring IR (emits `"not in"`, `"is null"`) and programmatic call
  // sites (emit `"not_in"`, `"is_null"`) land on the same matcher
  // without silently accepting unknown shapes.
  const rawOperator = normalizeFilterToken((filter as any).operator ?? (typeCarriesOperator ? typeToken : ''))
  const normalizeCategoricalValue = (raw: unknown): unknown => {
    if (typeof raw !== 'string') return raw
    const trimmed = raw.trim()
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) return raw
    try {
      const parsed = JSON.parse(trimmed)
      if (!Array.isArray(parsed)) return raw
      const scalar = parsed.every((v) =>
        v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
      )
      return scalar ? parsed : raw
    } catch {
      return raw
    }
  }
  const categoricalValue = normalizeCategoricalValue(value)
  const isArrayValue = Array.isArray(categoricalValue)

  let operator: TypedFilterOperator
  if (
    rawOperator === '' ||
    rawOperator === 'in' ||
    rawOperator === 'include' ||
    rawOperator === 'equals' ||
    rawOperator === 'eq' ||
    rawOperator === '='
  ) {
    if (categoricalValue === null || categoricalValue === undefined) {
      return { ok: true, filters: [] }
    }
    operator = isArrayValue ? 'in_list' : 'equals'
  } else if (
    rawOperator === 'not_in' ||
    rawOperator === 'exclude' ||
    rawOperator === 'not_equals' ||
    rawOperator === 'ne' ||
    rawOperator === '!=' ||
    rawOperator === '<>'
  ) {
    if (categoricalValue === null || categoricalValue === undefined) {
      return { ok: true, filters: [] }
    }
    operator = isArrayValue ? 'not_in_list' : 'not_equals'
  } else if (rawOperator === 'is_null') {
    const typed: TypedFilter = { field, operator: 'is_null', value: null }
    if (hideDisplay) typed.hideDisplay = true
    return { ok: true, filters: [typed] }
  } else if (rawOperator === 'is_not_null') {
    const typed: TypedFilter = { field, operator: 'is_not_null', value: null }
    if (hideDisplay) typed.hideDisplay = true
    return { ok: true, filters: [typed] }
  } else {
    return { ok: false, reason: `filter_operator_not_yet_typed:${rawOperator}` }
  }

  if (isArrayValue) {
    for (const v of categoricalValue as unknown[]) {
      if (v !== null && typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean') {
        return { ok: false, reason: 'filter_in_list_value_not_scalar' }
      }
    }
  } else if (
    categoricalValue !== null &&
    typeof categoricalValue !== 'string' &&
    typeof categoricalValue !== 'number' &&
    typeof categoricalValue !== 'boolean'
  ) {
    return { ok: false, reason: 'filter_value_not_scalar' }
  }

  // A categorical equality/in-list filter with a null/empty value is
  // Tableau's "no selection" shape (include all rows); `= NULL` SQL
  // never matches, so elide these rather than emitting a broken
  // predicate. Only `is_null` / `is_not_null` operators use null as a
  // real value.
  if (
    (operator === 'equals' || operator === 'not_equals') &&
    categoricalValue === null
  ) {
    return { ok: true, filters: [] }
  }
  if ((operator === 'in_list' || operator === 'not_in_list') && isArrayValue && (categoricalValue as unknown[]).length === 0) {
    return { ok: true, filters: [] }
  }

  // Tableau uses the literal sentinel `%null%` (case-insensitive) inside
  // categorical filter value lists to represent NULL. Pass-through as a
  // string literal causes SQL conversion errors against non-string columns
  // (dates, numerics). Normalize: strip %null% entries, add an IS NULL or
  // IS NOT NULL clause as needed, and elide if the remainder is empty.
  if ((operator === 'in_list' || operator === 'not_in_list') && isArrayValue) {
    const isNullSentinel = (v: unknown): boolean =>
      typeof v === 'string' && v.trim().replace(/^['"]|['"]$/g, '').toLowerCase() === '%null%'
    const arr = categoricalValue as unknown[]
    const hadNull = arr.some(isNullSentinel)
    if (hadNull) {
      const filtered = arr.filter((v) => !isNullSentinel(v))
      const out: TypedFilter[] = []
      const nullOp = operator === 'in_list' ? 'is_null' : 'is_not_null'
      const nullFilter: TypedFilter = { field, operator: nullOp, value: null }
      if (hideDisplay) nullFilter.hideDisplay = true
      out.push(nullFilter)
      if (filtered.length > 0) {
        const listFilter: TypedFilter = { field, operator, value: filtered }
        if (hideDisplay) listFilter.hideDisplay = true
        out.push(listFilter)
      }
      return { ok: true, filters: out }
    }
  }

  const typed: TypedFilter = { field, operator, value: categoricalValue }
  if (hideDisplay) typed.hideDisplay = true
  return { ok: true, filters: [typed] }
}

/**
 * Request-layer runtime filter -> typed filter lowering. These arrive
 * on `NormalizedWorksheetRequest.filters` as the merged base + override
 * set produced by `buildNormalizedWorksheetRequest`. The Rust compiler
 * already consumes runtime filters separately via `resolvedActionState`
 * (PR3), but threading them into the typed spec directly keeps a
 * single source of truth: the typed spec becomes the complete query
 * description Rust compiles, with no hidden out-of-band state.
 */
function tryLowerRuntimeFilter(rf: any): FilterLoweringOutcome {
  if (!rf || typeof rf !== 'object') {
    return { ok: false, reason: 'runtime_filter_shape_invalid' }
  }
  const field = String(rf.display_name ?? rf.field_name ?? '').trim()
  if (!field) return { ok: false, reason: 'runtime_filter_missing_field_name' }
  if (rf.is_measure === true) {
    return { ok: false, reason: 'runtime_measure_filter_not_yet_typed' }
  }

  // `explicit_empty: true` is the normalized runtime signal for
  // "exclude all rows" — the dashboard action layer produces it when a
  // filter resolves to the empty selection. Plan PR3 mandates this
  // maps to `WHERE FALSE` at the Rust level. The typed compiler renders
  // `in_list` with an empty value array as `1 = 0`, which is exactly
  // that. Emitting it through the typed spec keeps the single source
  // of truth on the lowering, not on a separate runtime override.
  if (rf.explicit_empty === true) {
    return {
      ok: true,
      filters: [{ field, operator: 'in_list', value: [] }],
    }
  }

  // Normalize operator tokens to a canonical underscore form so that
  // runtime filters emitted by the authoring IR (e.g. `"not in"`,
  // `"is null"`) land on the same matcher as programmatic call sites
  // (`"not_in"`, `"is_null"`). This mirrors the authored-filter path in
  // `tryLowerAuthoredFilter`: keeping the two normalizers in sync
  // prevents runtime-only shapes from forcing ts-legacy fallback for
  // worksheets whose authored filters would otherwise lower cleanly.
  const operatorRaw = String(rf.operator ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')

  // `kind: 'range'` runtime filters carry `value: [min, max]` (or a
  // single-sided range with null at the open end). Some dashboard paths
  // preserve Tableau's explicit `between` operator instead of setting
  // `kind`; lower both forms to the same paired gte/lte TypedFilters.
  const kindRaw = String(rf.kind ?? '').trim().toLowerCase()
  if (kindRaw === 'range' || operatorRaw === 'between' || operatorRaw === 'range') {
    const endpoints = normalizeRangeEndpoints(
      rf.value,
      {
        shapeInvalid: 'runtime_range_filter_value_shape_invalid',
        empty: 'runtime_range_filter_empty',
        minNotScalar: 'runtime_range_filter_min_not_scalar',
        maxNotScalar: 'runtime_range_filter_max_not_scalar',
      },
      { allowSingleItemArray: true },
    )
    if (!endpoints.ok) return endpoints
    return { ok: true, filters: lowerRangeEndpointsToTypedFilters(field, endpoints) }
  }

  const value = rf.value
  const isArrayValue = Array.isArray(value)

  let operator: TypedFilterOperator
  if (operatorRaw === 'in' || operatorRaw === '' || operatorRaw === 'include') {
    operator = isArrayValue ? 'in_list' : 'equals'
  } else if (operatorRaw === 'not_in' || operatorRaw === 'exclude' || operatorRaw === 'not_equals') {
    operator = isArrayValue ? 'not_in_list' : 'not_equals'
  } else if (operatorRaw === 'equals' || operatorRaw === 'eq' || operatorRaw === '=') {
    operator = 'equals'
  } else if (operatorRaw === 'ne' || operatorRaw === '!=' || operatorRaw === '<>') {
    operator = 'not_equals'
  } else if (operatorRaw === 'gt' || operatorRaw === 'greater_than' || operatorRaw === '>') {
    operator = 'greater_than'
  } else if (
    operatorRaw === 'gte' ||
    operatorRaw === 'ge' ||
    operatorRaw === 'greater_than_or_equal' ||
    operatorRaw === '>='
  ) {
    operator = 'greater_than_or_equal'
  } else if (operatorRaw === 'lt' || operatorRaw === 'less_than' || operatorRaw === '<') {
    operator = 'less_than'
  } else if (
    operatorRaw === 'lte' ||
    operatorRaw === 'le' ||
    operatorRaw === 'less_than_or_equal' ||
    operatorRaw === '<='
  ) {
    operator = 'less_than_or_equal'
  } else if (operatorRaw === 'is_null') {
    return { ok: true, filters: [{ field, operator: 'is_null', value: null }] }
  } else if (operatorRaw === 'is_not_null') {
    return { ok: true, filters: [{ field, operator: 'is_not_null', value: null }] }
  } else {
    return { ok: false, reason: `runtime_filter_operator_not_yet_typed:${operatorRaw}` }
  }

  if (
    (operator === 'equals' || operator === 'not_equals') &&
    (value === null || value === undefined)
  ) {
    return { ok: true, filters: [] }
  }
  if ((operator === 'in_list' || operator === 'not_in_list') && isArrayValue && value.length === 0) {
    return { ok: true, filters: [] }
  }

  if (isArrayValue) {
    for (const v of value) {
      if (v !== null && typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean') {
        return { ok: false, reason: 'runtime_filter_in_list_value_not_scalar' }
      }
    }
  } else if (
    value !== null &&
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    typeof value !== 'boolean'
  ) {
    return { ok: false, reason: 'runtime_filter_value_not_scalar' }
  }

  return { ok: true, filters: [{ field, operator, value }] }
}
